import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ChevronRight } from 'lucide-react';

// Hardcoded content
const contentMap: Record<string, string> = {
  index: `# Welcome to the FiverFlow Documentation

This home page helps you get started, understand how things work, and follow a clear syllabus across modules.

## Welcome message

Welcome! FiverFlow is your all‑in‑one cockpit to manage clients, orders, invoices, tasks, and analytics — built for freelancers and small teams.

## How it works (overview)

1. Sign in and profile: create your account, fill your public profile, set preferences.
2. Workspace: use the left sidebar to access Dashboard, Calendar, Clients, Orders, Invoices, and Workboard.
3. Real‑time data: all screens sync with the database (Supabase). Actions update instantly across the app.
4. AI Assistant: available depending on plan; speeds up input, search, and automation.

## Key areas

Essential features to run your business:

- **Dashboard**: central view of metrics and quick actions
- **Calendar**: planning, events, and deadlines
- **Statistics**: detailed reports and analysis
- **Referrals**: referral program

### AI

Accelerate and automate with the assistant:

- **Assistant**: contextual help, writing, search, and automations

### Workspace

Organize your work and clients:

- **Clients**: contact and account management
- **Orders**: project tracking and deliverables
- **Invoices**: billing and payments
- **Workboard**: tasks/kanban, priorities, progress

### More

Additional features:

- **Profile**: settings, appearance, and security
- **Upgrade**: plans and billing

## Need help

If you have questions or get stuck:

- Browse the dedicated Docs pages from the sidebar
- Open a support ticket from the dashboard
- Visit the pricing page to upgrade your plan

---

*Last updated: ${new Date().toLocaleDateString()}*`,
  
  dashboard: `# Dashboard

The Dashboard is your central command center for managing your freelance business on FiverFlow. It provides a comprehensive real-time overview of your business health, key metrics, recent activity, and quick access to essential functions.

## What You'll Find

The Dashboard is organized into four main sections:

- **[Overview](/docs/dashboard/overview)** - Comprehensive business metrics and key performance indicators
- **[Reports](/docs/dashboard/reports)** - Detailed analytics and revenue insights
- **[Quick Actions](/docs/dashboard/quick-actions)** - Fast access to common tasks and workflows
- **[Widgets](/docs/dashboard/widgets)** - Customizable dashboard components and metrics

## Quick Access

Navigate to any section using the sidebar or the links above. Each section provides detailed information about specific dashboard features and how to use them effectively.

## Getting Started

1. **Access the Dashboard**: Navigate to Dashboard from the main sidebar
2. **Review Your Stats**: Check the key metrics cards at the top
3. **Explore Sections**: Click through Overview, Reports, Quick Actions, and Widgets
4. **Customize**: Use Quick Actions to add clients, create orders, or generate invoices

## Key Features

- **Real-time Metrics**: All statistics update automatically as you work
- **Visual Analytics**: Charts and graphs help you understand trends
- **Quick Actions**: Common tasks accessible with one click
- **Recent Activity**: Stay updated with latest client and order updates

---

*Last updated: ${new Date().toLocaleDateString()}*`,
  
  'dashboard/overview': `# Dashboard Overview

The Overview section provides a comprehensive at-a-glance view of your business health with key performance indicators, growth metrics, and recent activity.

## Overview Section

The Overview is the first thing you see when you open the Dashboard. It displays essential metrics in an easy-to-read card format.

## Key Metrics Cards

### Total Clients

**What it shows**: The total number of clients in your database

**Features**:
- Real-time count of all clients
- Growth percentage compared to the previous month
- Visual indicator (green for growth, red for decline)
- Trend arrow showing direction of change

**How it's calculated**: Counts all clients associated with your user account in the database.

**Use it to**: Track business expansion and client acquisition trends.

### Total Orders

**What it shows**: The total number of orders/projects you have

**Features**:
- Complete count of all orders
- Month-over-month growth percentage
- Activity indicator showing order trends
- Color-coded growth indicators

**How it's calculated**: Counts all orders created in your account, regardless of status.

**Use it to**: Monitor project volume and workload.

### Revenue

**What it shows**: Total revenue from completed orders

**Features**:
- Currency-formatted revenue display
- Respects your currency preference (USD, EUR, etc.)
- Month-over-month growth percentage
- Only includes completed, delivered, or paid orders

**How it's calculated**: Sum of budgets from orders with status: 'completed', 'delivered', or 'paid'.

**Use it to**: Track income and business growth over time.

### Completion Rate

**What it shows**: Percentage of orders that have been completed

**Features**:
- Percentage display (0-100%)
- Average delivery time in days
- Visual completion indicator
- Calendar icon for time tracking

**How it's calculated**: (Completed orders / Total orders) × 100

**Use it to**: Measure productivity and project completion efficiency.

## Growth Indicators

Each metric card displays growth percentages:

- **Positive Growth** (Green): Shows with a "+" sign and green color
- **Negative Growth** (Red): Shows with a "-" sign and red color
- **Calculation**: Compares current month to previous month

**Example**: If you had 10 clients last month and 12 this month, the growth would show "+20%".

## Visual Analytics

### Monthly Revenue Chart

**Location**: Below the metrics cards

**What it shows**: Revenue trends over the last 6 months

**Features**:
- Bar chart visualization
- Month labels on X-axis
- Revenue amounts displayed on each bar
- Highest revenue month highlighted with emerald gradient
- Hover effects for better interactivity
- Currency-formatted values

**How to read it**:
- Each bar represents one month
- Height indicates revenue amount
- The highest bar is highlighted in emerald color
- Hover over bars to see exact values

**Use it to**: Identify revenue trends, seasonal patterns, and growth periods.

### Orders by Status Chart

**Location**: Right side of the charts section

**What it shows**: Distribution of orders by their current status

**Features**:
- Visual breakdown of order statuses
- Status labels (In Progress, Completed, Pending, etc.)
- Count and percentage for each status
- Color-coded status indicators:
  - **In Progress**: Gray
  - **Completed**: Green
  - **Pending**: Yellow/Amber
- Highest count status highlighted

**Status Types**:
- **In Progress**: Orders currently being worked on
- **Completed**: Finished orders
- **Pending**: Orders waiting to start
- **Other statuses**: Custom statuses you may have created

**Use it to**: Understand workload distribution and identify bottlenecks.

## Recent Activity Sections

### Recent Orders

**Location**: Bottom left of the dashboard

**What it shows**: The 5 most recent orders created

**Features**:
- Order title and client name
- Creation date
- Status badge with color coding
- Status indicators:
  - Completed: Green badge
  - In Progress: Gray badge
  - Pending: Amber badge
- Click to view order details

**Information displayed**:
- Order title
- Client name
- Creation date (formatted)
- Status with visual indicator

**Use it to**: Quickly see what you've been working on recently and track order status.

### Top Clients

**Location**: Bottom right of the dashboard

**What it shows**: Your top 5 clients by revenue

**Features**:
- Ranked list (1-5) with numbered badges
- Client name
- Total revenue per client
- Number of orders per client
- Gradient badges for ranking
- Revenue formatted in your currency

**How it's calculated**: Clients are ranked by total revenue from all their orders, regardless of status.

**Use it to**: Identify your most valuable clients and focus on key relationships.

## Data Refresh

The dashboard automatically refreshes when:
- You create a new client
- You create a new order
- Order status changes
- You click the "Refresh" button

**Manual Refresh**: Click the refresh button (circular arrow icon) in the top-right to manually update all statistics.

## Loading States

- **Loading**: Shows a spinner while fetching data
- **Error**: Displays an error message with a retry button
- **No Data**: Shows a message when no data is available

## Best Practices

1. **Daily Review**: Check your overview metrics each morning
2. **Monitor Growth**: Watch growth percentages to track trends
3. **Identify Patterns**: Use charts to spot seasonal patterns
4. **Focus on Top Clients**: Use the Top Clients section to prioritize relationships
5. **Track Recent Activity**: Stay on top of recent orders and updates

## Tips

- Click on any metric card to see more details (future feature)
- Hover over chart elements to see exact values
- Use the refresh button if data seems outdated
- Review growth percentages weekly to track progress
- Compare monthly revenue to identify your best months

---

*Explore more: [Reports](/docs/dashboard/reports), [Quick Actions](/docs/dashboard/quick-actions), [Widgets](/docs/dashboard/widgets)*`,
  
  'dashboard/reports': `# Dashboard Reports

The Reports section provides detailed analytics and insights about your business performance, revenue trends, and order statistics.

## Reports Overview

Reports give you deep insights into your business performance through visual analytics, detailed breakdowns, and trend analysis. All reports are generated from your actual business data in real-time.

## Available Reports

### Monthly Revenue Report

**Location**: Main charts section (left side)

**What it shows**: Revenue trends over the last 6 months

**Details**:
- **Time Period**: Last 6 months from current date
- **Data Source**: Completed orders only (status: completed, delivered, or paid)
- **Visualization**: Bar chart with gradient colors
- **Currency**: Respects your currency preference
- **Formatting**: Currency-formatted with appropriate decimal places

**Features**:
- Month labels (abbreviated, e.g., "Jan", "Feb")
- Revenue amount displayed on each bar
- Highest revenue month highlighted in emerald gradient
- Visual pulse animation on highest bar
- Hover effects for better interaction
- Responsive design for mobile and desktop

**How to read**:
1. Each bar represents one month
2. Bar height indicates revenue amount
3. Highest bar is highlighted and pulsing
4. Month labels are at the bottom
5. Revenue values are shown above each bar

**Use cases**:
- Identify your best-performing months
- Track revenue growth trends
- Plan for seasonal variations
- Compare month-over-month performance
- Forecast future revenue

**Calculation method**:
- Filters orders by completion status
- Groups by month based on order creation date
- Sums budgets for each month
- Displays last 6 months from current date

### Orders by Status Report

**Location**: Main charts section (right side)

**What it shows**: Distribution of all orders by their current status

**Details**:
- **Data Source**: All orders in your account
- **Visualization**: List with status indicators and percentages
- **Status Types**: All unique statuses in your orders
- **Formatting**: Count and percentage for each status

**Features**:
- Status name (formatted, e.g., "In Progress" instead of "in_progress")
- Order count for each status
- Percentage of total orders
- Color-coded status indicators
- Highest count status highlighted
- Visual pulse on dominant status

**Status Colors**:
- **In Progress**: Gray (bg-gray-500)
- **Completed**: Green (bg-green-500)
- **Pending**: Yellow/Amber (bg-yellow-500)
- **Other statuses**: Rotating colors (blue, amber, rose, purple)

**How to read**:
1. Each row represents one status
2. Colored dot indicates status type
3. Count shows number of orders
4. Percentage shows proportion of total
5. Highest count is highlighted with ring effect

**Use cases**:
- Understand workload distribution
- Identify bottlenecks (too many in progress)
- Track completion rates
- Monitor pending orders
- Balance work across statuses

**Calculation method**:
- Groups orders by status field
- Counts orders in each status
- Calculates percentage of total
- Sorts by count (highest first)

### Daily Revenue Report

**What it shows**: Revenue trends over the last 30 days (available in data but not displayed in UI)

**Details**:
- **Time Period**: Last 30 days
- **Data Source**: Completed orders
- **Granularity**: Day-by-day breakdown
- **Purpose**: Detailed daily performance tracking

**Note**: This data is calculated and available in the dashboard stats but currently used for future reporting features.

### Revenue by Platform Report

**What it shows**: Revenue breakdown by platform (e.g., Fiverr, Upwork, Direct)

**Details**:
- **Data Source**: Completed orders with platform information
- **Grouping**: By platform field
- **Top Platforms**: Shows top 7 platforms by revenue
- **Purpose**: Understand which platforms generate most revenue

**Note**: This data is calculated and available for future reporting features.

## Report Data Accuracy

All reports use real-time data from your database:

- **Source**: Supabase database
- **Updates**: Automatic when data changes
- **Filters**: Based on user_id (your account only)
- **Currency**: Uses your selected currency preference
- **Date Range**: Based on actual order dates

## Data Calculations

### Revenue Calculation

Revenue includes only orders with these statuses:
- \`completed\`
- \`delivered\`
- \`paid\`

Orders with other statuses (pending, in_progress, cancelled) are excluded from revenue calculations.

### Growth Calculation

Growth percentages compare current period to previous period:

\`\`\`
Growth % = ((Current - Previous) / Previous) × 100
\`\`\`

**Example**:
- Last month: $5,000 revenue
- This month: $6,000 revenue
- Growth: ((6000 - 5000) / 5000) × 100 = +20%

### Percentage Calculation

For status distribution:

\`\`\`
Percentage = (Status Count / Total Orders) × 100
\`\`\`

## Exporting Reports

**Future Feature**: Export reports to CSV, PDF, or Excel formats.

**Planned Features**:
- Export monthly revenue to spreadsheet
- Generate PDF reports
- Email reports automatically
- Schedule weekly/monthly reports

## Custom Date Ranges

**Current**: Reports show fixed ranges (6 months for revenue, all time for status)

**Future Feature**: Custom date range selector for:
- Last 7 days
- Last 30 days
- Last quarter
- Last year
- Custom range (date picker)

## Report Refresh

Reports automatically update when:
- New orders are created
- Order status changes
- Orders are completed
- Manual refresh is triggered

**Manual Refresh**: Click the refresh button in the dashboard header to update all reports.

## Best Practices

1. **Regular Review**: Check reports weekly to track trends
2. **Compare Periods**: Compare current period to previous periods
3. **Identify Patterns**: Look for seasonal patterns in revenue
4. **Monitor Status Distribution**: Keep an eye on order status balance
5. **Focus on Growth**: Track growth percentages over time

## Tips

- **Revenue Trends**: Use monthly revenue chart to identify your best months
- **Status Balance**: If too many orders are "in progress", you may be overloaded
- **Client Focus**: Combine reports with Top Clients to identify revenue sources
- **Growth Tracking**: Monitor growth percentages to measure business health
- **Seasonal Planning**: Use historical data to plan for busy/slow seasons

## Troubleshooting

### Reports Show Zero

**Possible causes**:
- No orders in database
- No completed orders
- Database connection issues
- RLS (Row Level Security) restrictions

**Solutions**:
- Create some test orders
- Complete some orders
- Check database connection
- Verify user permissions

### Incorrect Revenue

**Possible causes**:
- Orders not marked as completed
- Budget not set on orders
- Currency conversion issues
- Date range issues

**Solutions**:
- Mark orders as completed when done
- Set budget on all orders
- Check currency settings
- Verify order dates

### Missing Data

**Possible causes**:
- Orders created before account setup
- Data migration issues
- Filtered out by status
- Date range limitations

**Solutions**:
- Check order creation dates
- Verify data migration
- Review order statuses
- Adjust date ranges (when available)

---

*Explore more: [Overview](/docs/dashboard/overview), [Quick Actions](/docs/dashboard/quick-actions), [Widgets](/docs/dashboard/widgets)*`,
  
  'dashboard/quick-actions': `# Dashboard Quick Actions

Quick Actions provide fast access to common tasks and workflows directly from the dashboard. Instead of navigating through multiple pages, you can perform essential operations with a single click.

## Quick Actions Overview

The Quick Actions menu is accessible from the dashboard header. It provides instant access to the most commonly used features in FiverFlow.

## Accessing Quick Actions

### Location

Quick Actions are available in the dashboard header, next to the refresh button.

### How to Open

1. **Click the Button**: Click the "Quick Actions" button in the top-right of the dashboard
2. **Mobile**: On mobile devices, the button shows a three-dot menu icon
3. **Keyboard**: Press Escape to close the menu

### Menu Display

When opened, a dropdown menu appears with the following options:
- Add Client
- Create Order
- Create Task
- Create Invoice

## Available Actions

### Add Client

**Icon**: Users icon

**What it does**: Opens the client creation form

**Details**:
- Opens a modal dialog with client form
- Allows you to add a new client without leaving the dashboard
- Form includes all client fields (name, email, phone, etc.)
- Automatically refreshes dashboard stats after creation

**When to use**:
- You have a new client to add
- You want to quickly add client information
- You're on a call and need to add client details

**Steps**:
1. Click "Quick Actions" button
2. Select "Add Client"
3. Fill in client information in the modal
4. Click "Save" or "Create Client"
5. Dashboard automatically refreshes to show updated client count

**After creation**:
- Client is added to your database
- Dashboard stats update automatically
- Client appears in Clients page
- You can immediately create orders for this client

### Create Order

**Icon**: Package icon

**What it does**: Opens the order creation form

**Details**:
- Opens a modal dialog with order form
- Allows you to create a new order/project
- Form includes order fields (title, client, budget, dates, etc.)
- Links order to selected client
- Automatically refreshes dashboard stats after creation

**When to use**:
- You have a new project to track
- You want to quickly create an order
- A client has requested new work

**Steps**:
1. Click "Quick Actions" button
2. Select "Create Order"
3. Fill in order information:
   - Title (required)
   - Client (select from dropdown)
   - Description (optional)
   - Budget (optional)
   - Start date (optional)
   - Due date (optional)
   - Platform (optional)
   - Status (optional)
4. Click "Save" or "Create Order"
5. Dashboard automatically refreshes to show updated order count

**After creation**:
- Order is added to your database
- Dashboard stats update automatically
- Order appears in Orders page
- Order appears in Recent Orders section
- Order can be linked to tasks

### Create Task

**Icon**: Calendar icon

**What it does**: Redirects to the Tasks/Workboard page with create action

**Details**:
- Redirects to \`/dashboard/tasks?action=create\`
- Opens task creation modal automatically
- Allows you to create a new task
- Task is linked to Workboard

**When to use**:
- You have a new task to track
- You want to add a task to your workboard
- You need to track work for an order

**Steps**:
1. Click "Quick Actions" button
2. Select "Create Task"
3. You are redirected to Tasks page
4. Task creation modal opens automatically
5. Fill in task information:
   - Title (required)
   - Description (optional)
   - Priority (Low, Medium, High)
   - Estimated hours (optional)
   - Due date (optional)
   - Order (optional - link to order)
6. Click "Create Task"

**After creation**:
- Task is added to your Workboard
- Task appears in Tasks page
- Task can be linked to orders
- Task appears in Calendar if due date is set

**Note**: Creating a task requires the Boost plan or higher.

### Create Invoice

**Icon**: DollarSign icon

**What it does**: Redirects to the Invoices page

**Details**:
- Redirects to \`/invoices\` page
- Allows you to create a new invoice
- Invoice can be linked to clients and orders

**When to use**:
- You need to bill a client
- You want to create an invoice for completed work
- You're ready to send payment request

**Steps**:
1. Click "Quick Actions" button
2. Select "Create Invoice"
3. You are redirected to Invoices page
4. Click "Create Invoice" or "New Invoice" button
5. Fill in invoice information:
   - Client (required)
   - Items (required)
   - Due date (optional)
   - Notes (optional)
6. Click "Save" or "Create Invoice"

**After creation**:
- Invoice is added to your database
- Invoice appears in Invoices page
- Invoice can be sent to client
- Invoice appears in Calendar if due date is set

**Note**: Creating invoices may require specific plan permissions.

## Quick Actions Menu Behavior

### Opening and Closing

- **Click Outside**: Click anywhere outside the menu to close it
- **Escape Key**: Press Escape to close the menu
- **Action Selection**: Menu closes automatically when you select an action
- **Mobile**: Menu closes when you tap outside or select an action

### Menu Positioning

- **Desktop**: Menu appears below the Quick Actions button, aligned to the right
- **Mobile**: Menu appears below the button, full width on small screens
- **Z-index**: Menu appears above other content (z-50)

### Visual Design

- **Background**: White (light mode) or slate-800 (dark mode)
- **Border**: Gray border with rounded corners
- **Shadow**: Drop shadow for depth
- **Icons**: Each action has an associated icon
- **Hover**: Actions highlight on hover

## Workflow Examples

### Adding a New Client and Order

1. Click "Quick Actions" → "Add Client"
2. Fill in client information and save
3. Click "Quick Actions" → "Create Order"
4. Select the client you just added
5. Fill in order details and save
6. Dashboard updates with new client and order counts

### Quick Task Creation

1. Click "Quick Actions" → "Create Task"
2. Fill in task details in the modal
3. Link task to an order (optional)
4. Set due date for calendar integration
5. Task appears in Workboard and Calendar

### Invoice Workflow

1. Complete an order
2. Click "Quick Actions" → "Create Invoice"
3. Select client and add invoice items
4. Set due date
5. Send invoice to client

## Keyboard Shortcuts

- **Escape**: Close Quick Actions menu
- **Tab**: Navigate through menu items (future feature)
- **Enter**: Select highlighted action (future feature)

## Best Practices

1. **Use Frequently**: Quick Actions save time, use them often
2. **Complete Forms**: Fill in all required fields for best results
3. **Link Data**: Link tasks to orders and invoices to clients for better organization
4. **Refresh Dashboard**: Dashboard auto-refreshes, but you can manually refresh if needed
5. **Check Permissions**: Some actions may require specific plan permissions

## Tips

- **Quick Client Addition**: Use Quick Actions to add clients during calls
- **Batch Creation**: Create multiple orders quickly using Quick Actions
- **Task Organization**: Link tasks to orders for better project tracking
- **Invoice Timing**: Create invoices immediately after completing work
- **Dashboard Updates**: Wait for dashboard to refresh after creating items

## Troubleshooting

### Menu Doesn't Open

**Possible causes**:
- JavaScript errors
- Z-index conflicts
- Button not clicked properly

**Solutions**:
- Refresh the page
- Check browser console for errors
- Try clicking the button again
- Clear browser cache

### Action Doesn't Work

**Possible causes**:
- Missing required fields
- Permission issues
- Database connection problems
- Form validation errors

**Solutions**:
- Fill in all required fields
- Check your plan permissions
- Verify database connection
- Check form validation messages

### Dashboard Doesn't Refresh

**Possible causes**:
- Auto-refresh not triggered
- Data not saved
- Cache issues

**Solutions**:
- Manually click refresh button
- Verify data was saved
- Clear browser cache
- Check network connection

---

*Explore more: [Overview](/docs/dashboard/overview), [Reports](/docs/dashboard/reports), [Widgets](/docs/dashboard/widgets)*`,
  
  'dashboard/widgets': `# Dashboard Widgets

Widgets are the individual components that make up your dashboard. Each widget displays specific information and can be customized to show what matters most to your business.

## Widgets Overview

The Dashboard consists of several widgets organized in a grid layout. Each widget serves a specific purpose and displays relevant business metrics or information.

## Available Widgets

### Stats Cards Widgets

These widgets display key performance indicators in card format at the top of the dashboard.

#### Total Clients Widget

**Location**: Top-left (first card)

**Displays**:
- Total number of clients
- Growth percentage (month-over-month)
- Trend indicator (up/down arrow)
- Growth color (green for positive, red for negative)

**Data Source**: \`clients\` table filtered by \`user_id\`

**Calculations**:
- **Total Clients**: Count of all clients
- **Growth**: ((This month - Last month) / Last month) × 100
- **New Clients This Month**: Clients created in current month
- **New Clients Last Month**: Clients created in previous month

**Visual Features**:
- Large number display (2xl font)
- Growth percentage with icon
- Color-coded growth indicator
- Responsive design

**Customization**: Currently fixed position, future customization planned

#### Total Orders Widget

**Location**: Top-center-left (second card)

**Displays**:
- Total number of orders
- Growth percentage (month-over-month)
- Activity indicator
- Growth color coding

**Data Source**: \`orders\` table filtered by \`user_id\`

**Calculations**:
- **Total Orders**: Count of all orders
- **Growth**: ((This month - Last month) / Last month) × 100
- **Orders This Month**: Orders created in current month
- **Orders Last Month**: Orders created in previous month

**Visual Features**:
- Large number display
- Activity icon (pulse animation)
- Growth percentage
- Status indicators

**Customization**: Currently fixed position, future customization planned

#### Revenue Widget

**Location**: Top-center-right (third card)

**Displays**:
- Total revenue from completed orders
- Currency formatting (respects user preference)
- Growth percentage (month-over-month)
- Revenue trend indicator

**Data Source**: \`orders\` table filtered by status and \`user_id\`

**Calculations**:
- **Total Revenue**: Sum of budgets from orders with status: 'completed', 'delivered', or 'paid'
- **Revenue This Month**: Completed orders created in current month
- **Revenue Last Month**: Completed orders created in previous month
- **Growth**: ((This month - Last month) / Last month) × 100
- **Pending Revenue**: Sum of budgets from non-completed orders

**Currency**: Uses currency from CurrencyContext (user preference)

**Visual Features**:
- Currency-formatted display
- Large number with currency symbol
- Growth percentage
- Trend arrow

**Customization**: Currency can be changed in settings

#### Completion Rate Widget

**Location**: Top-right (fourth card)

**Displays**:
- Completion rate percentage (0-100%)
- Average delivery time in days
- Calendar icon indicator

**Data Source**: \`orders\` table filtered by \`user_id\`

**Calculations**:
- **Completion Rate**: (Completed orders / Total orders) × 100
- **Completed Orders**: Orders with status: 'completed', 'delivered', or 'paid'
- **Average Delivery Time**: Average days between start_date and completed_date
- **Delivery Times**: Calculated from orders with both start_date and completed_date

**Visual Features**:
- Percentage display with % symbol
- Average days with calendar icon
- Progress indicator style

**Customization**: Currently fixed calculation, future customization planned

### Chart Widgets

These widgets display visual analytics and trends.

#### Monthly Revenue Chart Widget

**Location**: Bottom-left (main charts section)

**Type**: Bar chart

**Displays**:
- Revenue for last 6 months
- Month labels (abbreviated)
- Revenue amounts on each bar
- Highest month highlighted

**Data Source**: \`orders\` table filtered by completion status

**Calculations**:
- Groups orders by month based on \`created_at\` date
- Sums budgets for completed orders in each month
- Shows last 6 months from current date
- Highlights month with highest revenue

**Visual Features**:
- Bar chart with gradient colors
- Emerald gradient for highest bar
- Blue gradient for other bars
- Pulse animation on highest bar
- Hover effects
- Responsive grid layout (6 columns)

**Customization**:
- Currently shows 6 months (fixed)
- Future: Customizable date range
- Future: Different chart types (line, area)

#### Orders by Status Chart Widget

**Location**: Bottom-right (main charts section)

**Type**: Status distribution list

**Displays**:
- List of all order statuses
- Count for each status
- Percentage of total
- Color-coded status indicators

**Data Source**: \`orders\` table filtered by \`user_id\`

**Calculations**:
- Groups orders by \`status\` field
- Counts orders in each status
- Calculates percentage: (Status count / Total orders) × 100
- Sorts by count (highest first)

**Status Colors**:
- **In Progress**: Gray (bg-gray-500)
- **Completed**: Green (bg-green-500)
- **Pending**: Yellow/Amber (bg-yellow-500)
- **Other**: Rotating colors (blue, amber, rose, purple)

**Visual Features**:
- Colored status dots
- Count and percentage display
- Highlighted dominant status
- Pulse animation on highest count
- Ring effect on active status
- Hover effects

**Customization**:
- Automatically includes all statuses
- Future: Filter specific statuses
- Future: Group similar statuses

### Activity Widgets

These widgets display recent activity and top performers.

#### Recent Orders Widget

**Location**: Bottom-left (activity section)

**Displays**:
- List of 5 most recent orders
- Order title
- Client name
- Creation date
- Status badge

**Data Source**: \`orders\` table filtered by \`user_id\`

**Calculations**:
- Sorts orders by \`created_at\` date (newest first)
- Limits to 5 most recent
- Joins with clients table for client names
- Formats dates for display

**Visual Features**:
- Status indicator dot (colored)
- Order title and client name
- Formatted date
- Status badge with color coding
- Hover effects
- Empty state message

**Status Badges**:
- **Completed**: Green badge
- **In Progress**: Gray badge
- **Pending**: Amber badge
- **Other**: Gray badge

**Customization**:
- Currently shows 5 orders (fixed)
- Future: Configurable number of items
- Future: Filter by status
- Future: Click to view order details

#### Top Clients Widget

**Location**: Bottom-right (activity section)

**Displays**:
- List of top 5 clients by revenue
- Client name
- Total revenue
- Number of orders
- Rank indicator (1-5)

**Data Source**: \`orders\` table filtered by \`user_id\`

**Calculations**:
- Groups orders by \`client_id\`
- Sums revenue (budget) for each client
- Counts orders per client
- Sorts by revenue (highest first)
- Limits to top 5 clients
- Joins with clients table for client names

**Visual Features**:
- Ranked badges (1-5) with gradient
- Client name
- Order count
- Revenue formatted in currency
- Hover effects
- Empty state message

**Ranking**:
- Clients ranked by total revenue
- Includes revenue from all orders (not just completed)
- Ties broken by order count

**Customization**:
- Currently shows top 5 (fixed)
- Future: Configurable number of clients
- Future: Filter by time period
- Future: Click to view client details

## Widget Layout

### Grid Structure

The dashboard uses a responsive grid layout:

- **Desktop**: 4 columns for stats, 2 columns for charts, 2 columns for activity
- **Tablet**: 2 columns for stats, 1 column for charts, 1 column for activity
- **Mobile**: 1 column for all widgets

### Responsive Design

- **Large screens (lg)**: Full grid layout
- **Medium screens (md)**: 2-column layout
- **Small screens (sm)**: 1-column layout
- **Mobile**: Stacked layout

### Widget Spacing

- **Gap**: 4-6 units between widgets (responsive)
- **Padding**: Consistent padding within widgets
- **Margins**: Responsive margins for different screen sizes

## Widget Customization

### Current Limitations

- Widgets are currently fixed in position
- No drag-and-drop reordering
- No widget visibility toggles
- No custom widget sizes
- No widget removal/addition

### Planned Features

- **Drag-and-Drop**: Rearrange widgets by dragging
- **Visibility Toggles**: Show/hide specific widgets
- **Custom Sizes**: Resize widgets (small, medium, large)
- **Add/Remove Widgets**: Add custom widgets or remove unused ones
- **Widget Settings**: Configure widget-specific settings
- **Save Layout**: Save custom widget layouts
- **Multiple Layouts**: Create different layouts for different purposes

## Widget Data Refresh

### Automatic Refresh

Widgets automatically refresh when:
- New data is created (clients, orders)
- Data is updated (order status changes)
- Form success callbacks are triggered
- Manual refresh button is clicked

### Manual Refresh

Click the refresh button in the dashboard header to manually refresh all widgets.

### Refresh Indicators

- **Loading State**: Spinner shown while loading
- **Error State**: Error message with retry button
- **Empty State**: Message when no data is available

## Widget Performance

### Data Loading

- Widgets load data in parallel
- Uses React hooks for data fetching
- Implements loading states
- Handles errors gracefully

### Optimization

- Data is cached in component state
- Refetch only when necessary
- Efficient database queries
- Minimal re-renders

## Best Practices

1. **Regular Review**: Check all widgets regularly for updates
2. **Understand Metrics**: Know what each widget measures
3. **Monitor Trends**: Watch for changes in widget data
4. **Use Filters**: When available, use filters to focus on specific data
5. **Export Data**: Export widget data for external analysis (when available)

## Tips

- **Stats Cards**: Review growth percentages weekly
- **Revenue Chart**: Identify seasonal patterns
- **Status Chart**: Balance workload across statuses
- **Recent Orders**: Stay on top of latest activity
- **Top Clients**: Focus on high-value relationships

## Troubleshooting

### Widget Shows No Data

**Possible causes**:
- No data in database
- Database connection issues
- RLS restrictions
- Filter issues

**Solutions**:
- Create test data
- Check database connection
- Verify user permissions
- Review filter settings

### Widget Shows Incorrect Data

**Possible causes**:
- Calculation errors
- Date range issues
- Status filter issues
- Currency conversion problems

**Solutions**:
- Verify calculations
- Check date ranges
- Review status filters
- Verify currency settings

### Widget Not Updating

**Possible causes**:
- Auto-refresh not working
- Cache issues
- Data not saved
- Network problems

**Solutions**:
- Manually refresh
- Clear browser cache
- Verify data was saved
- Check network connection

---

*Explore more: [Overview](/docs/dashboard/overview), [Reports](/docs/dashboard/reports), [Quick Actions](/docs/dashboard/quick-actions)*`,
  
  calendar: `# Calendar

Manage your schedule, deadlines, and events with the FiverFlow Calendar. Keep track of important dates, client meetings, project milestones, and subscription renewals all in one unified view.

## What You'll Find

The Calendar is organized into three main sections:

- **[Overview](/docs/calendar/overview)** - Comprehensive calendar features and event management
- **[Create Event](/docs/calendar/create-event)** - How to create tasks, orders, and events from the calendar
- **[Sync Integrations](/docs/calendar/sync-integrations)** - External calendar synchronization (coming soon)

## Quick Access

Navigate to any section using the sidebar or the links above. Each section provides detailed information about specific calendar features and how to use them effectively.

## Getting Started

1. **Access the Calendar**: Navigate to Calendar from the main navigation menu
2. **Plan Requirement**: The calendar is available on the **Boost** plan and above
3. **View Events**: See all your tasks, orders, invoices, and subscriptions in one place
4. **Create Events**: Click on any date to add new tasks or orders

## Key Features

- **Unified Event View**: Automatically displays events from tasks, orders, invoices, subscriptions, and calendar events
- **Advanced Filtering**: Filter by category, type, and priority
- **Real-time Updates**: Calendar updates automatically when data changes
- **Mobile-Friendly**: Responsive design with agenda view on mobile
- **Statistics Sidebar**: Real-time statistics about your events and workload
- **Subscription Management**: Manage subscription renewals directly from the calendar

---

*Last updated: ${new Date().toLocaleDateString()}*`,
  
  'calendar/overview': `# Calendar Overview

The Calendar Overview provides a comprehensive view of all your important dates and deadlines. It automatically aggregates events from multiple sources across your FiverFlow workspace.

## Calendar Overview

The Calendar page is your central hub for managing all time-based activities in FiverFlow. It provides a unified view of tasks, orders, invoices, subscriptions, and custom calendar events.

## Event Sources

The calendar automatically displays events from multiple sources:

### Tasks with Due Dates

**Source**: Workboard tasks with \`due_date\` set

**Display**:
- Tasks appear as purple deadline events
- Event ID format: \`task-{task_id}\`
- Default time: 09:00
- Category: \`task\`
- Type: \`deadline\`
- Priority: Inherited from task (low, medium, high)

**Features**:
- Click on task event to open Task Drawer
- Edit task directly from calendar
- Change task status (To Do, In Progress, Completed)
- Delete tasks from calendar
- Tasks sync automatically with Workboard

**Requirements**: Task must have a \`due_date\` set to appear on calendar

### Orders with Due Dates

**Source**: Orders with \`due_date\` set

**Display**:
- Orders appear as high-priority deadline events
- Event ID format: \`order-{order_id}\`
- Default time: 17:00
- Category: \`order\`
- Type: \`deadline\`
- Priority: \`high\` (orders are important deadlines)

**Features**:
- Click on order event to view order details
- View order information in Order Detail Modal
- Update order status
- Edit order details
- Track project deadlines and client deliverables

**Requirements**: Order must have a \`due_date\` set to appear on calendar

### Invoices with Due Dates

**Source**: Invoices with \`due_date\` set

**Display**:
- Invoices appear as deadline events
- Event ID format: \`invoice-{invoice_id}\`
- Default time: 17:00
- Category: \`invoice\`
- Type: \`deadline\`
- Priority: \`high\` if overdue, \`medium\` otherwise

**Features**:
- Overdue invoices automatically marked as high priority
- Title format: \`🧾 {invoice_number} - {client_name}\`
- Description includes invoice total and status
- Helps you stay on top of payment deadlines

**Requirements**: Invoice must have a \`due_date\` set to appear on calendar

### Subscription Renewals

**Source**: Active subscriptions with \`next_renewal_date\` and \`billing_cycle\`

**Display**:
- Subscriptions appear as reminder events
- Event ID format: \`subscription-{subscription_id}-{index}\`
- Default time: 09:00
- Category: \`subscription\`
- Type: \`reminder\`
- Priority: \`medium\`

**Features**:
- Automatically generates recurring renewal events
- Supports weekly, monthly, quarterly, and yearly billing cycles
- Events generated up to 12 months in advance
- Title format: \`💳 {subscription_name} - $[amount]\` (where [amount] is the subscription amount)
- Description includes billing cycle and provider

**Recurring Date Generation**:
- **Weekly**: Events every 7 days
- **Monthly**: Events on the same day each month (handles edge cases like Jan 31 → Feb 28)
- **Quarterly**: Events every 3 months
- **Yearly**: Events on the same date each year

**Requirements**:
- Subscription must be active (\`is_active = true\`)
- Must have \`next_renewal_date\` set
- Must have \`billing_cycle\` configured

### Calendar Events

**Source**: Custom calendar events from \`calendar_events\` table

**Display**:
- Custom events created directly in calendar
- Event ID format: \`event-{event_id}\`
- Time: From \`start_time\` field
- Category: \`calendar\` or \`meeting\` (based on type)
- Type: \`meeting\`, \`deadline\`, or \`reminder\`
- Priority: From event data (low, medium, high)

**Features**:
- Can include attendees
- Can include location
- Can set custom times
- Full control over event details

**Requirements**: Events stored in \`calendar_events\` table with \`user_id\` matching current user

### Meetings

**Source**: Calendar events with type \`meeting\`

**Display**:
- Meetings appear as blue gradient events
- Category: \`meeting\`
- Type: \`meeting\`
- Can include attendees and location

**Features**:
- Attendee management
- Location tracking
- Meeting-specific details

## Event Categories

Events are organized into six categories with distinct visual indicators:

### 📋 Tasks
- **Icon**: 📋
- **Color**: Purple gradient
- **Source**: Workboard tasks
- **Type**: Deadline
- **Default Time**: 09:00

### 📦 Orders
- **Icon**: 📦
- **Color**: Purple gradient (high priority)
- **Source**: Orders
- **Type**: Deadline
- **Default Time**: 17:00

### 🧾 Invoices
- **Icon**: 🧾
- **Color**: Purple/Red gradient (high priority if overdue)
- **Source**: Invoices
- **Type**: Deadline
- **Default Time**: 17:00

### 💳 Subscriptions
- **Icon**: 💳
- **Color**: Cyan gradient
- **Source**: Subscriptions
- **Type**: Reminder
- **Default Time**: 09:00

### 📅 Calendar Events
- **Icon**: 📅
- **Color**: Based on type
- **Source**: Custom calendar events
- **Type**: Varies
- **Time**: Custom

### 👥 Meetings
- **Icon**: 👥
- **Color**: Blue gradient
- **Source**: Calendar events with type "meeting"
- **Type**: Meeting
- **Time**: Custom

## Event Types

Each event has a type that determines its visual appearance:

### Deadline (⚡)
- **Color**: Purple gradient (\`from-[#8b5cf6] to-[#7c3aed]\`)
- **Icon**: Clock icon
- **Used for**: Tasks, orders, invoices
- **Purpose**: Important deadlines and due dates

### Meeting (👥)
- **Color**: Blue gradient (\`from-[#3b82f6] to-[#2563eb]\`)
- **Icon**: Users icon
- **Used for**: Meetings and appointments
- **Purpose**: Scheduled meetings with attendees

### Reminder (🔔)
- **Color**: Cyan gradient (\`from-[#06b6d4] to-[#0891b2]\`)
- **Icon**: Bell icon
- **Used for**: Subscription renewals
- **Purpose**: Reminders for recurring events

## Priority System

Events are color-coded by priority with visual indicators:

### High Priority (🔴)
- **Color**: Red gradient (\`from-[#f43f5e] to-[#e11d48]\`)
- **Border**: Red (\`border-[#f43f5e]\`)
- **Dot**: Red (\`bg-[#f43f5e]\`)
- **Used for**: Urgent deadlines, overdue invoices, important orders
- **Visual**: Red gradient backgrounds and borders

### Medium Priority (🟡)
- **Color**: Orange gradient (\`from-[#f59e0b] to-[#d97706]\`)
- **Border**: Orange (\`border-[#f59e0b]\`)
- **Dot**: Orange (\`bg-[#f59e0b]\`)
- **Used for**: Normal events, subscription renewals
- **Visual**: Orange gradient backgrounds and borders

### Low Priority (🟢)
- **Color**: Green gradient (\`from-[#22c55e] to-[#16a34a]\`)
- **Border**: Green (\`border-[#22c55e]\`)
- **Dot**: Green (\`bg-[#22c55e]\`)
- **Used for**: Low-urgency tasks
- **Visual**: Green gradient backgrounds and borders

## Calendar Layout

### Desktop View

**Grid Layout**:
- 7 columns (Sunday through Saturday)
- Month view with all days displayed
- Previous and next month days shown in muted colors
- Today's date highlighted with purple border
- Events displayed in day cells (up to 2 visible, "+X more" for additional)

**Navigation**:
- Previous month: Left arrow button (◀)
- Next month: Right arrow button (▶)
- Month/Year display: Center header
- Click date: Open event modal or day events modal

### Mobile View

**Agenda Layout**:
- List view of events by day
- Days with events grouped together
- Date header with weekday and date
- Event count per day
- Click event: View event details
- Click date: Add event for that date

**Empty State**:
- Message: "Aucun évènement ce mois-ci"
- Call to action: "Ajoutez un évènement avec le bouton New"

## Advanced Filtering

### Category Filters

Toggle visibility for different event categories using checkboxes:

- **Tasks** (📋): Show/hide task deadlines
- **Orders** (📦): Show/hide order deadlines
- **Invoices** (🧾): Show/hide invoice due dates
- **Subscriptions** (💳): Show/hide subscription renewals
- **Calendar Events** (📅): Show/hide custom calendar events
- **Meetings** (👥): Show/hide meetings

**Location**: Filters dropdown menu (top-right, Filters button)

**Behavior**:
- Checkboxes toggle category visibility
- Events are filtered in real-time
- Filter state persists during session
- Reset button clears all filters

### Event Type Filters

Filter events by their type:

- **All Types**: Show all event types
- **Meetings** (👥): Show only meetings
- **Deadlines** (⚡): Show only deadlines
- **Reminders** (🔔): Show only reminders

**Location**: Filters dropdown menu, Event Type section

**Behavior**:
- Radio button selection (single choice)
- Applies to all categories
- Combines with category filters

### Priority Filters

Filter events by priority level:

- **All Priorities**: Show all priorities
- **High** (🔴): Show only high-priority events
- **Medium** (🟡): Show only medium-priority events
- **Low** (🟢): Show only low-priority events

**Location**: Filters dropdown menu, Priority section

**Behavior**:
- Radio button selection (single choice)
- Applies to all categories and types
- Combines with category and type filters

### Filter Reset

**Reset Button**: Located in filters dropdown header

**Action**: Resets all filters to default state:
- All categories enabled
- All types enabled
- All priorities enabled

## Today's Events Sidebar

### Today Section

**Location**: Right sidebar, top section

**Displays**:
- All events scheduled for today
- Event title and time
- Event type icon
- Priority indicator (colored dot)
- Attendees count (for meetings)
- Location (for meetings with location)

**Empty State**:
- Icon: Calendar icon
- Message: "No events today"
- Submessage: "Enjoy your free time!"

**Interaction**:
- Click event: View event details
- Hover: Highlight event card

### Statistics Section

**Location**: Right sidebar, bottom section

**Displays Real-time Statistics**:

1. **Total events**
   - Count: Total number of events across all categories
   - Color: Blue gradient dot

2. **Tasks with deadline**
   - Count: Number of tasks with \`due_date\` set
   - Color: Purple gradient dot
   - Calculation: \`tasks.filter(task => task.due_date).length\`

3. **Orders with due_date**
   - Count: Number of orders with \`due_date\` set
   - Color: Orange gradient dot
   - Calculation: \`orders.filter(order => order.due_date).length\`

4. **Invoice due dates**
   - Count: Number of invoices with \`due_date\` set
   - Color: Pink gradient dot
   - Calculation: \`invoices.filter(invoice => invoice.due_date).length\`

5. **Events this month**
   - Count: Number of events in current month
   - Color: Cyan gradient dot
   - Calculation: Events filtered by current month and year

6. **Active subscriptions**
   - Count: Number of active subscriptions
   - Color: Green gradient dot
   - Calculation: \`subscriptions.filter(sub => sub.is_active).length\`

7. **Renewals this month**
   - Count: Number of subscription renewals in current month
   - Color: Red gradient dot
   - Calculation: Active subscriptions with \`next_renewal_date\` in current month

## Subscription Management

**Location**: Bottom of calendar page

**Component**: SubscriptionManager

**Features**:
- View all active subscriptions
- Add new subscriptions
- Edit subscription details
- Track renewal dates
- Manage billing cycles

**Integration**:
- Subscription renewals automatically appear on calendar
- Renewals generated up to 12 months in advance
- Supports weekly, monthly, quarterly, yearly cycles

## Event Interaction

### Clicking on a Date

**Empty Date** (no events):
- Opens CalendarActionModal
- Options: Create Task, Create Order
- Date pre-selected in form

**Date with Events**:
- Opens DayEventsModal
- Shows all events for that day
- Options: View events, Add event
- Click event: View event details

### Clicking on an Event

**Task Event**:
- Opens TaskDrawer
- View task details
- Edit task
- Change status
- Delete task
- Update priority and estimated hours

**Order Event**:
- Opens OrderDetailModal
- View order information
- Update order status
- Edit order details

**Other Events**:
- Shows event details (future feature)
- May open edit modal (future feature)

## Date Handling

### Date Normalization

**Format**: YYYY-MM-DD (ISO date format)

**Normalization Process**:
1. Extract date from event source
2. If date contains 'T', split and take date part
3. If date is not in YYYY-MM-DD format, convert using local timezone
4. Use \`getFullYear()\`, \`getMonth()\`, \`getDate()\` to avoid timezone issues
5. Format as YYYY-MM-DD

**Timezone Handling**:
- Dates stored in database may include timezone
- Calendar displays dates in local timezone
- Date comparison uses date-only (ignores time)
- Events matched by date string (YYYY-MM-DD)

### Today's Date Highlighting

**Visual Indicator**:
- Purple border: \`border-[#9c68f2]\`
- Background: \`bg-[#9c68f2]/10\`
- Comparison: \`day.date.toDateString() === new Date().toDateString()\`

### Month Navigation

**Previous Month**:
- Button: Left arrow (◀)
- Action: \`setCurrentDate(prev => { prev.setMonth(prev.getMonth() - 1) })\`
- Updates calendar grid
- Maintains selected date state

**Next Month**:
- Button: Right arrow (▶)
- Action: \`setCurrentDate(prev => { prev.setMonth(prev.getMonth() + 1) })\`
- Updates calendar grid
- Maintains selected date state

## Real-time Updates

### Automatic Refresh

Calendar updates automatically when:
- Tasks are created, updated, or deleted
- Orders are created or updated
- Invoices are added or updated
- Subscriptions are modified
- Calendar events are created or edited

### Refresh Trigger

**Manual Refresh**: Not directly available (automatic only)

**Automatic Triggers**:
- Component mount
- Data changes (tasks, orders, invoices, subscriptions)
- \`refreshTrigger\` state changes
- Form submissions (task creation, order creation)

### Loading States

**Initial Load**:
- Shows spinner: "Loading calendar events..."
- Waits for all data to load (tasks, orders, subscriptions, invoices)
- Displays calendar once data is ready

**Data Loading**:
- \`tasksLoading\`: Loading tasks from database
- \`ordersLoading\`: Loading orders from database
- \`subscriptionsLoading\`: Loading subscriptions from database
- \`invoicesLoading\`: Loading invoices from database
- \`loading\`: Loading calendar events from database

## Best Practices

1. **Set Due Dates**: Always set due dates for tasks and orders to see them in the calendar
2. **Use Priorities**: Mark important deadlines as high priority for better visibility
3. **Link Tasks to Orders**: Connect tasks to orders to see project-related work in context
4. **Filter Smartly**: Use category filters to focus on specific types of events
5. **Review Daily**: Check the "Today" sidebar each morning to see what's scheduled
6. **Manage Subscriptions**: Keep subscription renewals up to date to avoid missing payments
7. **Use Mobile View**: Take advantage of mobile agenda view for on-the-go access

## Tips

- **Today's Highlight**: Today's date is highlighted with a purple border for easy identification
- **Event Limits**: Desktop view shows up to 2 events per day, with "+X more" for additional events
- **Mobile Agenda**: Mobile view shows all events in an agenda format, easier to read on small screens
- **Filter Combinations**: Combine category, type, and priority filters for precise event filtering
- **Statistics**: Use statistics sidebar to get quick insights into your workload
- **Subscription Renewals**: Subscription renewals are generated automatically, no manual setup needed

## Troubleshooting

### Events Not Appearing

**Possible causes**:
- Category filter disabled
- Event doesn't have required date field (due_date, next_renewal_date)
- Event not in current month view
- Data not loaded yet

**Solutions**:
- Check category filters in Filters dropdown
- Verify event has required date field set
- Navigate to correct month
- Wait for data to load (check loading spinner)

### Subscription Renewals Not Showing

**Possible causes**:
- Subscription not active
- \`next_renewal_date\` not set
- \`billing_cycle\` not configured
- Renewal date is in the past

**Solutions**:
- Verify subscription is marked as active
- Check \`next_renewal_date\` is set
- Ensure \`billing_cycle\` is configured (weekly, monthly, quarterly, yearly)
- Renewals are generated for future dates only (up to 12 months)

### Date Selection Issues

**Possible causes**:
- Timezone conversion issues
- Date format mismatches
- Invalid date values

**Solutions**:
- Calendar uses local timezone for date calculations
- Dates are normalized to YYYY-MM-DD format
- Events are matched by date only (not time)
- Check date values in database are valid

### Filter Not Working

**Possible causes**:
- Filter state not updating
- Event doesn't match filter criteria
- Multiple filters conflicting

**Solutions**:
- Click Reset button to clear all filters
- Check event category, type, and priority match filter settings
- Verify filter dropdown is open and filters are visible
- Refresh page if filters seem stuck

---

*Explore more: [Create Event](/docs/calendar/create-event), [Sync Integrations](/docs/calendar/sync-integrations)*`,
  
  'calendar/create-event': `# Create Event

Learn how to create tasks, orders, and events directly from the calendar. The calendar provides quick access to common workflows for adding time-based activities.

## Create Event Overview

The calendar allows you to create new tasks and orders directly from the calendar interface. This streamlines your workflow by letting you add events where you need them, when you need them.

## Accessing Create Event

### Methods to Create Events

1. **New Event Button**: Click the "New Event" button in the top-right corner
2. **Click on Date**: Click on any date in the calendar (empty or with events)
3. **Empty Date Click**: Click on a date with no events to open creation modal
4. **Date with Events**: Click on a date with events, then click "Add Event" in the modal

## Calendar Action Modal

### Opening the Modal

**Triggers**:
- Click "New Event" button
- Click on empty date (no events)
- Click on date and select "Add Event"

**Modal Features**:
- Displays selected date in readable format
- Shows weekday, month, day, year
- Options: Create Task, Create Order
- Cancel button to close modal

### Modal Layout

**Header**:
- Title: "Add to Calendar"
- Selected date: Formatted date (e.g., "Monday, December 16, 2024")
- Close button: X icon (top-right)

**Action Buttons**:
- **Create Task**: Opens task creation form
- **Create Order**: Opens order creation form

**Footer**:
- Cancel button: Closes modal without action

## Creating a Task

### Task Creation Flow

1. **Open Creation Modal**: Click "New Event" or click on a date
2. **Select "Create Task"**: Click the "Create Task" button in CalendarActionModal
3. **Fill Task Form**: Complete task details in the modal
4. **Submit**: Click "Create Task" button
5. **Confirmation**: Task appears on calendar and in Workboard

### Task Form Fields

**Title** (Required):
- Type: Text input
- Placeholder: "Enter task title..."
- Validation: Cannot be empty
- Character limit: None (practical limit applies)

**Description** (Optional):
- Type: Textarea
- Placeholder: "Enter task description..."
- Rows: 3
- Character limit: None

**Order** (Optional):
- Type: Select dropdown
- Options: All orders from your account
- Format: "{order_title} - $[budget]"
- Default: "No Order"
- Purpose: Link task to an order for project context

**Priority** (Required):
- Type: Select dropdown
- Options: Low, Medium, High
- Default: Medium
- Purpose: Determine task importance and visual styling

**Estimated Hours** (Optional):
- Type: Number input
- Min: 0.5
- Step: 0.5
- Default: 1
- Purpose: Estimate time required for task

**Due Date** (Required):
- Type: Date input
- Default: Selected date from calendar (pre-filled)
- Format: YYYY-MM-DD
- Purpose: When task should be completed

### Task Form Validation

**Required Fields**:
- Title: Must not be empty
- Due Date: Automatically set from selected date

**Optional Fields**:
- Description
- Order
- Estimated Hours

**Validation Errors**:
- Empty title: Shows error toast "Task title is required"
- Form prevents submission until title is filled

### Task Creation Process

**Data Preparation**:
1. Normalize due date to YYYY-MM-DD format
2. Handle timezone issues (use local date methods)
3. Set default status: 'todo'
4. Link to order if selected
5. Set priority, estimated hours, description

**Database Insert**:
1. Create task in \`tasks\` table
2. Set \`user_id\` to current user
3. Set \`status\` to 'todo'
4. Set \`due_date\` to normalized date
5. Set other fields from form

**Post-Creation**:
1. Show success toast: "Task created successfully!"
2. Refresh tasks list
3. Reload calendar events
4. Close task form modal
5. Task appears on calendar (if due_date is set)
6. Task appears in Workboard

### Task Form Reset

**After Creation**:
- Title: Cleared
- Description: Cleared
- Order: Reset to "No Order"
- Priority: Reset to "Medium"
- Estimated Hours: Reset to 1
- Due Date: Reset to selected date (or today if no date selected)

**After Cancellation**:
- Form resets to default values
- Modal closes
- No data saved

## Creating an Order

### Order Creation Flow

1. **Open Creation Modal**: Click "New Event" or click on a date
2. **Select "Create Order"**: Click the "Create Order" button in CalendarActionModal
3. **Fill Order Form**: Complete order details in OrderForm modal
4. **Submit**: Click "Save" or "Create Order" button
5. **Confirmation**: Order appears on calendar (if due_date is set)

### Order Form Fields

**Title** (Required):
- Type: Text input
- Validation: Cannot be empty
- Purpose: Order/project name

**Description** (Optional):
- Type: Textarea
- Purpose: Detailed order description

**Client** (Required):
- Type: Select dropdown
- Options: All clients from your account
- Purpose: Link order to client

**Platform** (Optional):
- Type: Select/Text input
- Options: Fiverr, Upwork, Direct, Other
- Purpose: Track where order came from

**Budget** (Optional):
- Type: Number input
- Purpose: Order value/price
- Currency: Uses account currency preference

**Currency** (Optional):
- Type: Select dropdown
- Options: USD, EUR, GBP, etc.
- Default: Account currency preference
- Purpose: Order currency

**Start Date** (Optional):
- Type: Date input
- Default: Selected date from calendar (if available)
- Purpose: When work starts

**Due Date** (Optional):
- Type: Date input
- Default: Selected date from calendar (pre-filled)
- Purpose: When order should be completed
- **Important**: Order only appears on calendar if due_date is set

**Status** (Optional):
- Type: Select dropdown
- Options: pending, in_progress, completed, etc.
- Default: pending
- Purpose: Order status

### Order Form Validation

**Required Fields**:
- Title: Must not be empty
- Client: Must be selected

**Optional Fields**:
- Description
- Platform
- Budget
- Currency
- Start Date
- Due Date
- Status

### Order Creation Process

**Data Preparation**:
1. Normalize dates to YYYY-MM-DD format
2. Set default status if not provided
3. Link to selected client
4. Set currency from account preference or form
5. Set platform if provided

**Database Insert**:
1. Create order in \`orders\` table
2. Set \`user_id\` to current user
3. Set \`client_id\` to selected client
4. Set \`due_date\` if provided
5. Set other fields from form

**Post-Creation**:
1. Show success notification
2. Refresh orders list
3. Reload calendar events
4. Close order form modal
5. Order appears on calendar (if due_date is set)
6. Order appears in Orders page

## Day Events Modal

### When It Opens

**Triggers**:
- Click on date with existing events
- Events filtered by selected date
- Modal shows all events for that day

### Modal Features

**Header**:
- Title: "Events for {formatted_date}"
- Event count: "{count} event(s) scheduled"
- Close button: X icon

**Events List**:
- Each event displays:
  - Title
  - Time
  - Type icon
  - Priority indicator
  - Description (if available)
  - Attendees (for meetings)
  - Location (for meetings)

**Empty State**:
- Icon: Calendar icon
- Message: "No events scheduled"
- Submessage: "This day is free. Add an event to get started."
- Button: "Add Event" (opens creation modal)

**Actions**:
- Click event: View event details (opens TaskDrawer for tasks, OrderDetailModal for orders)
- "Add Another Event" button: Opens creation modal for that date

## Task Form Modal

### Modal Layout

**Header**:
- Title: "New Task" or "Edit Task"
- Close button: X icon

**Form Fields**:
- Title (required)
- Description (optional)
- Order (optional dropdown)
- Priority (required dropdown)
- Estimated Hours (optional number)
- Due Date (required date)

**Footer**:
- Cancel button: Closes modal, resets form
- Create/Update button: Submits form

### Editing Existing Tasks

**Opening Edit Modal**:
- Click on task event in calendar
- Opens TaskDrawer
- Click "Edit" button in TaskDrawer
- Opens task form modal with pre-filled data

**Pre-filled Data**:
- Title: From task
- Description: From task
- Priority: From task
- Estimated Hours: From task
- Due Date: From task (or selected date)
- Order: From task (if linked)

**Update Process**:
- Same as creation, but updates existing task
- Shows "Task updated successfully!" toast
- Refreshes calendar and Workboard

## Best Practices

1. **Set Due Dates**: Always set due dates when creating tasks and orders to see them on calendar
2. **Use Priorities**: Mark important tasks as high priority for better visibility
3. **Link Tasks to Orders**: Connect tasks to orders for better project context
4. **Fill Required Fields**: Ensure title and due date are set for tasks
5. **Select Client**: Always select a client when creating orders
6. **Use Descriptions**: Add descriptions to tasks and orders for better context
7. **Estimate Hours**: Set estimated hours for tasks to track time

## Tips

- **Pre-filled Dates**: Selected date from calendar is automatically pre-filled in forms
- **Quick Creation**: Use "New Event" button for quick task/order creation
- **Date Selection**: Click on specific date to create event for that date
- **Task-Order Linking**: Link tasks to orders to see project-related work together
- **Priority Visibility**: High-priority events are more visible on calendar
- **Mobile Friendly**: Creation modals work well on mobile devices

## Troubleshooting

### Task Not Appearing on Calendar

**Possible causes**:
- Due date not set
- Task not saved successfully
- Calendar not refreshed
- Date filter hiding task

**Solutions**:
- Verify due date is set in task form
- Check for error messages during creation
- Refresh calendar page
- Check category filters (Tasks should be enabled)

### Order Not Appearing on Calendar

**Possible causes**:
- Due date not set
- Order not saved successfully
- Calendar not refreshed
- Date filter hiding order

**Solutions**:
- Verify due date is set in order form
- Check for error messages during creation
- Refresh calendar page
- Check category filters (Orders should be enabled)

### Form Validation Errors

**Possible causes**:
- Required fields not filled
- Invalid date format
- Invalid data types

**Solutions**:
- Fill all required fields (title for tasks, title and client for orders)
- Ensure date is in correct format (YYYY-MM-DD)
- Check data types (numbers for budget, estimated hours)
- Read error messages for specific issues

### Date Not Pre-filling

**Possible causes**:
- Date not selected from calendar
- Date format issues
- Timezone conversion problems

**Solutions**:
- Click on date in calendar before creating event
- Date should automatically pre-fill in form
- If not, manually select date in form
- Check date format is YYYY-MM-DD

---

*Explore more: [Overview](/docs/calendar/overview), [Sync Integrations](/docs/calendar/sync-integrations)*`,
  
  'calendar/sync-integrations': `# Sync Integrations

Calendar synchronization with external calendar services is a planned feature that will allow you to sync your FiverFlow calendar with popular calendar applications.

## Coming Soon

Calendar sync integrations are currently in development and will be available in a future update. This feature will enable bidirectional synchronization between FiverFlow and external calendar services.

## Planned Integrations

### Google Calendar

**Status**: Planned for future release

**Features** (Planned):
- Two-way sync with Google Calendar
- Automatic event creation in Google Calendar
- Import events from Google Calendar
- Real-time synchronization
- Conflict resolution
- Calendar selection (multiple calendars support)

**Use Cases**:
- Sync FiverFlow tasks and orders to Google Calendar
- View Google Calendar events in FiverFlow
- Manage all events from one place
- Share calendar with team members
- Access calendar from mobile devices

### Outlook Calendar

**Status**: Planned for future release

**Features** (Planned):
- Two-way sync with Outlook Calendar
- Automatic event creation in Outlook
- Import events from Outlook
- Real-time synchronization
- Office 365 integration
- Multiple calendar support

**Use Cases**:
- Sync FiverFlow events to Outlook
- View Outlook events in FiverFlow
- Manage business calendar in one place
- Integration with Microsoft ecosystem

### Apple Calendar (iCal)

**Status**: Planned for future release

**Features** (Planned):
- Two-way sync with Apple Calendar
- iCloud calendar support
- Automatic event creation
- Import events from Apple Calendar
- Real-time synchronization
- Multiple calendar support

**Use Cases**:
- Sync FiverFlow events to Apple devices
- View Apple Calendar events in FiverFlow
- Manage personal and business calendars
- Access from iPhone, iPad, Mac

### Other Calendar Services

**Status**: Under consideration

**Potential Integrations**:
- CalDAV protocol support
- Yahoo Calendar
- Zoho Calendar
- Custom calendar servers

## Planned Features

### Sync Configuration

**Settings** (Planned):
- Enable/disable sync per service
- Sync direction (one-way or two-way)
- Sync frequency (real-time, hourly, daily)
- Calendar selection (which calendars to sync)
- Event type filtering (tasks, orders, invoices, etc.)
- Conflict resolution rules

### Sync Directions

**One-way Sync** (Planned):
- FiverFlow → External Calendar: Export FiverFlow events to external calendar
- External Calendar → FiverFlow: Import external calendar events to FiverFlow

**Two-way Sync** (Planned):
- Bidirectional synchronization
- Changes in FiverFlow reflect in external calendar
- Changes in external calendar reflect in FiverFlow
- Automatic conflict resolution

### Event Mapping

**Field Mapping** (Planned):
- Task → Calendar Event
- Order → Calendar Event
- Invoice → Calendar Event
- Subscription → Recurring Calendar Event
- Calendar Event → FiverFlow Event

**Metadata** (Planned):
- Preserve event descriptions
- Preserve event priorities
- Preserve event attendees
- Preserve event locations
- Preserve event reminders

### Conflict Resolution

**Strategies** (Planned):
- Last-write-wins: Most recent change takes precedence
- FiverFlow-wins: FiverFlow changes take precedence
- External-wins: External calendar changes take precedence
- Manual resolution: User decides which version to keep
- Merge: Combine changes from both sources

### Sync Status

**Monitoring** (Planned):
- Sync status indicator
- Last sync time
- Sync errors and warnings
- Sync history log
- Failed sync notifications

## Benefits

### Centralized Management

- Manage all events from FiverFlow
- View external calendar events in FiverFlow
- Reduce calendar fragmentation
- Single source of truth for scheduling

### Accessibility

- Access calendar from multiple devices
- Use favorite calendar app
- Share calendar with team members
- Integration with existing workflows

### Automation

- Automatic event creation
- Automatic updates
- Reduced manual data entry
- Real-time synchronization

### Collaboration

- Share calendar with team
- Coordinate with clients
- Schedule meetings efficiently
- Avoid scheduling conflicts

## Implementation Timeline

**Current Status**: Planned for future release

**Development Phases** (Planned):
1. **Phase 1**: Google Calendar integration (one-way sync)
2. **Phase 2**: Google Calendar integration (two-way sync)
3. **Phase 3**: Outlook Calendar integration
4. **Phase 4**: Apple Calendar integration
5. **Phase 5**: Additional calendar services
6. **Phase 6**: Advanced features (conflict resolution, filtering)

## Stay Updated

To be notified when calendar sync integrations are available:

1. Check the FiverFlow updates page
2. Subscribe to FiverFlow newsletter
3. Follow FiverFlow on social media
4. Check documentation for updates
5. Contact support for beta access (when available)

## Feedback

We welcome your feedback on planned calendar sync features:

- Which calendar services are most important to you?
- What sync features would be most useful?
- How would you like to handle conflicts?
- What event types should be synced?

Please share your feedback through:
- Support tickets
- Feature requests
- User surveys
- Community forums

## Alternative Solutions

While calendar sync is in development, you can:

### Manual Export

- Export events manually (when available)
- Import to external calendar
- Update manually as needed

### Calendar Links

- Use calendar subscription links (when available)
- Subscribe to FiverFlow calendar in external calendar
- View FiverFlow events in external calendar (read-only)

### Third-party Integrations

- Use third-party integration tools (if available)
- Connect FiverFlow to external calendars via API
- Custom integration solutions

## Frequently Asked Questions

### When will calendar sync be available?

Calendar sync is planned for a future release. We don't have a specific timeline yet, but we're working on it.

### Which calendar services will be supported?

We plan to support Google Calendar, Outlook Calendar, and Apple Calendar initially, with more services to follow.

### Will sync be free?

Sync features will be available based on your subscription plan. Specific pricing will be announced when the feature is released.

### Can I request a specific calendar service?

Yes! Please submit a feature request or contact support to request specific calendar services.

### Will sync work with multiple calendars?

Yes, we plan to support multiple calendars per service, allowing you to sync to different calendars for different event types.

---

*Explore more: [Overview](/docs/calendar/overview), [Create Event](/docs/calendar/create-event)*`,
  
  statistics: `# Statistics & Analytics

Gain deep insights into your freelance business performance with comprehensive analytics, visualizations, and detailed metrics. Track revenue, orders, clients, and performance trends over time.

## What You'll Find

The Statistics page is organized into three main sections:
- **Overview** - Comprehensive analytics and key performance indicators
- **Performance Reports** - Detailed performance metrics and visualizations
- **Exporting Data** - Data export capabilities (coming soon)

## Documentation Sections

For detailed information about each section, see:
- [Statistics Overview](/docs/statistics/overview) - Comprehensive analytics and KPIs
- [Performance Reports](/docs/statistics/performance-reports) - Detailed performance metrics
- [Exporting Data](/docs/statistics/exporting-data) - Data export capabilities (coming soon)

---

## Quick Start

### Accessing Statistics

1. Navigate to **Statistics** from the main navigation menu
2. The Statistics page is available on the **Scale** plan and above
3. If you don't have access, you'll see an upgrade prompt

### Key Features

- **Real-time Analytics**: Live data from your orders, clients, and revenue
- **Interactive Charts**: Visual representations of your business performance
- **Date Range Selection**: Analyze data for any time period
- **Performance Metrics**: Track delivery times, completion rates, and more
- **Client Insights**: Identify your top clients and growth trends

## Overview

The Statistics page provides advanced analytics and insights into your freelance business. It displays real-time data from your orders, clients, and revenue streams, helping you make informed decisions and track your business growth.

## Getting Started

### Accessing Statistics

1. Navigate to **Statistics** from the main navigation menu
2. The Statistics page is available on the **Scale** plan and above
3. If you don't have access, you'll see an upgrade prompt

### Understanding the Dashboard

The Statistics page is organized into several sections:
- **KPI Cards**: Key performance indicators at a glance
- **Revenue Charts**: Visual representation of revenue trends
- **Platform Analytics**: Earnings breakdown by platform
- **Performance Metrics**: Delivery times and completion rates
- **Client Insights**: Top clients and growth trends

## Key Performance Indicators (KPIs)

### Total Revenue

The total revenue card displays:
- **Total Revenue**: Sum of all completed orders' budgets
- **Average Order Value**: Total revenue divided by number of completed orders

This metric shows your overall business performance and helps you understand the value of each order.

### Pending Revenue

The pending revenue card shows:
- **Pending Revenue**: Sum of all orders that are not yet completed/delivered/paid
- **Open Orders**: Count of orders that are still in progress

This helps you track potential future revenue and manage your workload.

### Average Delivery Time

This metric shows:
- **Average Delivery Time**: Mean number of days between order start and completion
- Based on all completed orders with both start and completion dates

Use this to set realistic expectations for clients and improve your delivery efficiency.

### Completion Rate

The completion rate displays:
- **Completion Rate**: Percentage of orders that have been completed
- **Total Orders**: Total number of orders in your system

This metric helps you track your order fulfillment performance.

## Charts and Visualizations

### Daily Revenue Chart

A bar chart showing revenue for each day over the selected time period:
- **Blue bars** represent daily revenue
- **Tooltip** shows exact amounts when hovering
- Helps identify high-performing days and revenue patterns

### Cumulative Revenue Chart

A line chart showing cumulative revenue over time:
- **Green line** shows how your total revenue grows over time
- **Trend analysis** helps you see growth patterns
- Useful for tracking long-term business performance

### Earnings by Platform

A pie chart breaking down revenue by platform:
- **Donut chart** showing distribution of earnings
- **Color-coded** by platform (Fiverr, Upwork, etc.)
- **Legend** displays platform names and values
- Helps identify which platforms generate the most revenue

### Orders by Status (Daily)

A stacked bar chart showing order distribution by status:
- **Color-coded bars** for different order statuses:
  - Amber: Pending orders
  - Gray: In Progress orders
  - Green: Completed/Delivered/Paid orders
  - Red: Canceled orders
- **Percentage distribution** shown in tooltips
- Helps visualize your order pipeline

## Advanced Metrics

### Client Growth Trend

This section shows:
- **New Clients This Month**: Count of clients added in the current month
- **Growth Rate**: Percentage change in client count compared to last month
- **Retention Rate**: Based on completion rate and client engagement

### Order Performance

Detailed order metrics:
- **Total Orders**: Overall count of all orders
- **Average Order Value**: Mean value of completed orders
- **Pending Orders**: Number of orders still in progress

### Revenue Breakdown

Revenue analysis:
- **Total Revenue**: Sum of all completed orders
- **Pending Revenue**: Revenue from orders not yet completed
- **Revenue Growth**: Percentage change compared to previous period

## Date Range Selection

### Preset Periods

Choose from quick preset periods:
- **Today**: Current day only
- **Yesterday**: Previous day
- **Last 7 Days**: Past week
- **Last 30 Days**: Past month (default)
- **Last 90 Days**: Past quarter
- **This Week**: Current week (Sunday to Saturday)
- **Last Week**: Previous week
- **This Month**: Current month
- **Last Month**: Previous month
- **This Quarter**: Current quarter (3 months)
- **Last Quarter**: Previous quarter
- **This Year**: Current year
- **Last Year**: Previous year

### Custom Date Range

For more specific analysis:
1. Select **"Custom Range"** from the period dropdown
2. Click the calendar icon to open the date picker
3. Select your start date
4. Select your end date
5. Click **"Apply"** to update the statistics

The custom date picker features:
- Visual calendar interface
- Start and end date selection
- Automatic validation (end date must be after start date)
- Immediate application of selected range

## Performance Visualizations

### Delivery Performance

A radial bar chart showing:
- **Average Delivery Time**: Displayed in the center
- **Visual gauge**: Circular progress indicator
- **Color**: Amber/orange gradient
- Helps you quickly see if you're meeting delivery targets

### Completion Gauge

A radial gauge showing:
- **Completion Rate**: Percentage displayed prominently
- **Visual indicator**: How close you are to 100%
- **Color**: Green gradient for positive performance
- Motivates you to complete more orders

### Order Status Distribution

A detailed breakdown showing:
- **Status cards**: Each order status with color coding
- **Count**: Number of orders in each status
- **Percentage**: Proportion of total orders
- **Visual indicators**: Color-coded dots matching the status

## Additional Insights

### Top Clients

A list of your most valuable clients:
- **Client name**: Displayed clearly
- **Revenue generated**: Total revenue from this client
- **Sorted**: By revenue (highest first)
- **Top 5**: Shows your best clients

Use this to:
- Identify your most valuable relationships
- Focus on maintaining key client relationships
- Understand revenue concentration

### Recent Activity

A feed of recent orders:
- **Order title**: Quick identification
- **Creation date**: When the order was created
- **Last 5 orders**: Most recent activity
- **Chronological**: Newest first

## Refresh and Updates

### Manual Refresh

Click the **Refresh** button (circular arrow icon) to:
- Reload all statistics from the database
- Update charts and metrics
- Get the latest data

The refresh button shows a spinning animation while loading.

### Automatic Updates

Statistics update automatically when:
- You navigate to the Statistics page
- You change the date range
- You create or update orders
- You complete or cancel orders

## Understanding the Data

### Revenue Calculations

Revenue is calculated from:
- **Completed orders**: Orders with status "completed", "delivered", or "paid"
- **Order budget**: The budget amount set for each order
- **Currency**: Uses your account's default currency
- **Date filtering**: Only includes orders within the selected date range

### Order Statuses

Orders are categorized by status:
- **Pending**: Newly created orders
- **In Progress**: Orders currently being worked on
- **Completed**: Finished orders
- **Delivered**: Orders delivered to clients
- **Paid**: Orders that have been paid
- **Canceled**: Cancelled orders

### Growth Calculations

Growth rates are calculated as:
- **Percentage change**: ((Current - Previous) / Previous) × 100
- **Positive values**: Indicate growth (green)
- **Negative values**: Indicate decline (red)
- **Zero or missing data**: Shown as 0%

## Best Practices

### Regular Review

1. **Check weekly**: Review statistics every week to track trends
2. **Monthly analysis**: Do a deeper dive at the end of each month
3. **Compare periods**: Use "Last Month" to compare with current performance
4. **Set goals**: Use metrics to set and track business goals

### Performance Optimization

1. **Focus on completion rate**: Aim to complete more orders
2. **Reduce delivery time**: Work on efficiency to improve average delivery time
3. **Balance platforms**: Use platform breakdown to diversify revenue sources
4. **Maintain top clients**: Focus on your highest-value client relationships

### Data-Driven Decisions

1. **Identify trends**: Use charts to spot patterns in revenue and orders
2. **Platform strategy**: Focus efforts on platforms generating the most revenue
3. **Client prioritization**: Use top clients list to prioritize work
4. **Time management**: Use delivery time metrics to improve scheduling

## Troubleshooting

### No Data Showing

If you see "No statistics available":
1. **Check date range**: Ensure you have orders in the selected period
2. **Verify orders**: Make sure you have orders created in your account
3. **Check status**: Orders need to have proper statuses to be counted
4. **Refresh**: Click the refresh button to reload data

### Incorrect Revenue

If revenue seems incorrect:
1. **Check order status**: Only completed/delivered/paid orders count
2. **Verify budgets**: Ensure orders have budget amounts set
3. **Date range**: Check if the date range includes the right orders
4. **Currency**: Verify currency settings match your orders

### Charts Not Loading

If charts don't appear:
1. **Check plan**: Ensure you're on the Scale plan or higher
2. **Refresh page**: Reload the page to reset components
3. **Check browser**: Ensure your browser supports modern JavaScript
4. **Clear cache**: Clear browser cache and reload

### Date Range Issues

If date selection isn't working:
1. **Select custom**: Use "Custom Range" for specific dates
2. **Check format**: Dates should be in YYYY-MM-DD format
3. **Valid range**: End date must be after start date
4. **Apply button**: Remember to click "Apply" after selecting dates

## Integration with Other Features

### Orders Integration

Statistics automatically pull data from:
- **Orders page**: All orders in your system
- **Order status**: Changes reflect immediately in statistics
- **Order budgets**: Used for revenue calculations
- **Order dates**: Used for time-based filtering

### Clients Integration

Client data is integrated:
- **Client names**: Displayed in top clients and recent activity
- **Client growth**: Tracks new client acquisition
- **Client revenue**: Calculates revenue per client

### Calendar Integration

Calendar events can inform statistics:
- **Delivery dates**: Used for delivery time calculations
- **Order deadlines**: Help track performance
- **Time tracking**: Can be used for efficiency metrics

## Advanced Analytics

### Revenue Trends

Analyze revenue patterns:
- **Daily patterns**: See which days generate more revenue
- **Weekly trends**: Identify best performing weeks
- **Monthly growth**: Track month-over-month improvement
- **Seasonal patterns**: Identify seasonal trends

### Performance Metrics

Track operational efficiency:
- **Delivery speed**: Average time to complete orders
- **Completion rate**: Percentage of orders finished
- **Order value**: Average revenue per order
- **Client retention**: Growth and repeat business

### Platform Analysis

Optimize platform usage:
- **Revenue by platform**: Which platforms are most profitable
- **Platform distribution**: Balance across platforms
- **Platform trends**: Growth or decline by platform

## Export and Sharing

While direct export isn't available in the current version, you can:
- **Screenshot**: Take screenshots of specific charts
- **Browser print**: Use browser print to PDF
- **Share insights**: Discuss metrics with clients or team
- **Documentation**: Keep records of key metrics

## Subscription Requirements

The Statistics feature is available on:
- **Scale Plan** and above
- Enterprise plans
- Admin accounts

If you don't have access, you'll see a subscription upgrade prompt with details about the Scale plan.

## Tips for Maximizing Value

### Use Presets Efficiently

1. **Quick overview**: Use "Last 30 Days" for general trends
2. **Weekly review**: Use "This Week" for weekly performance
3. **Monthly comparison**: Use "Last Month" vs "This Month"
4. **Yearly analysis**: Use "This Year" for annual review

### Focus on Key Metrics

1. **Revenue growth**: Track this as your primary success metric
2. **Completion rate**: Aim for 80%+ completion rate
3. **Delivery time**: Work on reducing average delivery time
4. **Platform balance**: Don't rely too heavily on one platform

### Set Goals

1. **Revenue targets**: Set monthly revenue goals
2. **Order targets**: Aim for a specific number of orders
3. **Efficiency goals**: Target specific delivery times
4. **Growth goals**: Set percentage growth targets

---

*Last updated: November 2024*`,

  'statistics/overview': `# Statistics Overview

The Statistics Overview provides a comprehensive view of your freelance business performance with real-time analytics, interactive visualizations, and detailed metrics. All data is calculated from your orders, clients, and revenue streams.

## Page Layout

The Statistics page is organized into multiple sections:

### Header Section

- **Title**: "Analytics & Insights"
- **Date Range Picker**: Select time periods (Today, Yesterday, Last 7/30/90 Days, This/Last Week/Month/Quarter/Year, Custom Range)
- **Refresh Button**: Manual refresh to reload all statistics

### KPI Cards Section

Four key performance indicator cards displayed in a grid:

1. **Total Revenue Card**
   - Displays total revenue from completed orders
   - Shows average order value below the main metric
   - Currency formatted based on your account settings

2. **Pending Revenue Card**
   - Shows revenue from orders not yet completed
   - Displays count of open orders
   - Helps track potential future revenue

3. **Average Delivery Time Card**
   - Shows mean delivery time in days
   - Based on completed orders with start and completion dates
   - Helps set realistic client expectations

4. **Completion Rate Card**
   - Displays percentage of completed orders
   - Shows total order volume
   - Tracks order fulfillment performance

## Revenue Momentum Chart

### Primary Revenue Visualization

The main chart shows revenue momentum with comparison to previous period:

- **Chart Type**: Composed chart (Area + Line)
- **Current Period**: Solid purple line with gradient fill
- **Previous Period**: Dashed line for comparison
- **Interactive Tooltip**: Shows current vs previous revenue with delta and percentage change
- **Hover Effects**: Dims the non-focused series for better clarity
- **Height**: 360px for optimal visibility

### Chart Features

- **X-Axis**: Date labels (formatted as "MMM d")
- **Y-Axis**: Revenue amount (currency formatted)
- **Grid Lines**: Subtle grid for easy reading
- **Animation**: Smooth chart animations on load
- **Responsive**: Adapts to container width

### Tooltip Information

When hovering over data points:
- **Date**: Selected date
- **Current Revenue**: Revenue for current period
- **Previous Revenue**: Revenue for comparison period
- **Change**: Absolute difference (delta)
- **Percentage Change**: Percentage difference vs previous period

## Revenue Charts Row

### Daily Revenue Chart

- **Chart Type**: Bar chart
- **Data**: Daily revenue breakdown
- **Color**: Sky blue (#06b6d4)
- **Features**: 
  - Rounded bar tops
  - Interactive tooltips
  - Grid lines for reference

### Cumulative Revenue Chart

- **Chart Type**: Line chart
- **Data**: Cumulative revenue over time
- **Color**: Green (#22c55e)
- **Features**:
  - Smooth line with dots
  - Shows growth trajectory
  - Visual trend analysis

## Analytics Charts Row

### Earnings by Platform

- **Chart Type**: Donut/Pie chart
- **Data**: Revenue breakdown by platform (Fiverr, Upwork, etc.)
- **Features**:
  - Inner radius: 50px (donut style)
  - Outer radius: 90px
  - Color palette: Sky, Green, Purple, Amber, Rose, Blue, Slate
  - Legend: Platform names with values
  - Interactive: Hover to highlight segments

### Orders by Status

- **Chart Type**: Stacked bar chart
- **Data**: Order distribution by status
- **Status Colors**:
  - Amber: Pending orders
  - Gray: In Progress orders
  - Green: Completed/Delivered/Paid orders
  - Red: Canceled orders
- **Features**:
  - Stacked visualization
  - Percentage distribution
  - Status labels formatted (e.g., "In Progress")

## Additional Statistics Row

Three metric cards providing detailed insights:

### Client Growth Trend

- **New Clients This Month**: Count of clients added
- **Growth Rate**: Percentage change (green if positive)
- **Retention Rate**: Based on completion rate

### Order Performance

- **Total Orders**: Overall order count
- **Average Order Value**: Mean value of completed orders
- **Pending Orders**: Count of open orders (amber highlight)

### Revenue Breakdown

- **Total Revenue**: Sum of completed orders
- **Pending Revenue**: Revenue from open orders
- **Revenue Growth**: Percentage change (green if positive)

## Performance Metrics Row

### Delivery Performance

- **Chart Type**: Radial bar chart
- **Display**: Average delivery time in days
- **Visual**: Semi-circular gauge
- **Color**: Amber/Orange (#f59e0b)
- **Features**: 
  - Inner radius: 60%
  - Outer radius: 90%
  - Start angle: 180° (semi-circle)
  - End angle: 0°

### Order Status Distribution

- **Display**: List of order statuses with counts and percentages
- **Format**: Cards with color-coded dots
- **Information**: 
  - Status name (formatted)
  - Order count
  - Percentage of total
- **Colors**: Match status color scheme

## Bottom Row: Gauges and Lists

### Completion Gauge

- **Chart Type**: Radial bar chart
- **Display**: Completion rate percentage
- **Color**: Green (#22c55e)
- **Visual**: Semi-circular progress indicator
- **Features**: Shows completion percentage prominently

### Top Clients

- **Display**: List of top 5 clients by revenue
- **Information**:
  - Client name
  - Total revenue generated
- **Sorting**: Highest revenue first
- **Format**: Card-based list with hover effects

### Recent Activity

- **Display**: List of 5 most recent orders
- **Information**:
  - Order title
  - Creation date
- **Sorting**: Newest first
- **Format**: Card-based list with hover effects

## Date Range Selection

### Date Range Picker

The date range picker provides flexible time period selection:

#### Preset Options

- **Today**: Current day only
- **Yesterday**: Previous day
- **Last 7 Days**: Past 7 days (includes today)
- **Last 30 Days**: Past 30 days (default, includes today)
- **Last 90 Days**: Past 90 days (includes today)
- **This Week**: Current week (Sunday to Saturday, up to today)
- **Last Week**: Previous complete week
- **This Month**: Current month (up to today)
- **Last Month**: Previous complete month
- **This Quarter**: Current quarter (up to today)
- **Last Quarter**: Previous complete quarter
- **This Year**: Current year (up to today)
- **Last Year**: Previous complete year
- **Custom Range**: Select specific start and end dates

#### Custom Date Range

When selecting "Custom Range":
1. Click the calendar icon to open the date picker
2. Select start date from calendar
3. Select end date from calendar
4. Click "Apply" to update statistics
5. Date range is validated (end must be after start)

#### Date Picker Features

- **Visual Calendar**: Month view with day selection
- **Range Selection**: Click to select start, then end date
- **Hover Effects**: Highlights date range while selecting
- **Month Navigation**: Previous/Next month buttons
- **Today Indicator**: Highlights current day
- **Date Validation**: Prevents invalid date ranges
- **Reset Option**: Clear selected dates
- **Apply Button**: Confirms selection and closes picker

## Data Calculations

### Revenue Calculations

Revenue is calculated from:
- **Source**: Orders with status "completed", "delivered", or "paid"
- **Amount**: Order budget field
- **Date Filter**: Only orders within selected date range
- **Effective Date**: Uses completion date if available, otherwise creation date
- **Currency**: Uses account's default currency setting

### Order Filtering

Orders are filtered by:
- **Creation Date**: Must be within selected date range
- **Status**: All statuses included for counts, only completed for revenue
- **Date Range**: Inclusive of both start and end dates

### Delivery Time Calculation

Average delivery time is calculated from:
- **Start Date**: Order start_date or created_at
- **Completion Date**: Order completed_date
- **Calculation**: (Completion Date - Start Date) in days
- **Filtering**: Only orders with both dates
- **Average**: Mean of all delivery times

### Completion Rate

Completion rate is calculated as:
- **Completed Orders**: Count of orders with status "completed", "delivered", or "paid"
- **Total Orders**: Count of all orders in date range
- **Formula**: (Completed Orders / Total Orders) × 100
- **Rounding**: Rounded to nearest integer

### Growth Rates

Growth rates are calculated as:
- **Current Value**: Value for selected period
- **Previous Value**: Value for equivalent previous period
- **Formula**: ((Current - Previous) / Previous) × 100
- **Display**: Percentage with + or - sign
- **Color**: Green for positive, red for negative

## Platform Revenue Breakdown

Revenue is grouped by platform:
- **Platform Field**: Order platform field (e.g., "Fiverr", "Upwork")
- **Default**: "Other" if platform is not specified
- **Aggregation**: Sum of budgets for each platform
- **Sorting**: Highest revenue first
- **Display**: Pie chart with platform names and values

## Client Revenue Analysis

Top clients are identified by:
- **Grouping**: Orders grouped by client_name
- **Revenue**: Sum of all order budgets for each client
- **Count**: Number of orders per client
- **Sorting**: Highest revenue first
- **Limit**: Top 5 clients displayed

## Refresh Functionality

### Manual Refresh

- **Button**: Refresh icon (circular arrow) in header
- **Action**: Reloads all statistics from database
- **Animation**: Spinning animation during load
- **Update**: All charts and metrics are updated

### Automatic Updates

Statistics update automatically when:
- Page is loaded
- Date range is changed
- Orders are created or updated (requires page refresh)
- Orders are completed or canceled (requires page refresh)

## Currency Formatting

All monetary values are formatted using:
- **Currency**: Account's default currency
- **Format**: International number format
- **Locale**: en-US
- **Style**: Currency
- **Fraction Digits**: 0 (whole numbers only)
- **Symbol**: Currency symbol based on currency code

## Responsive Design

The Statistics page is fully responsive:
- **Mobile**: Single column layout, stacked cards
- **Tablet**: Two column layout for charts
- **Desktop**: Full grid layout with all sections visible
- **Large Screens**: Optimized spacing and sizing

## Best Practices

### Regular Monitoring

1. **Weekly Reviews**: Check statistics weekly to track trends
2. **Monthly Analysis**: Deep dive at month-end
3. **Period Comparison**: Compare current vs previous periods
4. **Goal Tracking**: Use metrics to track business goals

### Data Interpretation

1. **Revenue Trends**: Look for patterns in daily revenue
2. **Platform Performance**: Identify most profitable platforms
3. **Client Value**: Focus on top clients for relationship management
4. **Delivery Efficiency**: Monitor delivery times for improvement

### Performance Optimization

1. **Completion Rate**: Aim for high completion rates
2. **Delivery Speed**: Reduce average delivery time
3. **Platform Diversity**: Balance revenue across platforms
4. **Client Retention**: Maintain relationships with top clients

## Troubleshooting

### No Data Showing

If statistics show zero or no data:
1. **Check Date Range**: Ensure orders exist in selected period
2. **Verify Orders**: Confirm orders are created in your account
3. **Check Status**: Only completed orders count for revenue
4. **Refresh Data**: Click refresh button to reload

### Incorrect Metrics

If metrics seem incorrect:
1. **Order Status**: Verify order statuses are correct
2. **Budgets**: Ensure orders have budget amounts
3. **Dates**: Check order creation and completion dates
4. **Currency**: Verify currency settings match orders

### Charts Not Displaying

If charts don't appear:
1. **Plan Access**: Verify you're on Scale plan or higher
2. **Browser**: Ensure modern browser with JavaScript enabled
3. **Refresh**: Reload the page
4. **Cache**: Clear browser cache if issues persist

---

*Last updated: December 2024*`,

  'statistics/performance-reports': `# Performance Reports

The Performance Reports section provides detailed insights into your business performance through comprehensive metrics, visualizations, and comparative analysis.

## Revenue Momentum Analysis

### Current vs Previous Period Comparison

The Revenue Momentum chart compares your current period performance with the equivalent previous period:

#### Chart Components

- **Current Period Line**: Solid purple line (#9c68f2) showing current revenue
- **Previous Period Line**: Dashed line (#b896ff) showing previous period revenue
- **Area Fill**: Gradient fill under current period line
- **Interactive Tooltip**: Detailed comparison on hover
- **Focus Effects**: Dims non-focused series on hover

#### Comparison Metrics

The tooltip displays:
- **Current Revenue**: Revenue for selected date
- **Previous Revenue**: Revenue for equivalent date in previous period
- **Absolute Change**: Difference in currency amount
- **Percentage Change**: Percentage difference vs previous period
- **Date Labels**: Both current and previous period dates

#### Use Cases

- **Trend Analysis**: Identify revenue growth or decline
- **Seasonal Patterns**: Compare same periods across time
- **Performance Tracking**: Monitor progress over time
- **Goal Assessment**: Compare actual vs expected performance

## Daily Revenue Analysis

### Daily Revenue Bar Chart

The daily revenue chart shows revenue for each day in the selected period:

#### Chart Features

- **Bar Chart**: Individual bars for each day
- **Color**: Sky blue (#06b6d4)
- **Height**: 260px
- **Tooltips**: Show exact revenue on hover
- **Date Labels**: Formatted as "MMM d" (e.g., "Jan 15")

#### Insights

Use this chart to:
- **Identify Peak Days**: See which days generate most revenue
- **Pattern Recognition**: Spot weekly or monthly patterns
- **Daily Performance**: Track day-to-day revenue fluctuations
- **Anomaly Detection**: Identify unusually high or low days

### Cumulative Revenue Trend

The cumulative revenue chart shows how your total revenue grows over time:

#### Chart Features

- **Line Chart**: Smooth line with data points
- **Color**: Green (#22c55e)
- **Cumulative Calculation**: Running total of daily revenue
- **Trend Visualization**: Shows growth trajectory
- **Data Points**: Dots on line for each day

#### Insights

Use this chart to:
- **Growth Tracking**: Monitor overall revenue growth
- **Trend Analysis**: Identify accelerating or decelerating growth
- **Goal Progress**: Track progress toward revenue goals
- **Forecasting**: Project future revenue based on trends

## Platform Performance Analysis

### Earnings by Platform

The platform breakdown shows revenue distribution across different platforms:

#### Chart Features

- **Donut Chart**: Pie chart with hollow center
- **Inner Radius**: 50px
- **Outer Radius**: 90px
- **Color Palette**: 
  - Sky (#06b6d4)
  - Green (#22c55e)
  - Purple (#a78bfa)
  - Amber (#f59e0b)
  - Rose (#f43f5e)
  - Blue (#3b82f6)
  - Slate (#64748b)
- **Legend**: Platform names with revenue values
- **Interactive**: Hover to highlight segments

#### Platform Data

- **Grouping**: Orders grouped by platform field
- **Revenue**: Sum of budgets for each platform
- **Sorting**: Highest revenue first
- **Default**: "Other" for orders without platform

#### Insights

Use this analysis to:
- **Platform Optimization**: Identify most profitable platforms
- **Diversification**: Balance revenue across platforms
- **Resource Allocation**: Focus efforts on high-performing platforms
- **Strategy Planning**: Develop platform-specific strategies

## Order Status Analysis

### Orders by Status Distribution

The status distribution chart shows how orders are distributed across different statuses:

#### Chart Features

- **Stacked Bar Chart**: Bars stacked by status
- **Status Colors**:
  - Amber (#f59e0b): Pending orders
  - Gray (#64748b): In Progress orders
  - Green (#22c55e): Completed/Delivered/Paid orders
  - Red (#f43f5e): Canceled orders
- **Percentage Display**: Shows proportion of each status
- **Tooltips**: Display counts and percentages

#### Status Categories

- **Pending**: Newly created orders
- **In Progress**: Orders being worked on
- **Completed**: Finished orders
- **Delivered**: Orders delivered to clients
- **Paid**: Orders that have been paid
- **Canceled**: Cancelled orders

#### Insights

Use this analysis to:
- **Pipeline Management**: Understand order flow
- **Bottleneck Identification**: Find statuses with high counts
- **Completion Tracking**: Monitor completion rates
- **Workload Planning**: Plan based on order status distribution

## Delivery Performance Metrics

### Average Delivery Time

The delivery performance gauge shows your average delivery time:

#### Gauge Features

- **Chart Type**: Radial bar chart (semi-circle)
- **Display**: Average delivery time in days
- **Color**: Amber/Orange (#f59e0b)
- **Inner Radius**: 60%
- **Outer Radius**: 90%
- **Start Angle**: 180° (top)
- **End Angle**: 0° (bottom)

#### Calculation

- **Source**: Completed orders with both start and completion dates
- **Formula**: Mean of (completion_date - start_date) in days
- **Rounding**: Rounded to 1 decimal place
- **Display**: Prominently shown in gauge center

#### Insights

Use this metric to:
- **Performance Benchmarking**: Compare against targets
- **Efficiency Tracking**: Monitor delivery speed improvements
- **Client Communication**: Set realistic delivery expectations
- **Process Optimization**: Identify opportunities to reduce delivery time

## Completion Rate Analysis

### Completion Gauge

The completion gauge shows your order completion rate:

#### Gauge Features

- **Chart Type**: Radial bar chart (semi-circle)
- **Display**: Completion rate as percentage
- **Color**: Green (#22c55e)
- **Range**: 0% to 100%
- **Visual**: Progress indicator showing completion percentage

#### Calculation

- **Completed Orders**: Count of orders with status "completed", "delivered", or "paid"
- **Total Orders**: Count of all orders in date range
- **Formula**: (Completed Orders / Total Orders) × 100
- **Rounding**: Rounded to nearest integer

#### Insights

Use this metric to:
- **Performance Tracking**: Monitor completion rate over time
- **Goal Setting**: Set and track completion rate goals
- **Quality Assurance**: Ensure high completion rates
- **Client Satisfaction**: Higher completion rates indicate better service

## Order Status Distribution

### Status Breakdown List

The status distribution list shows detailed breakdown of order statuses:

#### Display Format

- **Status Cards**: Individual cards for each status
- **Color Dots**: Color-coded indicators matching status colors
- **Status Name**: Formatted status label (e.g., "In Progress")
- **Order Count**: Number of orders in each status
- **Percentage**: Proportion of total orders

#### Status Information

Each status card displays:
- **Status**: Formatted status name
- **Count**: Number of orders
- **Percentage**: Percentage of total orders
- **Color**: Status-specific color coding

#### Insights

Use this breakdown to:
- **Detailed Analysis**: Understand exact status distribution
- **Status Tracking**: Monitor changes in status distribution
- **Workflow Optimization**: Identify statuses needing attention
- **Resource Planning**: Plan resources based on status counts

## Client Performance Analysis

### Client Growth Trend

The client growth trend card shows client acquisition metrics:

#### Metrics Displayed

- **New Clients This Month**: Count of clients added in current month
- **Growth Rate**: Percentage change in client count (green if positive)
- **Retention Rate**: Based on completion rate and client engagement

#### Insights

Use this analysis to:
- **Acquisition Tracking**: Monitor new client acquisition
- **Growth Monitoring**: Track client base growth
- **Retention Analysis**: Understand client retention
- **Marketing Effectiveness**: Assess marketing and outreach efforts

### Top Clients

The top clients list shows your most valuable clients:

#### Display Format

- **Client List**: Top 5 clients by revenue
- **Client Name**: Displayed clearly
- **Revenue**: Total revenue generated from client
- **Sorting**: Highest revenue first
- **Card Format**: Individual cards with hover effects

#### Calculation

- **Grouping**: Orders grouped by client_name
- **Revenue**: Sum of all order budgets per client
- **Sorting**: Sorted by total revenue (descending)
- **Limit**: Top 5 clients displayed

#### Insights

Use this analysis to:
- **Client Prioritization**: Identify most valuable clients
- **Relationship Management**: Focus on key client relationships
- **Revenue Concentration**: Understand revenue distribution
- **Client Strategy**: Develop strategies for top clients

## Order Performance Metrics

### Order Performance Card

The order performance card shows detailed order metrics:

#### Metrics Displayed

- **Total Orders**: Overall count of all orders in date range
- **Average Order Value**: Mean value of completed orders
- **Pending Orders**: Count of orders still in progress (amber highlight)

#### Insights

Use this analysis to:
- **Volume Tracking**: Monitor order volume
- **Value Analysis**: Understand average order value
- **Workload Management**: Track pending orders
- **Performance Benchmarking**: Compare order metrics over time

## Revenue Breakdown Analysis

### Revenue Breakdown Card

The revenue breakdown card shows detailed revenue metrics:

#### Metrics Displayed

- **Total Revenue**: Sum of all completed orders
- **Pending Revenue**: Revenue from orders not yet completed
- **Revenue Growth**: Percentage change compared to previous period (green if positive)

#### Insights

Use this analysis to:
- **Revenue Tracking**: Monitor total revenue
- **Future Revenue**: Track pending revenue potential
- **Growth Analysis**: Understand revenue growth trends
- **Financial Planning**: Plan based on revenue metrics

## Recent Activity Feed

### Recent Orders

The recent activity feed shows your most recent orders:

#### Display Format

- **Order List**: Last 5 orders
- **Order Title**: Quick identification
- **Creation Date**: When order was created
- **Sorting**: Newest first
- **Card Format**: Individual cards with hover effects

#### Insights

Use this feed to:
- **Recent Activity**: Monitor latest order activity
- **Quick Overview**: Get quick view of recent work
- **Activity Tracking**: Track order creation patterns
- **Workflow Monitoring**: Monitor ongoing work

## Comparative Analysis

### Period Comparison

The Statistics page automatically compares current period with previous period:

#### Comparison Logic

- **Previous Period**: Equivalent period before current selection
- **Day Count**: Matches number of days in current period
- **Calculation**: Compares same day count periods
- **Visualization**: Shown in Revenue Momentum chart

#### Comparison Metrics

- **Revenue Comparison**: Current vs previous revenue
- **Growth Calculation**: Percentage change
- **Trend Analysis**: Identify growth or decline
- **Visual Indicators**: Color-coded positive/negative changes

#### Use Cases

- **Trend Analysis**: Identify revenue trends
- **Seasonal Comparison**: Compare same periods
- **Performance Tracking**: Monitor progress over time
- **Goal Assessment**: Compare actual vs expected

## Best Practices for Performance Reports

### Regular Review

1. **Weekly Analysis**: Review performance weekly
2. **Monthly Deep Dive**: Comprehensive monthly analysis
3. **Quarterly Review**: Quarterly performance assessment
4. **Annual Summary**: Year-end performance review

### Metric Focus

1. **Revenue Growth**: Primary success metric
2. **Completion Rate**: Track order fulfillment
3. **Delivery Time**: Monitor efficiency
4. **Platform Performance**: Optimize platform usage

### Action Items

1. **Identify Trends**: Use charts to spot patterns
2. **Set Goals**: Use metrics to set targets
3. **Optimize Performance**: Focus on improvement areas
4. **Client Management**: Prioritize top clients

---

*Last updated: December 2024*`,

  'statistics/exporting-data': `# Exporting Data

Data export functionality allows you to export your statistics and analytics data for external analysis, reporting, and archival purposes.

## Coming Soon

Data export capabilities are currently in development and will be available in a future update. This feature will enable you to export your statistics data in various formats for further analysis and reporting.

## Planned Features

### Export Formats

The following export formats are planned:

- **CSV (Comma-Separated Values)**: Spreadsheet-compatible format
- **Excel (.xlsx)**: Microsoft Excel format with formatting
- **PDF**: Formatted reports for sharing and printing
- **JSON**: Structured data for programmatic access

### Export Options

#### Revenue Data Export

- **Daily Revenue**: Export daily revenue data
- **Cumulative Revenue**: Export cumulative revenue trends
- **Platform Breakdown**: Export revenue by platform
- **Client Revenue**: Export revenue by client

#### Order Data Export

- **Order List**: Export order details
- **Order Status**: Export order status distribution
- **Delivery Times**: Export delivery time data
- **Order Metrics**: Export order performance metrics

#### Performance Metrics Export

- **KPIs**: Export key performance indicators
- **Completion Rates**: Export completion rate data
- **Delivery Performance**: Export delivery time metrics
- **Growth Rates**: Export growth rate calculations

### Export Customization

#### Date Range Selection

- **Preset Periods**: Export data for preset periods
- **Custom Range**: Export data for custom date ranges
- **Multiple Periods**: Export multiple periods at once

#### Data Filtering

- **Status Filtering**: Filter by order status
- **Platform Filtering**: Filter by platform
- **Client Filtering**: Filter by client
- **Custom Filters**: Apply custom data filters

#### Column Selection

- **Custom Columns**: Select specific columns to export
- **Default Columns**: Use default column set
- **Column Ordering**: Customize column order
- **Column Formatting**: Apply formatting to columns

### Export Scheduling

#### Automated Exports

- **Scheduled Exports**: Schedule regular exports
- **Email Delivery**: Receive exports via email
- **Cloud Storage**: Save exports to cloud storage
- **Export History**: Track export history

#### Export Notifications

- **Completion Notifications**: Notify when export is ready
- **Error Notifications**: Notify on export errors
- **Export Status**: Track export status
- **Download Links**: Receive download links

## Use Cases

### Financial Reporting

- **Monthly Reports**: Generate monthly financial reports
- **Tax Preparation**: Export data for tax preparation
- **Accounting**: Export data for accounting software
- **Financial Analysis**: Export data for financial analysis

### Business Analysis

- **Performance Analysis**: Export data for performance analysis
- **Trend Analysis**: Export data for trend analysis
- **Forecasting**: Export data for forecasting
- **Benchmarking**: Export data for benchmarking

### Client Reporting

- **Client Reports**: Generate client performance reports
- **Project Reports**: Export project-specific data
- **Progress Reports**: Export progress reports
- **Summary Reports**: Export summary reports

### Data Archival

- **Historical Data**: Export historical data for archival
- **Backup**: Create backups of statistics data
- **Compliance**: Export data for compliance requirements
- **Record Keeping**: Maintain records of statistics data

## Integration Possibilities

### External Tools

- **Spreadsheet Software**: Import into Excel, Google Sheets
- **Business Intelligence**: Import into BI tools
- **Accounting Software**: Import into accounting software
- **Reporting Tools**: Import into reporting tools

### API Access

- **Programmatic Access**: Access data via API
- **Automated Exports**: Schedule automated exports
- **Custom Integrations**: Build custom integrations
- **Data Synchronization**: Sync data with external systems

## Timeline

Data export functionality is planned for a future release. Specific timeline and features will be announced as development progresses.

## Frequently Asked Questions

### When will data export be available?

Data export functionality is currently in development. We will announce availability as soon as it's ready.

### What formats will be supported?

We plan to support CSV, Excel, PDF, and JSON formats initially, with more formats added based on user feedback.

### Can I schedule automated exports?

Yes, automated exports and scheduling are planned features that will be available in the export functionality.

### Will exports include all statistics data?

Yes, exports will include all statistics data available in the Statistics page, with options to filter and customize what's included.

### Can I export data for specific date ranges?

Yes, you will be able to export data for any date range, including preset periods and custom ranges.

---

*Last updated: December 2024*`,

  assistant: `# AI Assistant

Your intelligent AI-powered workflow partner that helps you manage your freelance business more efficiently. Get instant answers, automate tasks, and receive personalized guidance directly within FiverFlow.

## What You'll Find

The AI Assistant is organized into four main sections:
- **Overview** - Comprehensive guide to using the AI Assistant
- **Tasks Automation** - Automate tasks and workflows with AI
- **Custom Prompts** - Customize assistant behavior (coming soon)
- **Integrations** - Integrate with external tools (coming soon)

## Documentation Sections

For detailed information about each section, see:
- [Assistant Overview](/docs/assistant/overview) - Comprehensive guide to using the AI Assistant
- [Tasks Automation](/docs/assistant/tasks-automation) - Automate tasks and workflows
- [Custom Prompts](/docs/assistant/custom-prompts) - Customize assistant behavior (coming soon)
- [Integrations](/docs/assistant/integrations) - Integrate with external tools (coming soon)

---

## Quick Start

### Accessing the Assistant

1. Navigate to **Assistant** from the main navigation menu
2. Or click the floating assistant button (bottom right corner)
3. The Assistant is available on all plans (Lunch, Boost, Scale)

### Key Features

- **Conversational Interface**: Chat naturally with the AI assistant
- **Task Automation**: Create clients, orders, tasks, and events through conversation
- **Context Awareness**: Understands your workspace and business data
- **Quick Questions**: Pre-defined questions to get started quickly
- **Conversation History**: All conversations are saved automatically

## Overview

The AI Assistant is an integrated conversational AI that understands your FiverFlow workspace and can help you with a wide range of tasks. It can answer questions about the platform, create clients and tasks, schedule events, update orders, and provide business insights.

The Assistant is powered by OpenAI's advanced language models and is designed to understand context about your business, your plans, and your workflow.

## Getting Started

### Accessing the Assistant

1. Navigate to **Assistant** from the main navigation menu
2. The Assistant is available on the **Lunch Plan** and above
3. If you don't have access, you'll see an upgrade prompt

### First Time Use

When you first open the Assistant:
1. You'll see a welcome screen with quick question suggestions
2. Click on any suggestion to get started
3. Or type your own question in the input box
4. The Assistant will respond and you can continue the conversation

## Features

### 1. Conversational Interface

The Assistant features a modern chat interface:
- **Message bubbles**: Your messages appear on the right (blue), Assistant's on the left (gray)
- **Avatars**: Visual indicators for user and assistant messages
- **Auto-scroll**: Automatically scrolls to show new messages
- **Loading indicators**: Shows "Thinking..." while the Assistant processes your request

### 2. Quick Questions

Get started quickly with pre-defined questions:
- **"How can I manage clients efficiently?"** - Learn best practices for client management
- **"What is the difference between Lunch, Boost and Scale plans?"** - Understand plan differences
- **"How do I create a professional invoice?"** - Step-by-step invoice creation guide
- **"How can I optimize my freelance workflow?"** - Tips for workflow optimization
- **"How does the referral system work?"** - Learn about the referral program
- **"How do I track my revenue and stats?"** - Understand analytics features

### 3. Conversation History

Your conversations are automatically saved:
- **Persistent storage**: All conversations are saved to your account
- **Auto-load**: Previous conversations load when you return
- **Context awareness**: The Assistant remembers your conversation history
- **Clear conversation**: Start fresh anytime with "New Chat" or "Clear" button

### 4. Smart Input

The input area includes:
- **Auto-resizing textarea**: Expands as you type (up to 200px)
- **Keyboard shortcuts**: 
  - Press **Enter** to send
  - Press **Shift+Enter** for a new line
- **Send button**: Click the send icon or press Enter
- **Disabled state**: Input is disabled while the Assistant is thinking

## Capabilities

### Answering Questions

The Assistant can answer questions about:
- **FiverFlow features**: How to use any feature in the platform
- **Plan differences**: Comparison between Lunch, Boost, and Scale plans
- **Best practices**: Tips for managing clients, orders, and workflow
- **Platform navigation**: How to find and use specific features
- **Business advice**: General freelance business guidance

### Creating Items

The Assistant can create items in your workspace:

#### Creating Clients
Ask the Assistant to create a client:
- "Create a client named John Smith with email john@example.com"
- "Add a new client: Acme Corp, contact@acme.com, phone 555-1234"
- The Assistant will create the client and confirm the action

#### Creating Tasks
Create single or multiple tasks:
- "Create a task to review the website design"
- "Add 3 tasks for the marketing project: Research, Design, Launch"
- Tasks can be linked to clients or orders
- You can set priorities and due dates

#### Scheduling Events
Schedule calendar events:
- "Schedule a meeting with John tomorrow at 2pm"
- "Add a deadline for the project on Friday"
- Events are automatically added to your calendar

### Updating Orders

The Assistant can update existing orders:
- "Mark order #123 as completed"
- "Update order XYZ: set due date to next Friday"
- "Change order status to in progress"

### Listing Items

Ask the Assistant to show you items:
- "Show me my pending tasks"
- "List all clients"
- "What orders are due this week?"
- The Assistant can filter and search through your data

### Platform Guidance

The Assistant provides step-by-step guidance:
- Explains how to navigate the platform
- Provides context-aware help based on your current plan
- Suggests features you might not know about
- Helps you choose the right plan for your needs

## Using the Assistant

### Asking Questions

Simply type your question and press Enter:
- Be specific for better results
- Ask follow-up questions for clarification
- The Assistant maintains context throughout the conversation

### Natural Language

You can use natural, conversational language:
- "How do I add a client?"
- "What's the best way to track my revenue?"
- "Can you help me create an invoice?"
- "Show me my top clients"

### Multi-Step Actions

The Assistant can handle complex requests:
- "Create a client named ABC Corp, then create 3 tasks for them"
- "Schedule a meeting next Monday and create a reminder task"
- "Update my pending orders to show due dates"

### Error Handling

The Assistant provides helpful error messages:
- **Authentication errors**: Prompts you to log in
- **Rate limiting**: Warns if too many requests
- **Invalid actions**: Explains what went wrong
- **Plan restrictions**: Informs you if a feature requires a higher plan

## Advanced Features

### Conversation Context

The Assistant remembers:
- Your previous messages in the conversation
- Your business context (clients, orders, tasks)
- Your current plan and feature access
- Platform-specific information

### Tool Integration

The Assistant can use tools to:
- **Create clients**: Adds clients directly to your workspace
- **Create tasks**: Creates tasks in bulk or individually
- **Schedule events**: Adds events to your calendar
- **Update orders**: Modifies order status and details
- **List items**: Searches and filters your data
- **Delete items**: Removes items (with confirmation)

### Confirmation for Dangerous Actions

For destructive actions like deleting:
- The Assistant will ask for confirmation
- You must explicitly confirm before deletion
- This prevents accidental data loss

### Language Support

The Assistant supports:
- **English**: Primary language
- **French**: Also supported
- Automatically detects your language preference

## Best Practices

### Getting the Most Out of the Assistant

1. **Be specific**: More specific questions get better answers
2. **Use quick questions**: Start with suggested questions to learn the Assistant's capabilities
3. **Ask follow-ups**: Continue the conversation for deeper understanding
4. **Use natural language**: Don't worry about perfect syntax
5. **Provide context**: Mention relevant details (client names, dates, etc.)

### Workflow Integration

Integrate the Assistant into your workflow:
- Use it as a quick reference guide
- Get help when stuck on a feature
- Automate repetitive tasks
- Get business insights and suggestions

### Privacy and Security

Your conversations are:
- **Private**: Only visible to you
- **Stored securely**: Saved in your account
- **Not shared**: Never used for training or shared with third parties
- **Deletable**: You can clear conversations anytime

## Troubleshooting

### Assistant Not Responding

If the Assistant doesn't respond:
1. **Check your connection**: Ensure you have internet access
2. **Refresh the page**: Reload the page to reset the connection
3. **Check authentication**: Make sure you're logged in
4. **Try again**: Wait a moment and send your message again

### Error Messages

Common error messages and solutions:

#### "Function not found" or "Edge Function not deployed"
- The Assistant backend needs to be set up
- Contact support or check Supabase Edge Functions configuration

#### "Invalid OpenAI API key"
- The API key needs to be configured in Supabase
- Check Edge Functions → assistant-message → Secrets

#### "Rate limit reached"
- Too many requests in a short time
- Wait 1-2 minutes before trying again

#### "Authentication error"
- You need to be logged in to use the Assistant
- Sign in and try again

#### "Insufficient credits"
- OpenAI account needs credits
- Check your OpenAI account balance

### Conversation Not Saving

If conversations aren't being saved:
1. Check your internet connection
2. Refresh the page
3. Try starting a new conversation
4. Check browser console for errors

### Messages Not Appearing

If messages don't appear:
1. Check if the page is loading
2. Scroll to the bottom of the conversation
3. Refresh the page
4. Clear browser cache if issues persist

## Technical Details

### Architecture

The Assistant uses:
- **OpenAI GPT models**: Advanced language understanding
- **Supabase Edge Functions**: Secure server-side processing
- **Conversation storage**: Persistent message history
- **Tool system**: Actions that modify your workspace

### API Integration

The Assistant communicates via:
- **Supabase Edge Function**: "assistant-message" function
- **Secure authentication**: Uses your session token
- **Error handling**: Graceful error messages and retries

### Data Flow

1. You send a message
2. Message is sent to Supabase Edge Function
3. Edge Function calls OpenAI API
4. Response is processed and tools are executed if needed
5. Response is sent back and displayed
6. Conversation is saved to database

## Subscription Requirements

The AI Assistant is available on:
- **Lunch Plan** and above
- **Boost Plan** and above
- **Scale Plan** and above
- Enterprise plans
- Admin accounts

All active plans include Assistant access.

## Tips and Tricks

### Effective Questioning

1. **Start broad, then narrow**: "How do I manage clients?" then "How do I add notes to a client?"
2. **Use examples**: "Like my client ABC Corp, how do I..."
3. **Reference your data**: "Show me tasks for my client John"
4. **Ask for explanations**: "Why should I use the Calendar feature?"

### Productivity Tips

1. **Use for research**: Ask about features before diving in
2. **Quick actions**: Use natural language to create items quickly
3. **Learning tool**: Use it to understand platform features
4. **Workflow help**: Get suggestions for optimizing your workflow

### Getting Help

The Assistant can help with:
- Understanding platform features
- Troubleshooting issues
- Learning best practices
- Automating repetitive tasks
- Business advice and insights

## Examples

### Example Conversations

**User**: "How do I create a client?"
**Assistant**: "You can create a client from your Dashboard or Clients page. Go to **Clients → Add new client**, then fill in the details like name, email, and notes. Would you like me to create one for you now?"

**User**: "Create a client named Tech Solutions Inc with email info@techsolutions.com"
**Assistant**: "✅ Client created: Tech Solutions Inc (info@techsolutions.com). Would you like me to create any tasks or orders for this client?"

**User**: "What's the difference between Boost and Scale plans?"
**Assistant**: "Our Boost plan ($24/month) unlocks unlimited clients and orders, includes calendar, workboard, invoices, and AI assistant. The Scale plan ($39/month) includes everything in Boost plus advanced statistics and invoices, ideal for experienced freelancers managing multiple clients."

**User**: "Show me my pending tasks"
**Assistant**: "I'll search for your pending tasks... [Lists tasks with status 'todo' or 'in_progress']"

## Integration with Other Features

### Workspace Integration

The Assistant integrates with:
- **Clients**: Can create and reference clients
- **Orders**: Can update and list orders
- **Tasks**: Can create and manage tasks
- **Calendar**: Can schedule events
- **Statistics**: Can explain analytics

### Platform Awareness

The Assistant knows about:
- Your current plan and features
- Your clients, orders, and tasks
- Platform features and capabilities
- Best practices for your workflow

## Limitations

### What the Assistant Cannot Do

- **Access external APIs**: Cannot connect to services outside FiverFlow
- **Modify account settings**: Cannot change subscription or billing
- **Delete conversations**: Only you can clear conversations
- **Access other users' data**: Only sees your own data
- **Bypass plan restrictions**: Cannot access features you don't have

### Rate Limits

The Assistant has rate limits:
- **Rate limiting**: Too many requests trigger a cooldown
- **Wait time**: Typically 1-2 minutes between rate limit errors
- **Best practice**: Avoid rapid-fire questions

## Future Enhancements

Upcoming features (subject to roadmap):
- Voice input and output
- Advanced automation workflows
- Integration with more platform features
- Custom AI training on your data
- Multi-language support expansion

---

*Last updated: November 2024*`,

  'assistant/overview': `# Assistant Overview

The AI Assistant provides a conversational interface to interact with FiverFlow, get help, and automate tasks. It understands your workspace context and can perform actions on your behalf.

## Page Layout

The Assistant page features a modern chat interface with:

### Header Section

- **Title**: "Fiverflow Assistant"
- **Subtitle**: "Your AI-powered workflow partner"
- **Menu Button**: Opens sidebar on mobile (hamburger icon)
- **Clear Button**: Starts a new conversation (appears when messages exist)
- **Logo**: Purple gradient icon with Sparkles icon

### Sidebar

The sidebar provides quick access to:

- **New Chat Button**: Starts a fresh conversation
- **Quick Questions**: Pre-defined questions to get started
  - "How can I manage clients efficiently?"
  - "What is the difference between Lunch, Boost and Scale plans?"
  - "How do I create a professional invoice?"
  - "How can I optimize my freelance workflow?"
  - "How does the referral system work?"
  - "How do I track my revenue and stats?"

**Sidebar Behavior**:
- **Desktop**: Always visible on the left (256px width)
- **Mobile**: Hidden by default, opens as drawer overlay
- **Overlay**: Dark overlay when sidebar is open on mobile

### Main Chat Area

The main chat area displays:

- **Empty State**: Welcome message with quick questions when no messages
- **Message List**: Conversation history with user and assistant messages
- **Loading State**: "Thinking..." indicator while processing
- **Scroll Button**: "Scroll to bottom" button when scrolled up
- **Auto-scroll**: Automatically scrolls to new messages

### Composer Section

The input area at the bottom includes:

- **Textarea**: Auto-resizing input field (up to 5 lines, then scrolls)
- **Send Button**: Send message button (disabled when empty or loading)
- **Placeholder**: "Ask me anything..."
- **Keyboard Shortcuts**: 
  - Enter: Send message
  - Shift+Enter: New line
- **Hint Text**: "Press Enter to send, Shift+Enter for new line"

## Message Display

### User Messages

- **Position**: Right-aligned
- **Style**: Blue background (#3b82f6)
- **Avatar**: Blue circle with MessageSquare icon
- **Content**: White text on blue background
- **Format**: Rounded corners (rounded-2xl)

### Assistant Messages

- **Position**: Left-aligned
- **Style**: Card background (theme card color)
- **Avatar**: Purple gradient circle with Bot icon
- **Content**: Theme text color
- **Format**: Rounded corners (rounded-2xl)

### Message Formatting

- **Text Wrapping**: Preserves line breaks (whitespace-pre-wrap)
- **Font Size**: Responsive (text-sm on mobile, text-base on desktop)
- **Line Height**: Relaxed (leading-relaxed)
- **Max Width**: 85% on mobile, 75% on desktop

## Conversation Management

### Loading Conversation History

When you open the Assistant:

1. **Loading State**: Shows "Loading conversation..." spinner
2. **Fetch History**: Loads saved conversation from database
3. **Display Messages**: Shows all previous messages
4. **Auto-scroll**: Scrolls to bottom to show latest messages

### Saving Conversations

Conversations are automatically saved:

- **Auto-save**: Saves after 1 second of inactivity
- **Database**: Stored in \`assistant_conversations\` table
- **User-specific**: Each user has their own conversation
- **Persistent**: Conversations persist across sessions

### Clearing Conversations

To start a new conversation:

1. Click **"Clear"** button in header (when messages exist)
2. Or click **"New Chat"** button in sidebar
3. Confirms deletion of current conversation
4. Starts fresh with empty state

## Quick Questions

### Purpose

Quick questions help you:

- **Get started quickly**: Pre-defined questions to explore features
- **Learn capabilities**: Understand what the Assistant can do
- **Discover features**: Learn about platform features
- **Best practices**: Get tips and recommendations

### Using Quick Questions

1. **Click a question**: Click any quick question button
2. **Auto-fill**: Question is inserted into input field
3. **Send automatically**: Question is sent (on mobile, sidebar closes)
4. **Get response**: Assistant responds to your question

### Quick Question Categories

- **Client Management**: "How can I manage clients efficiently?"
- **Plan Comparison**: "What is the difference between Lunch, Boost and Scale plans?"
- **Invoice Creation**: "How do I create a professional invoice?"
- **Workflow Optimization**: "How can I optimize my freelance workflow?"
- **Referral System**: "How does the referral system work?"
- **Analytics**: "How do I track my revenue and stats?"

## Assistant Capabilities

### Answering Questions

The Assistant can answer questions about:

- **Platform Features**: How to use any FiverFlow feature
- **Plan Differences**: Comparison between Lunch, Boost, and Scale plans
- **Best Practices**: Tips for managing clients, orders, and workflow
- **Navigation**: How to find and use specific features
- **Business Advice**: General freelance business guidance

### Creating Items

The Assistant can create items in your workspace:

#### Creating Clients

**Function**: \`create_client\`

**Parameters**:
- **name** (required): Client full name
- **email** (required): Client email address
- **phone** (optional): Client phone number
- **company** (optional): Company name
- **platform** (optional): Platform where client found (Fiverr, Upwork, etc.)
- **country** (optional): Client country
- **status** (optional): Client status (active, prospect, inactive)

**Example Requests**:
- "Create a client named John Smith with email john@example.com"
- "Add a new client: Acme Corp, contact@acme.com, phone 555-1234"
- "Create client Tech Solutions Inc with email info@techsolutions.com and platform Fiverr"

#### Creating Orders

**Function**: \`create_order\`

**Parameters**:
- **title** (required): Order title
- **client_id** (required): Client ID (UUID)
- **description** (optional): Order description
- **budget** (optional): Budget amount
- **currency** (optional): Currency code (default: USD)
- **due_date** (optional): Due date in ISO format (YYYY-MM-DD)
- **status** (optional): Order status (pending, in_progress, completed, cancelled)

**Example Requests**:
- "Create an order for client ABC Corp titled 'Website Redesign' with budget 5000 USD"
- "Add order 'Logo Design' for client John Smith, due next Friday"

#### Creating Tasks

**Function**: \`create_task\`

**Parameters**:
- **title** (required): Task title
- **description** (optional): Task description
- **order_id** (optional): Order ID
- **due_date** (optional): Due date in ISO format (YYYY-MM-DD)
- **priority** (optional): Task priority (low, medium, high, urgent)
- **status** (optional): Task status (todo, in_progress, completed, cancelled)

**Example Requests**:
- "Create a task to review the website design"
- "Add 3 tasks for the marketing project: Research, Design, Launch"
- "Create high priority task 'Fix bug in checkout' due tomorrow"

#### Creating Events

**Function**: \`create_event\`

**Parameters**:
- **title** (required): Event title
- **start_at** (required): Start date/time in ISO format
- **end_at** (required): End date/time in ISO format
- **description** (optional): Event description
- **location** (optional): Event location
- **related_client_id** (optional): Related client ID

**Example Requests**:
- "Schedule a meeting with John tomorrow at 2pm"
- "Add a deadline for the project on Friday"
- "Create event 'Client Call' next Monday from 10am to 11am"

### Updating Orders

**Function**: \`update_order\`

**Parameters**:
- **id** (required): Order ID (UUID)
- **status** (optional): New status
- **budget** (optional): New budget amount
- **currency** (optional): Currency code
- **start_date** (optional): New start date
- **due_date** (optional): New due date
- **completed_date** (optional): New completed date
- **title** (optional): New title
- **description** (optional): New description

**Example Requests**:
- "Mark order #123 as completed"
- "Update order XYZ: set due date to next Friday"
- "Change order status to in progress"

### Listing Items

The Assistant can list and search your data:

#### Listing Clients

**Function**: \`list_clients\`

**Parameters**:
- **search** (optional): Search term for client name or email
- **limit** (optional): Maximum number of results (default: 20)

**Example Requests**:
- "Show me my clients"
- "List all clients"
- "Find client named John"

#### Listing Orders

**Function**: \`list_orders\`

**Parameters**:
- **status** (optional): Filter by status
- **client_id** (optional): Filter by client ID
- **limit** (optional): Maximum number of results (default: 20)

**Example Requests**:
- "Show me my pending orders"
- "List orders for client ABC Corp"
- "What orders are due this week?"

#### Listing Tasks

**Function**: \`list_tasks\`

**Parameters**:
- **status** (optional): Filter by status
- **priority** (optional): Filter by priority
- **order_id** (optional): Filter by order ID
- **limit** (optional): Maximum number of results (default: 20)

**Example Requests**:
- "Show me my pending tasks"
- "List high priority tasks"
- "What tasks are due today?"

## System Prompt

The Assistant uses a comprehensive system prompt that includes:

### Company Context

- **FiverFlow Overview**: Description of the platform
- **Mission**: Simplify business management for freelancers
- **Features**: Client Management, Order Tracking, Workboard, Calendar, Invoices, Statistics, AI Assistant, Referrals

### Product Plans

Detailed information about:

- **Lunch Plan**: $0/month, up to 5 clients, 10 orders/month
- **Boost Plan**: $24/month, unlimited clients and orders
- **Scale Plan**: $39/month, advanced statistics and invoices

### Tone & Style

- **Voice**: Speaks as part of the FiverFlow team
- **Tone**: Friendly, confident, and professional
- **Language**: Matches user's language (French or English)
- **Style**: Clear, structured responses with actionable advice

### Capabilities

The Assistant knows how to:

- Help users create clients, orders, and tasks
- Explain and guide users through each dashboard
- Answer questions about invoices, statistics, or workboard
- Provide documentation links (fiverflow.com/docs)
- Suggest automation ideas for freelancers
- Explain differences between plans

### Technical Behavior

- **Function Calling**: Uses OpenAI function calling for actions
- **Context Awareness**: Understands user's workspace and data
- **Error Handling**: Provides helpful error messages
- **Confirmation**: Asks for confirmation for dangerous actions
- **Date Handling**: Converts natural language dates to ISO format

## Error Handling

### Authentication Errors

If you're not logged in:

- **Message**: "You must be logged in to use the assistant"
- **Action**: Sign in and try again

### Rate Limiting

If too many requests:

- **Message**: "Rate limit reached. Please wait 1-2 minutes before trying again."
- **Action**: Wait before sending another message

### API Errors

Common API errors:

- **Function not found**: Edge Function not deployed
- **Invalid API key**: OpenAI API key not configured
- **Insufficient credits**: OpenAI account needs credits
- **Network error**: Connection problem

### Error Messages

Error messages are displayed as assistant messages with:

- **Error icon**: ❌ prefix
- **Clear description**: What went wrong
- **Actionable advice**: How to fix the issue

## Responsive Design

### Mobile Layout

- **Sidebar**: Hidden by default, opens as drawer
- **Overlay**: Dark overlay when sidebar is open
- **Message Width**: 85% max width
- **Touch Targets**: Minimum 44px for buttons
- **Safe Area**: Respects safe area insets

### Desktop Layout

- **Sidebar**: Always visible on left (256px)
- **Chat Area**: Full width minus sidebar
- **Message Width**: 75% max width
- **Hover Effects**: Interactive hover states

## Best Practices

### Effective Communication

1. **Be Specific**: More specific questions get better answers
2. **Provide Context**: Mention client names, dates, etc.
3. **Use Natural Language**: Don't worry about perfect syntax
4. **Ask Follow-ups**: Continue the conversation for clarity
5. **Use Quick Questions**: Start with suggested questions

### Task Automation

1. **Create in Bulk**: Create multiple items at once
2. **Link Items**: Link tasks to orders, events to clients
3. **Set Priorities**: Use priority levels for tasks
4. **Set Due Dates**: Always set due dates for time-sensitive items
5. **Update Status**: Keep order and task statuses up to date

### Workflow Integration

1. **Use as Reference**: Quick help when stuck
2. **Automate Repetitive Tasks**: Create clients and tasks quickly
3. **Get Insights**: Ask for business advice
4. **Learn Features**: Discover platform capabilities
5. **Optimize Workflow**: Get suggestions for improvement

## Troubleshooting

### Assistant Not Responding

If the Assistant doesn't respond:

1. **Check Connection**: Ensure internet access
2. **Refresh Page**: Reload to reset connection
3. **Check Authentication**: Make sure you're logged in
4. **Try Again**: Wait a moment and send again

### Messages Not Saving

If conversations aren't saving:

1. **Check Connection**: Ensure internet access
2. **Refresh Page**: Reload the page
3. **Start New Chat**: Try starting a new conversation
4. **Check Console**: Look for errors in browser console

### Function Errors

If functions fail:

1. **Check Permissions**: Ensure you have access to the feature
2. **Verify Data**: Check if required data exists (e.g., client ID)
3. **Check Plan**: Some features require specific plans
4. **Try Again**: Retry the action

---

*Last updated: December 2024*`,

  'assistant/tasks-automation': `# Tasks Automation

The AI Assistant can automate tasks and workflows by creating, updating, and managing items in your workspace through natural language commands.

## Overview

Task automation allows you to:

- **Create items quickly**: Clients, orders, tasks, and events
- **Update items**: Modify status, dates, and details
- **List and search**: Find items in your workspace
- **Multi-step actions**: Perform complex workflows in one request
- **Bulk operations**: Create multiple items at once

## Creating Clients

### Basic Client Creation

Create a client with minimal information:

**Example**: "Create a client named John Smith with email john@example.com"

**Result**: Client created with:
- Name: John Smith
- Email: john@example.com
- Status: active (default)

### Advanced Client Creation

Create a client with additional details:

**Example**: "Create a client named Acme Corp with email contact@acme.com, phone 555-1234, company Acme Corporation, platform Fiverr, country USA"

**Result**: Client created with:
- Name: Acme Corp
- Email: contact@acme.com
- Phone: 555-1234
- Company: Acme Corporation
- Platform: Fiverr
- Country: USA
- Status: active

### Client Parameters

- **name** (required): Client full name
- **email** (required): Client email address
- **phone** (optional): Client phone number
- **company** (optional): Company name
- **platform** (optional): Platform where client found (Fiverr, Upwork, etc.)
- **country** (optional): Client country
- **status** (optional): Client status (active, prospect, inactive)

## Creating Orders

### Basic Order Creation

Create an order for a client:

**Example**: "Create an order for client ABC Corp titled 'Website Redesign'"

**Requirements**:
- Client must exist (provide client name or ID)
- Title is required

### Order with Budget

Create an order with budget information:

**Example**: "Create an order for client John Smith titled 'Logo Design' with budget 500 USD, due next Friday"

**Result**: Order created with:
- Title: Logo Design
- Client: John Smith
- Budget: 500 USD
- Due Date: Next Friday
- Status: pending (default)

### Order Parameters

- **title** (required): Order title
- **client_id** (required): Client ID (UUID)
- **description** (optional): Order description
- **budget** (optional): Budget amount
- **currency** (optional): Currency code (default: USD)
- **due_date** (optional): Due date in ISO format (YYYY-MM-DD)
- **status** (optional): Order status (pending, in_progress, completed, cancelled)

### Date Handling

The Assistant converts natural language dates to ISO format:

- "tomorrow" → Next day
- "next Friday" → Next Friday
- "December 25" → December 25 of current year
- "2024-12-25" → Explicit ISO date

## Creating Tasks

### Single Task Creation

Create a single task:

**Example**: "Create a task to review the website design"

**Result**: Task created with:
- Title: Review the website design
- Status: todo (default)
- Priority: medium (default)

### Task with Details

Create a task with priority and due date:

**Example**: "Create a high priority task 'Fix bug in checkout' due tomorrow"

**Result**: Task created with:
- Title: Fix bug in checkout
- Priority: high
- Due Date: Tomorrow
- Status: todo

### Task Linked to Order

Create a task linked to an order:

**Example**: "Create a task 'Design mockup' for order 'Website Redesign'"

**Result**: Task created and linked to the order.

### Bulk Task Creation

Create multiple tasks at once:

**Example**: "Create 3 tasks for the marketing project: Research competitors, Design campaign, Launch campaign"

**Result**: Three tasks created:
1. Research competitors
2. Design campaign
3. Launch campaign

### Task Parameters

- **title** (required): Task title
- **description** (optional): Task description
- **order_id** (optional): Order ID
- **due_date** (optional): Due date in ISO format (YYYY-MM-DD)
- **priority** (optional): Task priority (low, medium, high, urgent)
- **status** (optional): Task status (todo, in_progress, completed, cancelled)

## Creating Events

### Basic Event Creation

Create a calendar event:

**Example**: "Schedule a meeting with John tomorrow at 2pm"

**Result**: Event created with:
- Title: Meeting with John
- Start: Tomorrow at 2pm
- End: Tomorrow at 3pm (default 1 hour duration)

### Event with Duration

Create an event with specific duration:

**Example**: "Create event 'Client Call' next Monday from 10am to 11am"

**Result**: Event created with:
- Title: Client Call
- Start: Next Monday at 10am
- End: Next Monday at 11am

### Event Linked to Client

Create an event linked to a client:

**Example**: "Schedule a meeting with client ABC Corp next Friday at 3pm"

**Result**: Event created and linked to the client.

### Event Parameters

- **title** (required): Event title
- **start_at** (required): Start date/time in ISO format
- **end_at** (required): End date/time in ISO format
- **description** (optional): Event description
- **location** (optional): Event location
- **related_client_id** (optional): Related client ID

## Updating Orders

### Update Order Status

Update the status of an order:

**Example**: "Mark order #123 as completed"

**Result**: Order status updated to "completed".

### Update Order Details

Update multiple order fields:

**Example**: "Update order XYZ: set due date to next Friday, budget to 1000 USD, status to in progress"

**Result**: Order updated with:
- Due Date: Next Friday
- Budget: 1000 USD
- Status: in_progress

### Update Order Parameters

- **id** (required): Order ID (UUID)
- **status** (optional): New status
- **budget** (optional): New budget amount
- **currency** (optional): Currency code
- **start_date** (optional): New start date
- **due_date** (optional): New due date
- **completed_date** (optional): New completed date
- **title** (optional): New title
- **description** (optional): New description

## Listing and Searching

### Listing Clients

List all clients or search for specific clients:

**Example**: "Show me my clients"

**Example**: "Find client named John"

**Example**: "List all clients with email containing 'gmail'"

### Listing Orders

List orders with optional filters:

**Example**: "Show me my pending orders"

**Example**: "List orders for client ABC Corp"

**Example**: "What orders are due this week?"

### Listing Tasks

List tasks with optional filters:

**Example**: "Show me my pending tasks"

**Example**: "List high priority tasks"

**Example**: "What tasks are due today?"

**Example**: "Show tasks for order 'Website Redesign'"

## Multi-Step Workflows

### Creating Client and Order

Create a client and order in one request:

**Example**: "Create a client named Tech Solutions Inc with email info@techsolutions.com, then create an order for them titled 'Website Development' with budget 5000 USD"

**Result**: 
1. Client created: Tech Solutions Inc
2. Order created: Website Development (linked to client)

### Creating Order and Tasks

Create an order and multiple tasks:

**Example**: "Create an order 'Mobile App' for client John Smith, then create 3 tasks: Design UI, Develop backend, Test app"

**Result**:
1. Order created: Mobile App
2. Three tasks created and linked to order

### Scheduling and Task Creation

Schedule an event and create a related task:

**Example**: "Schedule a meeting next Monday at 2pm and create a task 'Prepare presentation' due the same day"

**Result**:
1. Event created: Meeting (next Monday at 2pm)
2. Task created: Prepare presentation (due next Monday)

## Automation Best Practices

### Be Specific

Provide specific details for better results:

- **Client Names**: Use exact client names
- **Dates**: Specify dates clearly ("next Friday" vs "Friday")
- **Amounts**: Include currency for budgets
- **Statuses**: Specify status when needed

### Use Natural Language

The Assistant understands natural language:

- "Create a client named John"
- "Add a task to fix the bug"
- "Schedule a meeting tomorrow"
- "Mark order as completed"

### Link Related Items

Link items for better organization:

- Link tasks to orders
- Link events to clients
- Link orders to clients
- Create related items together

### Set Priorities and Dates

Always set priorities and dates for time-sensitive items:

- Use priority levels (low, medium, high, urgent)
- Set due dates for tasks and orders
- Schedule events with specific times
- Update dates when needed

### Verify Results

Always verify that items were created correctly:

- Check the Assistant's confirmation message
- Review created items in the respective pages
- Update details if needed
- Delete if created incorrectly

## Error Handling

### Missing Required Fields

If required fields are missing:

- **Error**: "Missing required field: [field name]"
- **Action**: Provide the missing field and try again

### Invalid Data

If data is invalid:

- **Error**: "Invalid [field name]: [value]"
- **Action**: Check the data format and try again

### Client Not Found

If client doesn't exist:

- **Error**: "Client not found: [client name]"
- **Action**: Create the client first or use correct client name

### Permission Errors

If you don't have permission:

- **Error**: "You don't have permission to perform this action"
- **Action**: Check your plan or contact support

## Advanced Automation

### Bulk Operations

Create multiple items efficiently:

**Example**: "Create 5 clients: John, Jane, Bob, Alice, Charlie with emails john@example.com, jane@example.com, bob@example.com, alice@example.com, charlie@example.com"

### Conditional Actions

Perform actions based on conditions:

**Example**: "List all pending orders, then mark the oldest one as in progress"

### Workflow Automation

Automate complete workflows:

**Example**: "Create a client named New Client, create an order for them titled 'Project X', create 3 tasks for the order: Task 1, Task 2, Task 3, and schedule a kickoff meeting next Monday"

## Limitations

### What Cannot Be Automated

- **Deleting items**: Requires manual confirmation
- **Bulk updates**: Limited to single item updates
- **External integrations**: Cannot connect to external services
- **Account settings**: Cannot modify subscription or billing

### Rate Limits

- **Request limit**: Too many requests trigger rate limiting
- **Wait time**: Typically 1-2 minutes between rate limit errors
- **Best practice**: Space out requests or batch operations

### Data Validation

- **Required fields**: Must provide all required fields
- **Data types**: Must use correct data types (numbers, dates, etc.)
- **Formats**: Must use correct formats (ISO dates, UUIDs, etc.)

---

*Last updated: December 2024*`,

  'assistant/custom-prompts': `# Custom Prompts

Custom prompts allow you to personalize the AI Assistant's behavior and responses to better match your workflow and preferences.

## Coming Soon

Custom prompts functionality is currently in development and will be available in a future update. This feature will enable you to customize how the Assistant responds to your questions and performs tasks.

## Planned Features

### Prompt Customization

The following customization options are planned:

- **Response Style**: Customize the Assistant's tone and style
- **Workflow Preferences**: Set default behaviors for common tasks
- **Business Context**: Provide context about your business
- **Industry-Specific**: Tailor responses to your industry
- **Language Preferences**: Customize language and terminology

### Response Style Options

- **Formal vs Casual**: Choose between formal and casual tone
- **Brief vs Detailed**: Set preference for response length
- **Technical vs Simple**: Adjust technical depth of responses
- **Emoji Usage**: Enable or disable emoji in responses

### Workflow Preferences

- **Default Priorities**: Set default priority for new tasks
- **Default Statuses**: Set default status for new items
- **Date Formats**: Customize date and time formats
- **Currency Preferences**: Set default currency
- **Notification Preferences**: Customize notification settings

### Business Context

- **Company Information**: Provide company name and details
- **Industry**: Specify your industry or niche
- **Services**: List your services or offerings
- **Target Audience**: Describe your target clients
- **Business Goals**: Share your business objectives

### Industry-Specific Customization

Tailor the Assistant for your industry:

- **Web Development**: Technical terminology and workflows
- **Design**: Design-specific tools and processes
- **Writing**: Writing and content creation workflows
- **Marketing**: Marketing and SEO terminology
- **Consulting**: Consulting and client management workflows

### Language Preferences

- **Primary Language**: Set your primary language
- **Terminology**: Customize industry-specific terms
- **Formality Level**: Adjust formality of language
- **Regional Variations**: Choose regional language variations

## Use Cases

### Personalized Workflows

- **Custom Templates**: Create templates for common tasks
- **Workflow Automation**: Automate industry-specific workflows
- **Client Communication**: Customize client communication styles
- **Task Management**: Tailor task management to your process

### Industry Optimization

- **Technical Fields**: Optimize for technical workflows
- **Creative Fields**: Optimize for creative processes
- **Business Fields**: Optimize for business management
- **Service Fields**: Optimize for service delivery

### Team Collaboration

- **Team Preferences**: Set preferences for your team
- **Consistent Style**: Maintain consistent communication style
- **Shared Context**: Share business context with team
- **Collaboration Workflows**: Optimize for team collaboration

## Timeline

Custom prompts functionality is planned for a future release. Specific timeline and features will be announced as development progresses.

## Frequently Asked Questions

### When will custom prompts be available?

Custom prompts functionality is currently in development. We will announce availability as soon as it's ready.

### What can I customize?

You will be able to customize response style, workflow preferences, business context, industry-specific settings, and language preferences.

### Will custom prompts affect other users?

No, custom prompts are user-specific and only affect your own Assistant experience.

### Can I share custom prompts with my team?

Team sharing of custom prompts is a planned feature that will be available in the custom prompts functionality.

### Will custom prompts work with all Assistant features?

Yes, custom prompts will work with all Assistant features including task automation, question answering, and workflow guidance.

---

*Last updated: December 2024*`,

  'assistant/integrations': `# Integrations

The AI Assistant can integrate with external tools and services to extend its capabilities and automate workflows across your entire business stack.

## Coming Soon

Integration functionality is currently in development and will be available in a future update. This feature will enable you to connect the Assistant with external tools and services.

## Planned Integrations

### Communication Tools

- **Email**: Integrate with email services for client communication
- **Slack**: Connect with Slack for team collaboration
- **Discord**: Integrate with Discord servers
- **Microsoft Teams**: Connect with Teams for business communication
- **WhatsApp**: Integrate with WhatsApp for client messaging

### Project Management

- **Trello**: Sync tasks with Trello boards
- **Asana**: Connect with Asana projects
- **Monday.com**: Integrate with Monday.com boards
- **Jira**: Connect with Jira for issue tracking
- **Notion**: Sync with Notion databases

### Calendar Services

- **Google Calendar**: Sync events with Google Calendar
- **Outlook Calendar**: Connect with Outlook Calendar
- **Apple Calendar**: Integrate with Apple Calendar
- **Calendly**: Connect with Calendly for scheduling

### CRM Systems

- **HubSpot**: Integrate with HubSpot CRM
- **Salesforce**: Connect with Salesforce
- **Pipedrive**: Integrate with Pipedrive
- **Zoho CRM**: Connect with Zoho CRM

### Accounting and Invoicing

- **QuickBooks**: Integrate with QuickBooks
- **Xero**: Connect with Xero
- **FreshBooks**: Integrate with FreshBooks
- **Wave**: Connect with Wave Accounting

### Payment Processors

- **Stripe**: Integrate with Stripe for payments
- **PayPal**: Connect with PayPal
- **Square**: Integrate with Square
- **Wise**: Connect with Wise for international payments

### File Storage

- **Google Drive**: Sync files with Google Drive
- **Dropbox**: Connect with Dropbox
- **OneDrive**: Integrate with OneDrive
- **Box**: Connect with Box

### Development Tools

- **GitHub**: Integrate with GitHub for code management
- **GitLab**: Connect with GitLab
- **Bitbucket**: Integrate with Bitbucket
- **Jira**: Connect with Jira for development tracking

## Integration Features

### Bi-Directional Sync

- **Data Sync**: Sync data between FiverFlow and external tools
- **Real-Time Updates**: Real-time synchronization of changes
- **Conflict Resolution**: Handle conflicts between systems
- **Data Mapping**: Map fields between systems

### Automation Workflows

- **Trigger Actions**: Trigger actions in external tools
- **Event-Based Automation**: Automate based on external events
- **Multi-Step Workflows**: Create complex automation workflows
- **Conditional Logic**: Add conditional logic to workflows

### Data Import/Export

- **Import Data**: Import data from external tools
- **Export Data**: Export data to external tools
- **Data Transformation**: Transform data during import/export
- **Batch Operations**: Perform batch import/export operations

### Authentication

- **OAuth Integration**: Secure OAuth authentication
- **API Keys**: Support for API key authentication
- **Token Management**: Secure token storage and management
- **Permission Management**: Granular permission control

## Use Cases

### Client Communication

- **Email Integration**: Send emails directly from Assistant
- **Slack Notifications**: Get notifications in Slack
- **WhatsApp Messages**: Send WhatsApp messages to clients
- **SMS Integration**: Send SMS messages

### Project Management

- **Task Syncing**: Sync tasks with project management tools
- **Project Updates**: Get project updates from external tools
- **Team Collaboration**: Collaborate with team in external tools
- **Progress Tracking**: Track progress across tools

### Calendar Management

- **Event Syncing**: Sync events with calendar services
- **Meeting Scheduling**: Schedule meetings through Calendly
- **Availability Management**: Manage availability across calendars
- **Time Zone Handling**: Handle time zones automatically

### Financial Management

- **Invoice Syncing**: Sync invoices with accounting tools
- **Payment Tracking**: Track payments across systems
- **Expense Management**: Manage expenses in accounting tools
- **Financial Reporting**: Generate reports from integrated tools

## Integration Setup

### Authentication Process

1. **Select Integration**: Choose the integration you want to connect
2. **Authorize Access**: Authorize FiverFlow to access your account
3. **Configure Settings**: Configure integration settings
4. **Test Connection**: Test the connection to ensure it works
5. **Enable Integration**: Enable the integration for use

### Configuration Options

- **Sync Direction**: Choose sync direction (one-way or bi-directional)
- **Sync Frequency**: Set how often to sync data
- **Field Mapping**: Map fields between systems
- **Filter Rules**: Set rules for what data to sync
- **Notification Settings**: Configure notification preferences

### Security

- **Encrypted Connections**: All connections are encrypted
- **Secure Storage**: Credentials stored securely
- **Permission Control**: Granular permission control
- **Audit Logging**: Track all integration activities

## Timeline

Integration functionality is planned for a future release. Specific timeline and integrations will be announced as development progresses.

## Frequently Asked Questions

### When will integrations be available?

Integration functionality is currently in development. We will announce availability as soon as it's ready.

### Which integrations will be supported?

We plan to support popular tools and services across communication, project management, calendar, CRM, accounting, and development categories.

### Will integrations be free?

Integration availability and pricing will be announced when the feature is released. Some integrations may be available on specific plans.

### Can I request specific integrations?

Yes, we welcome feedback and integration requests. Please contact support or submit a feature request.

### Will integrations work with all Assistant features?

Integrations will work with relevant Assistant features. Specific compatibility will be documented for each integration.

### How secure are integrations?

All integrations use secure authentication methods (OAuth, API keys) and encrypted connections. Credentials are stored securely and never exposed.

---

*Last updated: December 2024*`,

  clients: `# Clients

Manage all your client relationships in one centralized location. Track contact information, communication history, project details, and business metrics for each client.

## What You'll Find

The Clients page is organized into four main sections:
- **Overview** - Comprehensive client management features and interface
- **Adding Clients** - Step-by-step guide to creating client profiles
- **Managing Data** - Editing, viewing, and organizing client information
- **Exporting Clients** - Export client data for backup or analysis (coming soon)

## Documentation Sections

For detailed information about each section, see:
- [Clients Overview](/docs/clients/overview) - Comprehensive client management features
- [Adding Clients](/docs/clients/adding-clients) - Guide to creating client profiles
- [Managing Data](/docs/clients/managing-data) - Editing and organizing clients
- [Exporting Clients](/docs/clients/exporting-clients) - Export client data (coming soon)

## Quick Start

### Accessing Clients

1. Navigate to **Clients** from the main navigation menu
2. The Clients page is available on the **Lunch Plan** and above
3. Plan limits: Lunch plan allows up to 5 clients, Boost and above have unlimited clients

### Creating Your First Client

1. Click the **"New Client"** button in the top-right corner
2. Fill in the client information form (3-step process)
3. Save the client
4. The client will appear in your clients list

## Key Features

- **Card and List Views**: Switch between visual cards and compact list view
- **Advanced Search**: Real-time search across name, company, and email
- **Smart Filtering**: Filter by status, platform, and country
- **Client Statistics**: View total clients, active clients, prospects, and monthly additions
- **Detailed Profiles**: Store comprehensive client information
- **Integration**: Link clients to orders, invoices, tasks, and calendar events

## Subscription Limits

### Lunch Plan
- **5 clients maximum**
- Full client management features
- All fields and filters available

### Boost Plan and Above
- **Unlimited clients**
- All features available
- No restrictions

---

*Last updated: December 2024*`,

  'clients/overview': `# Clients Overview

The Clients page is your Customer Relationship Management (CRM) hub within FiverFlow. It allows you to store and organize all information about your clients, track their projects, monitor revenue, and maintain detailed contact records.

## Page Layout

The Clients page is organized into multiple sections:

### Header Statistics Section

Four key performance indicator cards displayed in a grid:

1. **Total Clients Card**
   - Displays total number of clients in your system
   - Updates in real-time as you add or remove clients
   - Shows Users icon

2. **Active Clients Card**
   - Shows number of clients with "Active" status
   - Helps track current active relationships
   - Shows UserCheck icon in green

3. **Prospects Card**
   - Displays number of potential clients (Prospect status)
   - Tracks potential business opportunities
   - Shows TrendingUp icon in yellow

4. **This Month Card**
   - Shows number of clients created in the current month
   - Helps track client acquisition rate
   - Shows Calendar icon in purple

### Search and Actions Bar

- **Search Bar**: Real-time search across client name, company name, and email
  - Instant filtering as you type
  - Clear button (X) to reset search
  - Search icon for visual indication
  
- **View Mode Toggle**: Switch between card view and list view
  - Cards: Visual cards with key information
  - List: Compact table format
  
- **New Client Button**: Opens the client creation form

### Filters Section

Three filter dropdowns:

1. **Status Filter**
   - All statuses
   - Prospect
   - Active
   - Inactive
   - Project completed

2. **Platform Filter**
   - All platforms
   - Dynamically populated from your clients
   - Common platforms: Fiverr, Upwork, Direct, etc.

3. **Country Filter**
   - All countries
   - Dynamically populated from your clients
   - Filters by client location

### Client Display Area

#### Card View (Default)

- **Visual Cards**: Each client displayed as a card
- **Card Content**:
  - Avatar: Initial of client name in gradient circle
  - Name and Company: Client name and company name
  - Platform Icon: Visual indicator of platform
  - Contact Info: Email and country
  - Status Badge: Color-coded status indicator
  - Creation Date: When client was added
  - Action Buttons: View, Edit, Delete (visible on hover)

- **Card Layout**: Grid layout (1-4 columns based on screen size)
  - Mobile: 1 column
  - Tablet: 2 columns
  - Desktop: 3 columns
  - Large Desktop: 4 columns

#### List View

- **Table Format**: Compact table display
- **Columns**:
  - Client: Name, company, avatar
  - Platform: Platform with icon
  - Contact: Email and country
  - Status: Status badge
  - Created: Creation date
  - Actions: View, Edit, Delete buttons

- **Table Features**:
  - Hover effects on rows
  - Quick action buttons
  - Responsive design

### Pagination

When you have more than 12 clients:
- **Page Size**: 12 clients per page
- **Navigation**: Previous/Next buttons
- **Page Indicator**: Shows current page and total pages
- **Results Count**: Displays range of clients shown (e.g., "Showing 1 to 12 of 50 clients")

## Client Information Structure

Each client can store comprehensive information organized into categories:

### Identity Section

- **Full Name**: Client's full name (required)
- **Client Type**: Type of client (individual, company, etc.)
- **Company Name**: Company or organization name
- **Platform**: Where you found/met the client
  - Options: Fiverr, Upwork, Freelancer, Direct, LinkedIn, Malt, Toptal, 99designs, PeoplePerHour, Other

### Contact Section

- **Primary Email**: Main email address
- **Secondary Email**: Additional email address
- **Phone**: Primary phone number
- **WhatsApp**: WhatsApp number for international clients
- **Preferred Contact Method**: Preferred way to contact client

### Location Section

- **Country**: Client's country
- **City**: Client's city
- **Timezone**: Client's timezone for scheduling
- **Preferred Language**: Client's preferred language

### Business Section

- **Industry**: Client's industry sector
  - Options: Technology, Healthcare, Finance, Education, Retail, Manufacturing, Real Estate, Marketing, Consulting, Non-profit, Government, Entertainment, Food & Beverage, Travel, Other
- **Services Needed**: Types of services they require (multi-select)
  - Options: Web Development, Mobile Development, Graphic Design, UI/UX Design, Content Writing, SEO, Digital Marketing, Social Media Management, Video Editing, Photography, Translation, Data Entry, Virtual Assistant, Consulting, Other
- **Budget Range**: Expected project budget range
- **Collaboration Frequency**: How often you work together

### Commercial Section

- **Acquisition Source**: Where the client came from
- **Client Status**: Current status
  - Prospect: Potential client
  - Active: Currently working with
  - Inactive: Past client, not currently active
  - Project Completed: Project finished
- **Priority Level**: Low, Medium, or High
- **Payment Terms**: Agreed payment structure
  - Options: 50% upfront, 50% on completion, 100% upfront, Net 15, Net 30, Weekly payments, Monthly payments, Other

### Notes & Observations Section

- **Availability Notes**: Notes about client availability
- **Important Notes**: Key information to remember
- **Next Action**: Planned next action
- **Next Action Date**: Date for next action

### Tags & Categories Section

- **Tags**: Custom tags for organization
  - Add tags by typing and pressing Enter
  - Remove tags by clicking X
  - Use tags for segmentation (e.g., "VIP", "Recurring", "One-time")

## Client View Modal

When you click on a client, a detailed view modal opens showing:

### Modal Header

- **Client Avatar**: Initial in colored circle
- **Client Name**: Large display of client name
- **Creation Date**: When client was created
- **Edit Button**: Quick access to edit client
- **Close Button**: Close the modal

### Modal Sections

1. **Identity Section** (gradient background)
   - Full name
   - Client type
   - Company name
   - Platform

2. **Contact Section**
   - Primary email (copyable)
   - Secondary email (copyable)
   - Phone (copyable)
   - WhatsApp (copyable)
   - Preferred contact method

3. **Location Section**
   - Country
   - City
   - Timezone
   - Preferred language

4. **Business Section** (gradient background)
   - Industry
   - Services needed
   - Estimated budget
   - Collaboration frequency

5. **Commercial Section**
   - Acquisition source
   - Client status
   - Priority level
   - Payment terms

6. **Notes & Observations Section**
   - Availability notes
   - Important notes

7. **Follow-up & Actions Section** (gradient background)
   - Next action
   - Next action date

8. **Tags & Categories Section**
   - All tags displayed as badges

### Modal Features

- **Copyable Fields**: Email and phone numbers can be copied with one click
- **Responsive Design**: Adapts to screen size
- **Scrollable Content**: Long content scrolls within modal
- **Edit Access**: Quick edit button in header

## Client Actions

For each client, you can perform several actions:

### View Client

1. Click on a client card or list item
2. Client view modal opens
3. View all client information
4. Access related orders and revenue

### Edit Client

1. Click **Edit** button on client card/list
2. Or click **Edit** in client view modal
3. Client form opens with current data
4. Update any fields
5. Save changes

### Delete Client

1. Click **Delete** button (trash icon) on client
2. Confirmation dialog appears
3. Confirm deletion
4. Client is permanently removed

**Warning**: Deleting a client is permanent and cannot be undone. Associated orders and invoices may be affected.

## Search and Filtering

### Search Functionality

- **Real-time Search**: Type to instantly filter clients
- **Multi-field Search**: Searches across:
  - Client name
  - Company name
  - Email address
- **Case-insensitive**: Finds matches regardless of capitalization
- **Instant Results**: Results update as you type
- **Clear Button**: X button to clear search

### Advanced Filtering

- **Status Filter**: Filter by client status
  - All statuses
  - Prospect
  - Active
  - Inactive
  - Project completed

- **Platform Filter**: Filter by platform
  - All platforms
  - Dynamically populated from your clients
  - Shows only platforms you've used

- **Country Filter**: Filter by country
  - All countries
  - Dynamically populated from your clients
  - Shows only countries you have clients in

### Filter Combinations

- **Multiple Filters**: Apply multiple filters simultaneously
- **Filter + Search**: Combine search with filters
- **Reset Filters**: Clear all filters by selecting "All" options
- **Automatic Pagination**: Filters reset to page 1 when applied

## View Modes

### Card View (Default)

**Advantages**:
- Visual and intuitive
- Quick overview of key information
- Easy to scan
- Hover effects for interactivity

**Best For**:
- Browsing clients
- Visual identification
- Quick actions

### List View

**Advantages**:
- Compact display
- See more clients at once
- Table format for data comparison
- Quick actions in rows

**Best For**:
- Managing many clients
- Comparing client information
- Quick editing

### Switching Views

- Use the view mode toggle in the header
- View preference is not saved (resets on page reload)
- Both views support all actions (view, edit, delete)

## Integration with Other Features

### Orders Integration

- **Client Linking**: All orders are linked to clients
- **Order History**: View all orders for a client in client view modal
- **Revenue Tracking**: Calculate total revenue per client from orders
- **Order Count**: See total number of orders per client

### Calendar Integration

- **Client Events**: Schedule meetings and deadlines for clients
- **Client Filtering**: Filter calendar events by client
- **Client Reminders**: Set reminders for client-related tasks

### Invoices Integration

- **Invoice Association**: Link invoices to clients
- **Billing History**: View all invoices for a client
- **Payment Tracking**: Track payment status per client

### Workboard Integration

- **Client Tasks**: Create tasks linked to clients
- **Task Organization**: Group tasks by client in workboard
- **Client Filtering**: Filter tasks by client

### Statistics Integration

- **Client Analytics**: See client performance in statistics
- **Top Clients**: Identify highest-value clients
- **Client Growth**: Track new client acquisition
- **Revenue per Client**: Monitor client value

## Subscription Limits

### Lunch Plan

- **5 clients maximum**
- Full client management features
- All fields and filters available
- Warning banner when limit is reached

### Boost Plan and Above

- **Unlimited clients**
- All features available
- No restrictions
- No limit warnings

### Plan Limit Indicators

- **Warning Banner**: Shows when approaching limit on free plan
- **Upgrade Prompt**: Suggests upgrading when limit is reached
- **Limit Check**: Validates before creating new client

## Best Practices

### Organizing Clients

1. **Use Status Effectively**: Keep status updated to track client lifecycle
2. **Add Detailed Notes**: Document important conversations and agreements
3. **Set Priorities**: Mark high-value clients with high priority
4. **Use Tags**: Create tags for special categories (e.g., "VIP", "Recurring", "One-time")
5. **Complete Profiles**: Fill in all relevant fields for better organization

### Client Management Tips

1. **Regular Updates**: Keep client information current
2. **Status Tracking**: Update status as relationships evolve
3. **Note Taking**: Document important details in notes
4. **Platform Tracking**: Track which platforms generate the most clients
5. **Revenue Monitoring**: Use client view to track revenue per client

### Communication

1. **Contact Information**: Keep all contact methods updated
2. **Preferred Methods**: Note preferred communication channels
3. **Timezone Awareness**: Set timezone for scheduling
4. **Language Preference**: Note preferred language if applicable

## Troubleshooting

### Client Not Loading

If clients aren't loading:
1. **Check internet connection**: Ensure you're connected
2. **Refresh page**: Reload the page
3. **Check browser console**: Look for error messages
4. **Verify authentication**: Ensure you're logged in

### Search Not Working

If search isn't finding clients:
1. **Check spelling**: Verify the search term
2. **Clear filters**: Reset all filters
3. **Check view mode**: Try switching between card and list view
4. **Refresh page**: Reload the page

### Filters Not Applying

If filters aren't working:
1. **Select a filter**: Make sure a filter option is selected
2. **Clear and reapply**: Clear filters and select again
3. **Check data**: Ensure clients have the data you're filtering by
4. **Refresh**: Refresh the page

### Client Details Not Loading

If client details won't load:
1. **Check connection**: Ensure internet connection is stable
2. **Try again**: Click the client again
3. **Refresh page**: Reload the page
4. **Check browser console**: Look for error messages

---

*Last updated: December 2024*`,

  'clients/adding-clients': `# Adding Clients

Learn how to create comprehensive client profiles in FiverFlow. The client creation process is organized into a 3-step form that guides you through entering all necessary information.

## Accessing the Client Form

### From the Clients Page

1. Navigate to **Clients** from the main navigation menu
2. Click the **"New Client"** button in the top-right corner
3. The client creation form opens in a modal

### Plan Requirements

- **Lunch Plan**: Allows up to 5 clients
- **Boost Plan and Above**: Unlimited clients
- If you've reached your limit, you'll see a warning and upgrade prompt

## Client Creation Form

The client creation form is organized into **3 steps** with a progress indicator:

### Step 1: Basic Information

**Required Fields**:
- **Full Name**: Client's full name (required)
- **Platform**: Where you found/met the client (required)

**Optional Fields**:
- **Company Name**: Company or organization name

**Platform Options**:
- Fiverr
- Upwork
- Freelancer
- Direct
- LinkedIn
- Malt
- Toptal
- 99designs
- PeoplePerHour
- Other

**Validation**:
- Name is required and cannot be empty
- Platform is required and must be selected
- Form will show error messages if required fields are missing

### Step 2: Contact Information

**Optional Fields**:
- **Email**: Primary email address
- **Phone**: Primary phone number
- **WhatsApp**: WhatsApp number for international clients

**Field Features**:
- Email validation (format checking)
- Phone number format (international format supported)
- All fields are optional but recommended

### Step 3: Additional Details

**Business Information**:
- **Industry**: Client's industry sector
  - Options: Technology, Healthcare, Finance, Education, Retail, Manufacturing, Real Estate, Marketing, Consulting, Non-profit, Government, Entertainment, Food & Beverage, Travel, Other
- **Services Needed**: Types of services they require (multi-select)
  - Options: Web Development, Mobile Development, Graphic Design, UI/UX Design, Content Writing, SEO, Digital Marketing, Social Media Management, Video Editing, Photography, Translation, Data Entry, Virtual Assistant, Consulting, Other
  - Select multiple services by checking boxes

**Status & Priority**:
- **Client Status**: 
  - Prospect (default)
  - Active Client
  - Inactive Client
  - Project Completed
- **Priority Level**: 
  - Low
  - Medium (default)
  - High

**Payment Information**:
- **Payment Terms**: Agreed payment structure
  - Options: 50% upfront, 50% on completion, 100% upfront, Net 15, Net 30, Weekly payments, Monthly payments, Other

**Notes & Tags**:
- **Important Notes**: Key information to remember (textarea)
- **Custom Tags**: Add tags for organization
  - Type a tag and press Enter to add
  - Tags appear as badges
  - Click X on a tag to remove it
  - Use tags for segmentation (e.g., "VIP", "Recurring", "One-time")

## Form Navigation

### Progress Indicator

The form displays a progress indicator showing:
- **Step Numbers**: 1, 2, 3
- **Step Labels**: Basic Info, Contact, Details
- **Step Icons**: Visual icons for each step
- **Progress Bar**: Visual progress bar showing completion percentage
- **Current Step**: Highlighted step indicator

### Navigation Buttons

- **Previous Button**: Go back to previous step (disabled on step 1)
- **Next Button**: Advance to next step (validates current step)
- **Cancel Button**: Close form without saving
- **Create/Update Button**: Submit form (only on final step)

### Step Validation

- **Step 1 Validation**: 
  - Name is required
  - Platform is required
  - Shows error toast if validation fails

- **Step 2 Validation**: 
  - No required fields
  - Email format validation (if provided)

- **Step 3 Validation**: 
  - No required fields
  - All fields are optional

## Creating a Client

### Step-by-Step Process

1. **Click "New Client"**: Opens the client creation form

2. **Step 1: Basic Information**
   - Enter client's full name (required)
   - Select platform (required)
   - Optionally enter company name
   - Click "Next" to continue

3. **Step 2: Contact Information**
   - Enter email address (optional)
   - Enter phone number (optional)
   - Enter WhatsApp number (optional)
   - Click "Next" to continue or "Previous" to go back

4. **Step 3: Additional Details**
   - Select industry (optional)
   - Select services needed (optional, multi-select)
   - Select client status (default: Prospect)
   - Select priority level (default: Medium)
   - Select payment terms (optional)
   - Add important notes (optional)
   - Add custom tags (optional)
   - Click "Create" to save client

5. **Success**: Client is created and appears in your clients list

### Form Submission

When you click "Create":
- Form validates all required fields
- Checks plan limits (if on free plan)
- Shows loading state
- Creates client in database
- Shows success toast notification
- Closes form modal
- Refreshes clients list
- New client appears in list

## Editing Existing Clients

### Opening Edit Form

1. Click **Edit** button on a client card/list item
2. Or click **Edit** in client view modal
3. Client form opens with current data pre-filled
4. All steps show existing data

### Edit Form Features

- **Pre-filled Data**: All existing client data is loaded
- **Same 3 Steps**: Same form structure as creation
- **Update Button**: Button shows "Update" instead of "Create"
- **All Fields Editable**: Can update any field
- **Validation**: Same validation rules as creation

### Updating a Client

1. **Open Edit Form**: Click Edit on client
2. **Navigate Steps**: Use Next/Previous to navigate steps
3. **Update Fields**: Change any fields you want to update
4. **Save Changes**: Click "Update" to save
5. **Success**: Client is updated and list refreshes

## Form Field Details

### Text Fields

- **Full Name**: Single-line text input
- **Company Name**: Single-line text input
- **Email**: Email input with format validation
- **Phone**: Tel input for phone numbers
- **WhatsApp**: Tel input for WhatsApp numbers
- **Important Notes**: Multi-line textarea

### Select Fields

- **Platform**: Dropdown with platform options
- **Industry**: Dropdown with industry options
- **Client Status**: Dropdown with status options
- **Priority Level**: Dropdown with priority options
- **Payment Terms**: Dropdown with payment term options

### Multi-Select Fields

- **Services Needed**: Checkbox group
  - Multiple services can be selected
  - Checkboxes arranged in grid
  - Scrollable if many options

### Tag Input

- **Custom Tags**: Text input with tag functionality
  - Type tag and press Enter to add
  - Tags appear as badges below input
  - Click X on tag to remove
  - Duplicate tags are prevented

## Validation and Error Handling

### Required Field Validation

- **Name**: Must not be empty
- **Platform**: Must be selected
- **Error Messages**: Toast notifications show validation errors
- **Form Blocking**: Cannot proceed to next step if validation fails

### Email Validation

- **Format Checking**: Validates email format
- **Optional Field**: Email is not required
- **Error Display**: Shows error if format is invalid

### Plan Limit Validation

- **Limit Check**: Checks if you've reached client limit
- **Free Plan**: Maximum 5 clients
- **Upgrade Prompt**: Shows upgrade suggestion if limit reached
- **Blocking**: Prevents client creation if limit reached

### Error Messages

- **Toast Notifications**: Error messages appear as toast notifications
- **Clear Messages**: Error messages are clear and actionable
- **Validation Feedback**: Immediate feedback on validation errors

## Best Practices

### Completing Client Profiles

1. **Fill Required Fields**: Always fill name and platform
2. **Add Contact Information**: Email and phone are highly recommended
3. **Select Industry**: Helps with organization and filtering
4. **Add Services**: Select all services client needs
5. **Set Status**: Keep status current and accurate
6. **Add Notes**: Document important information
7. **Use Tags**: Organize clients with tags

### Client Information Tips

1. **Accurate Names**: Use full, accurate client names
2. **Platform Tracking**: Always select platform for analytics
3. **Complete Contact Info**: Add all available contact methods
4. **Industry Classification**: Select appropriate industry
5. **Service Selection**: Select all relevant services
6. **Status Management**: Keep status updated
7. **Priority Setting**: Mark high-value clients as high priority
8. **Payment Terms**: Document agreed payment terms
9. **Important Notes**: Document key information and agreements
10. **Tag Organization**: Use consistent tagging system

### Efficient Client Creation

1. **Quick Creation**: Create client with minimal info, add details later
2. **Bulk Information**: Fill all fields at once for completeness
3. **Template Approach**: Use consistent information structure
4. **Tag Strategy**: Develop tagging system for organization

## Troubleshooting

### Client Not Saving

If a client isn't saving:
1. **Check required fields**: Name and platform are required
2. **Verify plan limits**: Ensure you haven't exceeded client limit
3. **Check internet connection**: Ensure you're connected
4. **Try refreshing**: Refresh the page and try again
5. **Check error messages**: Look for error toast notifications

### Form Not Submitting

If form won't submit:
1. **Check validation**: Ensure all required fields are filled
2. **Check plan limits**: Verify you haven't reached client limit
3. **Check loading state**: Wait for previous operation to complete
4. **Try again**: Retry submission

### Tags Not Adding

If tags aren't adding:
1. **Check input**: Ensure tag text is entered
2. **Press Enter**: Tags are added by pressing Enter
3. **Check duplicates**: Duplicate tags are prevented
4. **Clear input**: Input clears after adding tag

### Services Not Selecting

If services aren't selecting:
1. **Click checkbox**: Services are selected by clicking checkboxes
2. **Multiple selection**: Multiple services can be selected
3. **Check scroll**: Scroll if many services are available

---

*Last updated: December 2024*`,

  'clients/managing-data': `# Managing Client Data

Learn how to view, edit, organize, and manage your client information in FiverFlow. This guide covers all aspects of managing client data after creation.

## Viewing Client Details

### Opening Client View

1. **Click on Client Card**: Click anywhere on a client card in card view
2. **Click on Client Row**: Click on a client row in list view
3. **Click View Button**: Click the eye icon (View button) on a client

### Client View Modal

The client view modal displays comprehensive client information organized into sections:

#### Identity Section

- **Full Name**: Client's full name
- **Client Type**: Type of client (individual, company, etc.)
- **Company Name**: Company or organization name
- **Platform**: Where you found/met the client

#### Contact Section

- **Primary Email**: Main email address (copyable)
- **Secondary Email**: Additional email address (copyable)
- **Phone**: Primary phone number (copyable)
- **WhatsApp**: WhatsApp number (copyable)
- **Preferred Contact Method**: Preferred way to contact client

#### Location Section

- **Country**: Client's country
- **City**: Client's city
- **Timezone**: Client's timezone
- **Preferred Language**: Client's preferred language

#### Business Section

- **Industry**: Client's industry sector
- **Services Needed**: Types of services they require (comma-separated list)
- **Estimated Budget**: Expected project budget range
- **Collaboration Frequency**: How often you work together

#### Commercial Section

- **Acquisition Source**: Where the client came from
- **Client Status**: Current status (Prospect, Active, Inactive, Completed)
- **Priority Level**: Low, Medium, or High
- **Payment Terms**: Agreed payment structure

#### Notes & Observations Section

- **Availability Notes**: Notes about client availability
- **Important Notes**: Key information to remember

#### Follow-up & Actions Section

- **Next Action**: Planned next action
- **Next Action Date**: Date for next action

#### Tags & Categories Section

- **Tags**: All tags displayed as badges
- **Tag Display**: Tags shown as colored badges
- **No Tags**: Shows "No tags" if no tags are set

### Client View Features

- **Copyable Fields**: Email and phone numbers can be copied with one click
- **Edit Button**: Quick access to edit client
- **Close Button**: Close the modal
- **Responsive Design**: Adapts to screen size
- **Scrollable Content**: Long content scrolls within modal
- **Loading State**: Shows loading indicator while fetching data

### Copying Client Information

1. **Hover over Field**: Hover over copyable fields (email, phone)
2. **Click Copy Icon**: Copy icon appears on hover
3. **Click to Copy**: Click copy icon to copy to clipboard
4. **Confirmation**: Field is copied to clipboard

## Editing Client Information

### Opening Edit Form

1. **From Client View**: Click "Edit" button in client view modal
2. **From Client List**: Click Edit button (pencil icon) on client card/list
3. **Edit Form Opens**: Client form opens with current data pre-filled

### Edit Form

The edit form is the same as the creation form with 3 steps:

#### Step 1: Basic Information

- **Full Name**: Update client name
- **Platform**: Change platform
- **Company Name**: Update company name

#### Step 2: Contact Information

- **Email**: Update email address
- **Phone**: Update phone number
- **WhatsApp**: Update WhatsApp number

#### Step 3: Additional Details

- **Industry**: Change industry
- **Services Needed**: Update services (multi-select)
- **Client Status**: Update status
- **Priority Level**: Change priority
- **Payment Terms**: Update payment terms
- **Important Notes**: Update notes
- **Tags**: Add or remove tags

### Updating Client Data

1. **Open Edit Form**: Click Edit on client
2. **Navigate Steps**: Use Next/Previous to navigate steps
3. **Update Fields**: Change any fields you want to update
4. **Save Changes**: Click "Update" to save
5. **Success**: Client is updated and list refreshes

### Edit Form Features

- **Pre-filled Data**: All existing client data is loaded
- **Same Validation**: Same validation rules as creation
- **Update Button**: Button shows "Update" instead of "Create"
- **All Fields Editable**: Can update any field
- **Cancel Option**: Can cancel without saving

## Deleting Clients

### Deleting a Client

1. **Click Delete Button**: Click trash icon on client card/list
2. **Confirmation Dialog**: Confirmation dialog appears
3. **Confirm Deletion**: Click "OK" to confirm or "Cancel" to abort
4. **Client Deleted**: Client is permanently removed

### Deletion Confirmation

- **Confirmation Required**: Deletion requires confirmation
- **Client Name Display**: Confirmation shows client name
- **Warning Message**: Warns that deletion is permanent
- **Cannot Undo**: Deletion cannot be undone

### Deletion Effects

- **Client Removed**: Client is removed from database
- **Associated Data**: Associated orders and invoices may be affected
- **List Refresh**: Client list refreshes after deletion
- **Success Notification**: Toast notification confirms deletion

**Warning**: Deleting a client is permanent and cannot be undone. Associated orders and invoices may be affected.

## Organizing Clients

### Using Status

**Status Options**:
- **Prospect**: Potential client, not yet active
- **Active**: Currently working with this client
- **Inactive**: Past client, not currently active
- **Project Completed**: Project finished, relationship may continue

**Best Practices**:
- Keep status updated to track client lifecycle
- Use status for filtering and organization
- Update status as relationships evolve

### Using Priority Levels

**Priority Options**:
- **Low**: Standard priority client
- **Medium**: Moderate priority client (default)
- **High**: High-priority, high-value client

**Best Practices**:
- Mark high-value clients as high priority
- Use priority for client segmentation
- Focus on high-priority clients for business growth

### Using Tags

**Tag Features**:
- **Custom Tags**: Create custom tags for organization
- **Multiple Tags**: Clients can have multiple tags
- **Tag Management**: Add or remove tags easily
- **Tag Display**: Tags shown as badges in client view

**Tag Ideas**:
- **VIP**: High-value clients
- **Recurring**: Regular clients
- **One-time**: One-time projects
- **Long-term**: Long-term relationships
- **Urgent**: Urgent projects
- **Preferred**: Preferred clients

**Best Practices**:
- Use consistent tagging system
- Create tags for common categories
- Use tags for filtering and organization
- Keep tags relevant and useful

### Using Filters

**Filter Options**:
- **Status Filter**: Filter by client status
- **Platform Filter**: Filter by platform
- **Country Filter**: Filter by country

**Filter Features**:
- **Multiple Filters**: Apply multiple filters simultaneously
- **Filter + Search**: Combine search with filters
- **Reset Filters**: Clear all filters by selecting "All" options
- **Automatic Pagination**: Filters reset to page 1 when applied

**Best Practices**:
- Use filters to find specific clients
- Combine filters for precise results
- Clear filters when done
- Use filters for client segmentation

## Search Functionality

### Using Search

1. **Enter Search Term**: Type in search bar
2. **Real-time Results**: Results update as you type
3. **Multi-field Search**: Searches name, company, and email
4. **Clear Search**: Click X to clear search

### Search Features

- **Real-time Search**: Instant filtering as you type
- **Case-insensitive**: Finds matches regardless of capitalization
- **Multi-field**: Searches across multiple fields
- **Clear Button**: X button to clear search

### Search Best Practices

- **Use Specific Terms**: Use specific client names or companies
- **Combine with Filters**: Use search with filters for precise results
- **Clear When Done**: Clear search when finished
- **Check Spelling**: Verify spelling if no results

## View Modes

### Card View

**Features**:
- Visual cards with key information
- Quick overview of clients
- Hover effects for interactivity
- Action buttons on hover

**Best For**:
- Browsing clients
- Visual identification
- Quick actions

### List View

**Features**:
- Compact table format
- See more clients at once
- Quick actions in rows
- Data comparison

**Best For**:
- Managing many clients
- Comparing client information
- Quick editing

### Switching Views

- Use view mode toggle in header
- View preference is not saved
- Both views support all actions

## Pagination

### Pagination Features

- **Page Size**: 12 clients per page
- **Navigation**: Previous/Next buttons
- **Page Indicator**: Shows current page and total pages
- **Results Count**: Displays range of clients shown

### Using Pagination

1. **Navigate Pages**: Use Previous/Next buttons
2. **Page Numbers**: See current page and total pages
3. **Results Count**: See how many clients are shown
4. **Automatic Reset**: Pagination resets when filters/search change

## Client Statistics

### Viewing Statistics

The clients page header displays key metrics:

- **Total Clients**: Total number of clients
- **Active Clients**: Number of active clients
- **Prospects**: Number of prospects
- **This Month**: Clients created this month

### Statistics Features

- **Real-time Updates**: Statistics update in real-time
- **Visual Indicators**: Icons and colors for each metric
- **Quick Overview**: Quick overview of client base

## Best Practices

### Data Management

1. **Regular Updates**: Keep client information current
2. **Status Tracking**: Update status as relationships evolve
3. **Note Taking**: Document important details in notes
4. **Tag Organization**: Use tags for organization
5. **Complete Profiles**: Fill in all relevant fields

### Client Organization

1. **Use Status**: Keep status updated for tracking
2. **Use Priority**: Mark high-value clients
3. **Use Tags**: Create tags for categories
4. **Use Filters**: Filter clients for organization
5. **Use Search**: Search for specific clients

### Information Maintenance

1. **Contact Information**: Keep all contact methods updated
2. **Business Information**: Update industry and services
3. **Status Updates**: Update status regularly
4. **Notes Updates**: Add notes for important information
5. **Tag Updates**: Add or remove tags as needed

## Troubleshooting

### Client Not Loading

If client details won't load:
1. **Check connection**: Ensure internet connection is stable
2. **Try again**: Click the client again
3. **Refresh page**: Reload the page
4. **Check browser console**: Look for error messages

### Edit Not Saving

If edits aren't saving:
1. **Check validation**: Ensure all required fields are filled
2. **Check internet connection**: Ensure you're connected
3. **Try again**: Retry saving
4. **Check error messages**: Look for error toast notifications

### Delete Not Working

If delete isn't working:
1. **Check confirmation**: Ensure you confirmed deletion
2. **Check permissions**: Ensure you have permission to delete
3. **Try again**: Retry deletion
4. **Check error messages**: Look for error messages

### Search Not Finding Clients

If search isn't finding clients:
1. **Check spelling**: Verify the search term
2. **Clear filters**: Reset all filters
3. **Try different terms**: Use different search terms
4. **Refresh page**: Reload the page

---

*Last updated: December 2024*`,

  'clients/exporting-clients': `# Exporting Clients

Client data export functionality allows you to export your client information for backup, analysis, or integration with other systems.

## Coming Soon

Client data export is currently in development and will be available in a future update. This feature will enable you to export your client data in various formats for backup, analysis, or integration with other systems.

## Planned Features

### Export Formats

The export feature will support multiple formats:

- **CSV (Comma-Separated Values)**: Standard spreadsheet format
- **Excel (XLSX)**: Microsoft Excel format
- **JSON**: JavaScript Object Notation for developers
- **PDF**: Portable Document Format for reports

### Export Options

#### Full Export

- **All Clients**: Export all clients in your account
- **All Fields**: Export all client information fields
- **Complete Data**: Includes all client data and metadata

#### Filtered Export

- **Filtered Clients**: Export clients matching specific filters
- **Status Filter**: Export clients by status
- **Platform Filter**: Export clients by platform
- **Country Filter**: Export clients by country
- **Custom Filters**: Export clients matching custom criteria

#### Selected Export

- **Selected Clients**: Export only selected clients
- **Multiple Selection**: Select multiple clients for export
- **Bulk Export**: Export multiple clients at once

### Export Data Fields

The export will include the following client data fields:

#### Identity Information

- Client ID
- Full Name
- Company Name
- Client Type
- Platform

#### Contact Information

- Primary Email
- Secondary Email
- Phone
- WhatsApp
- Preferred Contact Method

#### Location Information

- Country
- City
- Timezone
- Preferred Language

#### Business Information

- Industry
- Services Needed
- Budget Range
- Collaboration Frequency

#### Commercial Information

- Acquisition Source
- Client Status
- Priority Level
- Payment Terms

#### Notes & Observations

- Availability Notes
- Important Notes
- Next Action
- Next Action Date

#### Metadata

- Tags
- Created Date
- Updated Date
- Order Count
- Total Revenue

### Export Features

#### Scheduled Exports

- **Automatic Exports**: Schedule automatic exports
- **Recurring Exports**: Set up recurring export schedules
- **Email Delivery**: Receive exports via email
- **Cloud Storage**: Save exports to cloud storage

#### Export History

- **Export Log**: Track all export operations
- **Export History**: View past exports
- **Download Previous Exports**: Download previous export files
- **Export Statistics**: View export statistics

#### Export Settings

- **Default Format**: Set default export format
- **Field Selection**: Choose which fields to export
- **Export Location**: Set default export location
- **Export Preferences**: Customize export preferences

## Use Cases

### Backup

- **Data Backup**: Export clients for backup purposes
- **Data Preservation**: Preserve client data outside the system
- **Disaster Recovery**: Recover data in case of system issues
- **Data Migration**: Migrate data to other systems

### Analysis

- **Data Analysis**: Analyze client data in external tools
- **Reporting**: Create reports from client data
- **Business Intelligence**: Use client data for business insights
- **Data Visualization**: Visualize client data in external tools

### Integration

- **System Integration**: Integrate client data with other systems
- **CRM Integration**: Import client data into other CRM systems
- **Accounting Integration**: Import client data into accounting systems
- **Marketing Integration**: Import client data into marketing tools

### Compliance

- **Data Compliance**: Export data for compliance requirements
- **Data Retention**: Retain client data for legal purposes
- **Data Auditing**: Audit client data for compliance
- **Data Reporting**: Report client data for regulatory requirements

## Benefits

### Data Portability

- **Export Data**: Export your client data anytime
- **Data Ownership**: Maintain ownership of your data
- **Data Control**: Control how your data is used
- **Data Freedom**: Freedom to use data in other systems

### Data Analysis

- **External Analysis**: Analyze data in external tools
- **Advanced Analytics**: Use advanced analytics tools
- **Data Visualization**: Visualize data in external tools
- **Business Insights**: Gain business insights from data

### Data Backup

- **Data Security**: Backup your client data
- **Data Preservation**: Preserve data outside the system
- **Disaster Recovery**: Recover data in case of issues
- **Data Safety**: Ensure data safety and security

## Timeline

Client data export is planned for a future release. The exact timeline will be announced once development begins.

## Feedback

We welcome feedback and suggestions for the export feature. Please share your thoughts and requirements to help us build the best export functionality.

## FAQs

### When will export be available?

Export functionality is planned for a future release. We'll announce the exact timeline once development begins.

### What formats will be supported?

We plan to support CSV, Excel, JSON, and PDF formats.

### Can I export filtered clients?

Yes, you'll be able to export clients matching specific filters.

### Can I schedule automatic exports?

Yes, scheduled exports are planned for the export feature.

### Will exports include all client data?

Yes, exports will include all client information and metadata.

### Can I customize export fields?

Yes, you'll be able to choose which fields to export.

### Will exports be secure?

Yes, exports will be secure and encrypted.

### Can I export to cloud storage?

Yes, cloud storage integration is planned for exports.

---

*Last updated: December 2024*`,
  
  orders: `# Orders

Track and manage all your client orders and projects in one place. Monitor project status, deadlines, budgets, and deliverables with comprehensive order management tools.

## What You'll Find

The Orders page is organized into four main sections:
- **Overview** - Comprehensive order management features and interface
- **Creating Orders** - Step-by-step guide to creating orders
- **Managing Orders** - Editing, viewing, and organizing orders
- **Order Statuses** - Understanding order statuses and workflow

## Documentation Sections

For detailed information about each section, see:
- [Orders Overview](/docs/orders/overview) - Comprehensive order management features
- [Creating Orders](/docs/orders/creating-orders) - Guide to creating orders
- [Managing Orders](/docs/orders/managing-orders) - Editing and organizing orders
- [Order Statuses](/docs/orders/order-statuses) - Understanding order statuses

## Quick Start

### Accessing Orders

1. Navigate to **Orders** from the main navigation menu
2. The Orders page is available on the **Lunch Plan** and above
3. Plan limits: Lunch plan allows up to 10 orders per month, Boost and above have unlimited orders

### Creating Your First Order

1. Click the **"New Order"** button in the top-right corner
2. Fill in the order information form (3-step process)
3. Save the order
4. The order will appear in your orders list

## Key Features

- **Card and List Views**: Switch between visual cards and compact list view
- **Advanced Search**: Real-time search across title and description
- **Smart Filtering**: Filter by status and platform
- **Order Statistics**: View total orders, in progress, completed, and total revenue
- **Status Management**: Quick status updates with dropdown
- **Integration**: Link orders to clients, calendar, tasks, and invoices

## Subscription Limits

### Lunch Plan
- **10 orders per month**
- Full order management features
- All statuses and fields available

### Boost Plan and Above
- **Unlimited orders**
- All features available
- No monthly restrictions

---

*Last updated: December 2024*`,

  'orders/overview': `# Orders Overview

The Orders page helps you manage all client projects and orders throughout their lifecycle. Track project status, budgets, deadlines, and completion dates. Link orders to clients and organize your workflow efficiently.

## Page Layout

The Orders page is organized into multiple sections:

### Header Statistics Section

Four key performance indicator cards displayed in a grid:

1. **Total Orders Card**
   - Displays total number of orders in your system
   - Updates in real-time as you add or remove orders
   - Shows Package icon

2. **In Progress Card**
   - Shows number of orders with "In Progress" status
   - Helps track current active projects
   - Shows Activity icon in blue

3. **Completed Card**
   - Displays number of completed orders
   - Tracks finished projects
   - Shows CheckCircle icon in green

4. **Total Revenue Card**
   - Shows sum of all order budgets
   - Helps track total revenue from orders
   - Shows DollarSign icon in green
   - Formatted with currency symbol

### Search and Actions Bar

- **Search Bar**: Real-time search across order title and description
  - Instant filtering as you type
  - Clear button (X) to reset search
  - Search icon for visual indication
  
- **View Mode Toggle**: Switch between card view and list view
  - Cards: Visual cards with key information
  - List: Compact table format
  
- **New Order Button**: Opens the order creation form

### Filters Section

Two filter dropdowns:

1. **Status Filter**
   - All statuses
   - Pending
   - In Progress
   - Completed

2. **Platform Filter**
   - All platforms
   - Dynamically populated from your orders
   - Common platforms: Fiverr, Upwork, Direct, etc.

### Order Display Area

#### Card View (Default)

- **Visual Cards**: Each order displayed as a card
- **Card Content**:
  - Title: Order/project title
  - Client Name: Associated client name
  - Budget: Formatted budget amount with currency
  - Status Badge: Color-coded status with icon (clickable dropdown)
  - Due Date: Due date with days remaining/overdue indicator
  - Platform: Platform icon and name
  - Action Buttons: View, Edit (visible on hover)

- **Card Layout**: Grid layout (1-4 columns based on screen size)
  - Mobile: 1 column
  - Tablet: 2 columns
  - Desktop: 3 columns
  - Large Desktop: 4 columns

- **Due Date Indicators**:
  - Overdue: Red color, shows days overdue
  - Due Soon (≤3 days): Yellow color, shows days left
  - Normal: Gray color, shows days left

#### List View

- **Table Format**: Compact table display
- **Columns**:
  - Order: Title and platform
  - Client: Client name
  - Budget: Budget amount
  - Status: Status badge with dropdown
  - Due Date: Due date with indicator
  - Actions: View, Edit buttons

- **Table Features**:
  - Hover effects on rows
  - Quick action buttons
  - Responsive design
  - Status dropdown in status column

### Pagination

When you have more than 12 orders:
- **Page Size**: 12 orders per page
- **Navigation**: Previous/Next buttons
- **Page Indicator**: Shows current page and total pages
- **Results Count**: Displays range of orders shown (e.g., "Showing 1 to 12 of 50 orders")

## Order Information Structure

Each order can store comprehensive project details organized into categories:

### Basic Information

- **Title**: Order/project title (required)
- **Description**: Detailed project description
- **Client**: Associated client (required)
  - Selected from your existing clients
  - Must have at least one client to create orders
- **Platform**: Where the order came from
  - Inherited from client or can be set separately
  - Common platforms: Fiverr, Upwork, Direct, etc.
- **Status**: Current order status
  - Options: Pending, In Progress, Completed, On Hold, Cancelled

### Financial Details

- **Budget**: Project budget amount
  - Numeric value
  - Optional field
- **Currency**: Currency for the budget
  - Options: USD, EUR, GBP, CAD, AUD, and many more
  - Default: USD
  - Used for formatting budget display

### Timeline

- **Start Date**: When the project started
  - Date picker
  - Optional field
- **Due Date**: Project deadline
  - Date picker
  - Optional field
  - Used for deadline tracking and calendar display
- **Completed Date**: When the project was finished
  - Date picker
  - Optional field
  - Typically set when status changes to "Completed"

### Additional Details (in Order Detail Modal)

- **Priority Level**: Project priority (Low, Medium, High)
- **Estimated Hours**: Time estimate for the project
- **Hourly Rate**: Rate for hourly projects
- **Payment Status**: Payment information
- **Notes**: Additional project notes
- **Tags**: Custom tags for organization
- **Revision Count**: Number of revisions
- **Client Feedback**: Feedback from client

## Order View Modal

When you click on an order, a detailed view modal opens showing:

### Modal Header

- **Order Icon**: FileText icon in colored circle
- **Order Title**: Large display of order title
- **Creation Date**: When order was created
- **Edit Button**: Quick access to edit order (when not editing)
- **Save/Cancel Buttons**: When in edit mode
- **Close Button**: Close the modal

### Modal Sections

1. **Order Details Section** (gradient background)
   - Project title (editable in edit mode)
   - Project type
   - Status (editable dropdown in edit mode)
   - Priority level
   - Description (editable textarea in edit mode)

2. **Client Information Section**
   - Client name (copyable)
   - Platform (displayed as badge)

3. **Finance & Budget Section** (gradient background)
   - Total budget (editable in edit mode)
   - Payment status
   - Estimated hours
   - Hourly rate
   - Calculated total (hours × rate)

4. **Schedule & Deadlines Section**
   - Start date (editable date picker in edit mode)
   - Due date (editable date picker in edit mode)
   - Completion date (editable date picker in edit mode)
   - Number of revisions

5. **Notes & Feedback Section**
   - Internal notes (editable textarea in edit mode)
   - Client feedback

6. **Tags & Categories Section**
   - All tags displayed as badges (editable in edit mode)

### Modal Features

- **Edit Mode**: Click Edit button to enter edit mode
- **Inline Editing**: Edit fields directly in the modal
- **Save Changes**: Save button to persist changes
- **Cancel Editing**: Cancel button to discard changes
- **Copyable Fields**: Client name can be copied
- **Responsive Design**: Adapts to screen size
- **Scrollable Content**: Long content scrolls within modal

## Order Actions

For each order, you can perform several actions:

### View Order

1. Click on an order card or list item
2. Order view modal opens
3. View all order information
4. Access edit functionality

### Edit Order

1. Click **Edit** button on order card/list (opens form modal)
2. Or click **Edit** in order view modal (inline editing)
3. Update any fields
4. Save changes

### Update Status

1. Click **status dropdown** on order card/list
2. Select new status from dropdown
3. Status updates immediately
4. No need to save separately

### Delete Order

**Note**: Delete functionality is not currently available in the UI, but orders can be deleted through the database if needed.

## Search and Filtering

### Search Functionality

- **Real-time Search**: Type to instantly filter orders
- **Multi-field Search**: Searches across:
  - Order title
  - Order description
- **Case-insensitive**: Finds matches regardless of capitalization
- **Instant Results**: Results update as you type
- **Clear Button**: X button to clear search

### Advanced Filtering

- **Status Filter**: Filter by order status
  - All statuses
  - Pending
  - In Progress
  - Completed
  - On Hold (if available)
  - Cancelled (if available)

- **Platform Filter**: Filter by platform
  - All platforms
  - Dynamically populated from your orders
  - Shows only platforms you've used

### Filter Combinations

- **Multiple Filters**: Apply multiple filters simultaneously
- **Filter + Search**: Combine search with filters
- **Reset Filters**: Clear all filters by selecting "All" options
- **Automatic Pagination**: Filters reset to page 1 when applied

## View Modes

### Card View (Default)

**Advantages**:
- Visual and intuitive
- Quick overview of key information
- Easy to scan
- Status dropdown directly on cards
- Due date indicators clearly visible

**Best For**:
- Browsing orders
- Visual identification
- Quick status updates
- Deadline tracking

### List View

**Advantages**:
- Compact display
- See more orders at once
- Table format for data comparison
- Quick actions in rows
- Status dropdown in table

**Best For**:
- Managing many orders
- Comparing order information
- Quick editing
- Data analysis

### Switching Views

- Use the view mode toggle in the header
- View preference is not saved (resets on page reload)
- Both views support all actions (view, edit, status update)

## Integration with Other Features

### Clients Integration

- **Client Linking**: All orders must be linked to a client
- **Client Selection**: Select client from dropdown when creating order
- **Client View**: See all orders for a client in client details
- **Client Revenue**: Calculate revenue per client from orders
- **Order Count**: See total number of orders per client

### Calendar Integration

- **Due Date Display**: Orders with due dates appear on calendar
- **Deadline Tracking**: Never miss a deadline
- **Order Creation**: Create orders from calendar dates
- **Due Date Pre-fill**: Due date pre-filled when creating from calendar

### Workboard Integration

- **Task Linking**: Link tasks to orders
- **Project Tasks**: Organize tasks by order
- **Order Context**: See order details from tasks

### Statistics Integration

- **Revenue Calculation**: Orders contribute to revenue statistics
- **Order Metrics**: Track order completion rates
- **Delivery Time**: Calculate average delivery time from orders
- **Status Distribution**: See order status breakdown
- **Total Revenue**: Sum of all order budgets

### Invoices Integration

- **Order to Invoice**: Create invoices from completed orders
- **Order Reference**: Reference orders in invoices
- **Billing Tracking**: Track which orders have been invoiced

## Subscription Limits

### Lunch Plan

- **10 orders per month**
- Full order management features
- All statuses and fields available
- Warning banner when limit is reached

### Boost Plan and Above

- **Unlimited orders**
- All features available
- No monthly restrictions
- No limit warnings

### Plan Limit Indicators

- **Warning Banner**: Shows when approaching limit on free plan
- **Upgrade Prompt**: Suggests upgrading when limit is reached
- **Limit Check**: Validates before creating new order

## Best Practices

### Order Organization

1. **Use Descriptive Titles**: Make titles clear and specific
2. **Set Due Dates**: Always set deadlines for better planning
3. **Update Status Regularly**: Keep status current
4. **Add Notes**: Document important project details
5. **Link to Clients**: Always associate orders with clients

### Budget Management

1. **Set Budgets**: Always include budget amounts
2. **Use Correct Currency**: Match client's currency
3. **Track Completion**: Mark completed orders to track revenue
4. **Monitor Pending Revenue**: Check pending orders regularly

### Timeline Management

1. **Set Realistic Dates**: Use realistic due dates
2. **Update Completion Dates**: Mark when projects finish
3. **Use Calendar Integration**: Orders appear on calendar by due date
4. **Track Delivery Time**: Monitor time from start to completion

### Status Management

1. **Update Promptly**: Change status as work progresses
2. **Use On Hold**: Pause orders when waiting for client
3. **Complete Orders**: Mark finished orders as completed
4. **Track Cancellations**: Monitor cancelled orders

## Troubleshooting

### Order Not Loading

If orders aren't loading:
1. **Check internet connection**: Ensure you're connected
2. **Refresh page**: Reload the page
3. **Check browser console**: Look for error messages
4. **Verify authentication**: Ensure you're logged in

### Search Not Working

If search isn't finding orders:
1. **Check spelling**: Verify the search term
2. **Clear filters**: Reset all filters
3. **Check view mode**: Try switching between card and list view
4. **Refresh page**: Reload the page

### Filters Not Applying

If filters aren't working:
1. **Select a filter**: Make sure a filter option is selected
2. **Clear and reapply**: Clear filters and select again
3. **Check data**: Ensure orders have the data you're filtering by
4. **Refresh**: Refresh the page

### Status Not Updating

If status won't update:
1. **Try again**: Click the status dropdown again
2. **Refresh page**: Reload the page
3. **Check connection**: Ensure internet connection
4. **Use edit modal**: Edit status through the edit form

### Budget Not Displaying

If budget isn't showing:
1. **Check currency**: Ensure currency is set
2. **Verify amount**: Check that budget amount is entered
3. **Check format**: Ensure valid number format
4. **Refresh**: Refresh the page

---

*Last updated: December 2024`,

  'orders/creating-orders': `# Creating Orders

Learn how to create comprehensive orders in FiverFlow. The order creation process is organized into a 3-step form that guides you through entering all necessary information.

## Accessing the Order Form

### From the Orders Page

1. Navigate to **Orders** from the main navigation menu
2. Click the **"New Order"** button in the top-right corner
3. The order creation form opens in a modal

### From the Calendar

1. Navigate to **Calendar** from the main navigation menu
2. Click on a date in the calendar
3. Select **"Create Order"** from the action modal
4. The order creation form opens with the due date pre-filled

### Plan Requirements

- **Lunch Plan**: Allows up to 10 orders per month
- **Boost Plan and Above**: Unlimited orders
- If you've reached your limit, you'll see a warning and upgrade prompt

## Order Creation Form

The order creation form is organized into **3 steps** with a progress indicator:

### Step 1: Basic Information

**Required Fields**:
- **Order Title**: Project/order title (required)
- **Client**: Associated client (required)

**Optional Fields**:
- **Description**: Detailed project description
- **Status**: Current order status (default: Pending)

**Client Selection**:
- **Client Dropdown**: Select from your existing clients
- **Required**: Must select a client to create an order
- **Client List**: Shows all your clients alphabetically
- **No Clients**: If you have no clients, you'll need to create one first

**Status Options**:
- **Pending**: Newly created order, not yet started (default)
- **In Progress**: Currently being worked on
- **Completed**: Project finished
- **Cancelled**: Order cancelled

**Validation**:
- Title is required and cannot be empty
- Client is required and must be selected
- Form will show error messages if required fields are missing

### Step 2: Budget & Financial Information

**Optional Fields**:
- **Budget Amount**: Project budget amount
- **Currency**: Currency for the budget

**Budget Field**:
- **Number Input**: Enter budget as a number
- **Decimal Support**: Supports decimal values (e.g., 1000.50)
- **Optional**: Budget is not required but recommended
- **Format**: Can enter any numeric value

**Currency Selection**:
- **Currency Dropdown**: Select currency from list
- **Default Currency**: USD (United States Dollar)
- **Supported Currencies**: 
  - USD, EUR, GBP, CAD, AUD, CHF, JPY, CNY, INR, BRL, MXN, ZAR, SEK, NOK, DKK, PLN, CZK, HUF, RUB, TRY, KRW, SGD, HKD, NZD, AED, SAR, ILS, THB, MYR, PHP, IDR, VND
- **Currency Formatting**: Budget is formatted with selected currency symbol

### Step 3: Timeline & Dates

**Optional Fields**:
- **Start Date**: When the project started
- **Due Date**: Project deadline
- **Completed Date**: When the project was finished

**Date Fields**:
- **Date Pickers**: Calendar date picker for each field
- **Start Date**: Optional, when work begins
- **Due Date**: Optional, project deadline (pre-filled if created from calendar)
- **Completed Date**: Optional, when project was completed
- **Date Validation**: Due date should be after start date (if both are set)

**Calendar Integration**:
- **Pre-filled Due Date**: If creating from calendar, due date is pre-filled
- **Date Selection**: Click on date in calendar to pre-fill due date

## Form Navigation

### Progress Indicator

The form displays a progress indicator showing:
- **Step Numbers**: 1, 2, 3
- **Step Labels**: Basic Info, Budget, Timeline
- **Step Icons**: Visual icons for each step (FileText, DollarSign, Calendar)
- **Progress Bar**: Visual progress bar showing completion percentage
- **Current Step**: Highlighted step indicator

### Navigation Buttons

- **Previous Button**: Go back to previous step (disabled on step 1)
- **Next Button**: Advance to next step
- **Cancel Button**: Close form without saving
- **Create/Update Button**: Submit form (only on final step)

### Step Validation

- **Step 1 Validation**: 
  - Title is required
  - Client is required
  - Shows error toast if validation fails

- **Step 2 Validation**: 
  - No required fields
  - Budget validation (if provided, must be a valid number)

- **Step 3 Validation**: 
  - No required fields
  - Date validation (due date after start date)

## Creating an Order

### Step-by-Step Process

1. **Click "New Order"**: Opens the order creation form

2. **Step 1: Basic Information**
   - Enter order title (required)
   - Select client from dropdown (required)
   - Optionally enter description
   - Select status (default: Pending)
   - Click "Next" to continue

3. **Step 2: Budget & Financial Information**
   - Enter budget amount (optional)
   - Select currency (default: USD)
   - Click "Next" to continue or "Previous" to go back

4. **Step 3: Timeline & Dates**
   - Select start date (optional)
   - Select due date (optional, pre-filled if from calendar)
   - Select completed date (optional)
   - Click "Create" to save order

5. **Success**: Order is created and appears in your orders list

### Form Submission

When you click "Create":
- Form validates all required fields
- Checks plan limits (if on free plan)
- Shows loading state
- Creates order in database
- Shows success toast notification
- Closes form modal
- Refreshes orders list
- New order appears in list

## Editing Existing Orders

### Opening Edit Form

1. Click **Edit** button on an order card/list item
2. Order form opens with current data pre-filled
3. All steps show existing data

### Edit Form Features

- **Pre-filled Data**: All existing order data is loaded
- **Same 3 Steps**: Same form structure as creation
- **Update Button**: Button shows "Update" instead of "Create"
- **All Fields Editable**: Can update any field
- **Validation**: Same validation rules as creation

### Updating an Order

1. **Open Edit Form**: Click Edit on order
2. **Navigate Steps**: Use Next/Previous to navigate steps
3. **Update Fields**: Change any fields you want to update
4. **Save Changes**: Click "Update" to save
5. **Success**: Order is updated and list refreshes

## Form Field Details

### Text Fields

- **Order Title**: Single-line text input (required)
- **Description**: Multi-line textarea (optional)

### Select Fields

- **Client**: Dropdown with client options (required)
- **Status**: Dropdown with status options
- **Currency**: Dropdown with currency options

### Number Fields

- **Budget Amount**: Number input with decimal support

### Date Fields

- **Start Date**: Date picker
- **Due Date**: Date picker (pre-filled from calendar)
- **Completed Date**: Date picker

## Validation and Error Handling

### Required Field Validation

- **Title**: Must not be empty
- **Client**: Must be selected
- **Error Messages**: Toast notifications show validation errors
- **Form Blocking**: Cannot proceed to next step if validation fails

### Plan Limit Validation

- **Limit Check**: Checks if you've reached order limit
- **Free Plan**: Maximum 10 orders per month
- **Upgrade Prompt**: Shows upgrade suggestion if limit reached
- **Blocking**: Prevents order creation if limit reached

### Error Messages

- **Toast Notifications**: Error messages appear as toast notifications
- **Clear Messages**: Error messages are clear and actionable
- **Validation Feedback**: Immediate feedback on validation errors

## Best Practices

### Completing Order Forms

1. **Fill Required Fields**: Always fill title and select client
2. **Add Description**: Include detailed project description
3. **Set Budget**: Add budget amount for revenue tracking
4. **Select Currency**: Match client's currency
5. **Set Due Date**: Always set deadlines for better planning
6. **Set Status**: Keep status current and accurate

### Order Information Tips

1. **Descriptive Titles**: Use clear, specific order titles
2. **Client Selection**: Always select the correct client
3. **Complete Descriptions**: Document project requirements
4. **Budget Tracking**: Include budget for revenue calculations
5. **Currency Matching**: Match client's preferred currency
6. **Due Date Setting**: Set realistic due dates
7. **Status Management**: Set appropriate initial status

### Efficient Order Creation

1. **Quick Creation**: Create order with minimal info, add details later
2. **Complete Information**: Fill all fields at once for completeness
3. **Template Approach**: Use consistent information structure
4. **Calendar Integration**: Use calendar to pre-fill due dates

## Troubleshooting

### Order Not Saving

If an order isn't saving:
1. **Check required fields**: Title and client are required
2. **Verify plan limits**: Ensure you haven't exceeded order limit
3. **Check client selection**: Must select a valid client
4. **Check internet connection**: Ensure you're connected
5. **Try refreshing**: Refresh the page and try again
6. **Check error messages**: Look for error toast notifications

### Form Not Submitting

If form won't submit:
1. **Check validation**: Ensure all required fields are filled
2. **Check plan limits**: Verify you haven't reached order limit
3. **Check loading state**: Wait for previous operation to complete
4. **Try again**: Retry submission

### Client Not Available

If client dropdown is empty:
1. **Create Client First**: You must have at least one client
2. **Check Client Page**: Verify clients exist in Clients page
3. **Refresh Page**: Reload the page to refresh client list
4. **Check Permissions**: Ensure you have access to clients

### Date Not Pre-filling

If due date isn't pre-filled from calendar:
1. **Check Calendar Selection**: Ensure you selected a date
2. **Check Form Opening**: Verify form opened from calendar
3. **Manual Entry**: Manually enter due date if needed

---

*Last updated: December 2024`,

  'orders/managing-orders': `# Managing Orders

Learn how to view, edit, organize, and manage your orders in FiverFlow. This guide covers all aspects of managing orders after creation.

## Viewing Order Details

### Opening Order View

1. **Click on Order Card**: Click anywhere on an order card in card view
2. **Click on Order Row**: Click on an order row in list view
3. **Click View Button**: Click the eye icon (View button) on an order

### Order View Modal

The order view modal displays comprehensive order information organized into sections:

#### Order Details Section

- **Project Title**: Order/project title (editable in edit mode)
- **Project Type**: Type of project
- **Status**: Current status with badge (editable dropdown in edit mode)
- **Priority Level**: Project priority (Low, Medium, High)
- **Description**: Detailed project description (editable textarea in edit mode)

#### Client Information Section

- **Client Name**: Associated client name (copyable)
- **Platform**: Platform badge (Fiverr, Upwork, Direct, etc.)

#### Finance & Budget Section

- **Total Budget**: Budget amount formatted with currency (editable in edit mode)
- **Payment Status**: Payment information
- **Estimated Hours**: Time estimate for the project
- **Hourly Rate**: Rate for hourly projects
- **Calculated Total**: Calculated value (estimated hours × hourly rate)

#### Schedule & Deadlines Section

- **Start Date**: When project started (editable date picker in edit mode)
- **Due Date**: Project deadline (editable date picker in edit mode)
- **Completion Date**: When project was completed (editable date picker in edit mode)
- **Number of Revisions**: Revision count

#### Notes & Feedback Section

- **Internal Notes**: Project notes (editable textarea in edit mode)
- **Client Feedback**: Feedback from client

#### Tags & Categories Section

- **Tags**: All tags displayed as badges (editable in edit mode)
- **Tag Input**: Comma-separated tag input in edit mode

### Order View Features

- **Edit Mode**: Click Edit button to enter edit mode
- **Inline Editing**: Edit fields directly in the modal
- **Save Changes**: Save button to persist changes
- **Cancel Editing**: Cancel button to discard changes
- **Copyable Fields**: Client name can be copied
- **Responsive Design**: Adapts to screen size
- **Scrollable Content**: Long content scrolls within modal
- **Loading State**: Shows loading indicator while fetching data

### Editing in View Modal

1. **Click Edit Button**: Click Edit button in modal header
2. **Enter Edit Mode**: Modal switches to edit mode
3. **Edit Fields**: Update any editable fields
4. **Save Changes**: Click Save button to save
5. **Cancel Changes**: Click Cancel to discard changes

## Editing Order Information

### Opening Edit Form

1. **From Order View**: Click "Edit" button in order view modal (inline editing)
2. **From Order List**: Click Edit button (pencil icon) on order card/list (opens form modal)
3. **Edit Form Opens**: Order form opens with current data pre-filled

### Edit Form

The edit form is the same as the creation form with 3 steps:

#### Step 1: Basic Information

- **Order Title**: Update order title
- **Client**: Change client (if needed)
- **Description**: Update description
- **Status**: Update status

#### Step 2: Budget & Financial Information

- **Budget Amount**: Update budget amount
- **Currency**: Change currency

#### Step 3: Timeline & Dates

- **Start Date**: Update start date
- **Due Date**: Update due date
- **Completed Date**: Update completion date

### Updating Order Data

1. **Open Edit Form**: Click Edit on order
2. **Navigate Steps**: Use Next/Previous to navigate steps
3. **Update Fields**: Change any fields you want to update
4. **Save Changes**: Click "Update" to save
5. **Success**: Order is updated and list refreshes

### Edit Form Features

- **Pre-filled Data**: All existing order data is loaded
- **Same Validation**: Same validation rules as creation
- **Update Button**: Button shows "Update" instead of "Create"
- **All Fields Editable**: Can update any field
- **Cancel Option**: Can cancel without saving

## Updating Order Status

### Quick Status Update

1. **Click Status Dropdown**: Click status badge on order card/list
2. **Select New Status**: Choose new status from dropdown
3. **Status Updates**: Status updates immediately
4. **No Save Required**: Changes are saved automatically

### Status Dropdown

Available in both card and list views:
- **Pending**: Newly created order, not yet started
- **In Progress**: Currently being worked on
- **Completed**: Project finished
- **On Hold**: Temporarily paused
- **Cancelled**: Order cancelled

### Status Update Features

- **Instant Update**: Status updates immediately
- **Visual Feedback**: Status badge updates with new color
- **No Confirmation**: Changes are applied immediately
- **Error Handling**: Shows error if update fails

## Organizing Orders

### Using Status

**Status Options**:
- **Pending**: New order, not yet started
- **In Progress**: Currently being worked on
- **Completed**: Project finished
- **On Hold**: Temporarily paused
- **Cancelled**: Order cancelled

**Best Practices**:
- Keep status updated to track order lifecycle
- Use status for filtering and organization
- Update status as work progresses

### Using Filters

**Filter Options**:
- **Status Filter**: Filter by order status
- **Platform Filter**: Filter by platform

**Filter Features**:
- **Multiple Filters**: Apply multiple filters simultaneously
- **Filter + Search**: Combine search with filters
- **Reset Filters**: Clear all filters by selecting "All" options
- **Automatic Pagination**: Filters reset to page 1 when applied

**Best Practices**:
- Use filters to find specific orders
- Combine filters for precise results
- Clear filters when done
- Use filters for order segmentation

### Using Search

**Search Features**:
- **Real-time Search**: Instant filtering as you type
- **Case-insensitive**: Finds matches regardless of capitalization
- **Multi-field**: Searches across title and description
- **Clear Button**: X button to clear search

**Best Practices**:
- Use specific terms for better results
- Combine search with filters for precise results
- Clear search when finished
- Check spelling if no results

## View Modes

### Card View

**Features**:
- Visual cards with key information
- Quick overview of orders
- Status dropdown on cards
- Due date indicators
- Action buttons on hover

**Best For**:
- Browsing orders
- Visual identification
- Quick status updates
- Deadline tracking

### List View

**Features**:
- Compact table format
- See more orders at once
- Quick actions in rows
- Data comparison
- Status dropdown in table

**Best For**:
- Managing many orders
- Comparing order information
- Quick editing
- Data analysis

### Switching Views

- Use view mode toggle in header
- View preference is not saved
- Both views support all actions

## Pagination

### Pagination Features

- **Page Size**: 12 orders per page
- **Navigation**: Previous/Next buttons
- **Page Indicator**: Shows current page and total pages
- **Results Count**: Displays range of orders shown

### Using Pagination

1. **Navigate Pages**: Use Previous/Next buttons
2. **Page Numbers**: See current page and total pages
3. **Results Count**: See how many orders are shown
4. **Automatic Reset**: Pagination resets when filters/search change

## Order Statistics

### Viewing Statistics

The orders page header displays key metrics:

- **Total Orders**: Total number of orders
- **In Progress**: Number of in progress orders
- **Completed**: Number of completed orders
- **Total Revenue**: Sum of all order budgets

### Statistics Features

- **Real-time Updates**: Statistics update in real-time
- **Visual Indicators**: Icons and colors for each metric
- **Quick Overview**: Quick overview of order base

## Best Practices

### Data Management

1. **Regular Updates**: Keep order information current
2. **Status Tracking**: Update status as work progresses
3. **Note Taking**: Document important details in notes
4. **Complete Profiles**: Fill in all relevant fields

### Order Organization

1. **Use Status**: Keep status updated for tracking
2. **Use Filters**: Filter orders for organization
3. **Use Search**: Search for specific orders
4. **Set Due Dates**: Always set deadlines
5. **Track Completion**: Mark completed orders promptly

### Information Maintenance

1. **Update Descriptions**: Keep descriptions current
2. **Update Budgets**: Update budget if it changes
3. **Update Dates**: Keep dates current
4. **Add Notes**: Add notes for important information
5. **Update Status**: Update status regularly

## Troubleshooting

### Order Not Loading

If order details won't load:
1. **Check connection**: Ensure internet connection is stable
2. **Try again**: Click the order again
3. **Refresh page**: Reload the page
4. **Check browser console**: Look for error messages

### Edit Not Saving

If edits aren't saving:
1. **Check validation**: Ensure all required fields are filled
2. **Check internet connection**: Ensure you're connected
3. **Try again**: Retry saving
4. **Check error messages**: Look for error toast notifications

### Status Not Updating

If status won't update:
1. **Try again**: Click the status dropdown again
2. **Refresh page**: Reload the page
3. **Check connection**: Ensure internet connection
4. **Use edit form**: Edit status through the edit form

### Search Not Finding Orders

If search isn't finding orders:
1. **Check spelling**: Verify the search term
2. **Clear filters**: Reset all filters
3. **Try different terms**: Use different search terms
4. **Refresh page**: Reload the page

---

*Last updated: December 2024`,

  'orders/order-statuses': `# Order Statuses

Understand the different order statuses in FiverFlow and how to use them effectively to track your projects and manage your workflow.

## Order Status Overview

Order statuses help you track the progress of your projects from creation to completion. Each status has a specific meaning and is represented by a color-coded badge for easy visual identification.

## Available Statuses

### Pending

**Description**: Newly created order that has not yet been started.

**When to Use**:
- When you first create an order
- When an order is waiting to be started
- When an order is in the planning phase

**Color**: Yellow/Amber
**Icon**: Clock icon

**Status Flow**:
- **From**: Initial status for new orders
- **To**: In Progress (when work begins)
- **To**: Cancelled (if order is cancelled)

### In Progress

**Description**: Order that is currently being worked on.

**When to Use**:
- When you start working on an order
- When an order is actively being developed
- When an order is in the production phase

**Color**: Blue
**Icon**: Activity icon

**Status Flow**:
- **From**: Pending (when work begins)
- **From**: On Hold (when work resumes)
- **To**: Completed (when work is finished)
- **To**: On Hold (if work is paused)
- **To**: Cancelled (if order is cancelled)

### Completed

**Description**: Order that has been finished and delivered.

**When to Use**:
- When an order is fully completed
- When deliverables have been sent to client
- When an order is ready for invoicing

**Color**: Green
**Icon**: CheckCircle icon

**Status Flow**:
- **From**: In Progress (when work is finished)
- **To**: (Final status, typically no further changes)

### On Hold

**Description**: Order that is temporarily paused.

**When to Use**:
- When waiting for client feedback
- When waiting for client approval
- When work is temporarily paused
- When waiting for resources or information

**Color**: Orange
**Icon**: AlertCircle icon

**Status Flow**:
- **From**: In Progress (when work is paused)
- **From**: Pending (if order is put on hold before starting)
- **To**: In Progress (when work resumes)
- **To**: Cancelled (if order is cancelled)

### Cancelled

**Description**: Order that has been cancelled.

**When to Use**:
- When an order is cancelled by client
- When an order is cancelled by you
- When an order is no longer needed
- When an order cannot be completed

**Color**: Red
**Icon**: X icon

**Status Flow**:
- **From**: Any status (when order is cancelled)
- **To**: (Final status, typically no further changes)

## Status Colors and Icons

### Visual Identification

Each status has a unique color and icon for easy visual identification:

- **Pending**: Yellow/Amber background with Clock icon
- **In Progress**: Blue background with Activity icon
- **Completed**: Green background with CheckCircle icon
- **On Hold**: Orange background with AlertCircle icon
- **Cancelled**: Red background with X icon

### Status Badges

Status badges are displayed on:
- Order cards (card view)
- Order table rows (list view)
- Order detail modal
- Status dropdown menus

## Status Management

### Updating Status

#### Quick Status Update

1. **Click Status Badge**: Click on status badge on order card/list
2. **Select New Status**: Choose new status from dropdown
3. **Status Updates**: Status updates immediately
4. **No Save Required**: Changes are saved automatically

#### Status Dropdown

- **Available in**: Card view and list view
- **Location**: On status badge
- **Options**: All available statuses
- **Current Status**: Highlighted in dropdown
- **Instant Update**: Changes apply immediately

#### Status Update in Edit Form

1. **Open Edit Form**: Click Edit on order
2. **Navigate to Step 1**: Go to Basic Information step
3. **Select Status**: Choose new status from dropdown
4. **Save Changes**: Click Update to save

### Status Workflow

#### Typical Workflow

1. **Create Order**: Status starts as "Pending"
2. **Start Work**: Change status to "In Progress"
3. **Pause Work** (optional): Change status to "On Hold" if needed
4. **Resume Work**: Change status back to "In Progress"
5. **Complete Work**: Change status to "Completed"

#### Status Transitions

**Recommended Flow**:
- **Pending** → **In Progress** → **Completed**
- **Pending** → **In Progress** → **On Hold** → **In Progress** → **Completed**
- **Any Status** → **Cancelled** (if order is cancelled)

**Flexible Transitions**:
- You can change status to any available status at any time
- No restrictions on status transitions
- Status changes are logged for tracking

## Status Best Practices

### Status Management

1. **Update Promptly**: Change status as work progresses
2. **Use Appropriate Status**: Choose status that reflects current state
3. **Keep Status Current**: Don't leave orders in outdated statuses
4. **Use On Hold**: Pause orders when waiting for client

### Workflow Optimization

1. **Consistent Status Use**: Use statuses consistently across orders
2. **Status Tracking**: Track status changes for analytics
3. **Status Filters**: Use status filters to organize orders
4. **Status Reports**: Use status for reporting and analytics

### Client Communication

1. **Status Updates**: Keep clients informed of status changes
2. **Status Visibility**: Status is visible in order details
3. **Status Notifications**: Consider notifying clients of status changes
4. **Status Transparency**: Be transparent about order status

## Status Filtering

### Filtering by Status

**Status Filter**:
- **Location**: Filters section on orders page
- **Options**: All statuses, Pending, In Progress, Completed
- **Functionality**: Filters orders by selected status
- **Reset**: Select "All statuses" to clear filter

**Filter Combinations**:
- **Status + Platform**: Filter by status and platform
- **Status + Search**: Filter by status and search term
- **Multiple Filters**: Apply multiple filters simultaneously

### Status Statistics

**Status Metrics**:
- **Total Orders**: Total number of orders
- **In Progress**: Number of in progress orders
- **Completed**: Number of completed orders
- **Status Distribution**: Breakdown by status

## Status Integration

### Calendar Integration

- **Status Display**: Order status is visible on calendar
- **Status Filtering**: Filter calendar events by status
- **Status Updates**: Status changes reflect on calendar

### Statistics Integration

- **Status Metrics**: Status contributes to statistics
- **Status Reports**: Status used for reporting
- **Status Analytics**: Status used for analytics

### Client Integration

- **Status Visibility**: Status visible in client order list
- **Status Tracking**: Track status per client
- **Status Reports**: Status used for client reports

## Troubleshooting

### Status Not Updating

If status won't update:
1. **Try again**: Click the status dropdown again
2. **Refresh page**: Reload the page
3. **Check connection**: Ensure internet connection
4. **Use edit form**: Edit status through the edit form
5. **Check error messages**: Look for error toast notifications

### Status Not Displaying

If status isn't displaying:
1. **Check status value**: Ensure status is set correctly
2. **Refresh page**: Reload the page
3. **Check status options**: Verify status is valid
4. **Check browser console**: Look for error messages

### Status Dropdown Not Opening

If status dropdown won't open:
1. **Try again**: Click the status badge again
2. **Check browser**: Ensure browser supports dropdowns
3. **Refresh page**: Reload the page
4. **Check permissions**: Ensure you have edit permissions

---

*Last updated: December 2024`,
  
  invoices: `# Invoices

Create, manage, and track professional invoices for your clients. Generate PDF invoices, track payments, manage invoice status, and maintain a complete billing history.

## What You'll Find

The Invoices page is organized into four main sections:
- **Overview** - Comprehensive invoice management features and interface
- **Creating Invoices** - Step-by-step guide to creating invoices
- **Payment Tracking** - Tracking payments and invoice status
- **Exporting Invoices** - PDF generation and export options

## Documentation Sections

For detailed information about each section, see:
- [Invoices Overview](/docs/invoices/overview) - Comprehensive invoice management features
- [Creating Invoices](/docs/invoices/creating-invoices) - Guide to creating invoices
- [Payment Tracking](/docs/invoices/payment-tracking) - Tracking payments and status
- [Exporting Invoices](/docs/invoices/exporting-invoices) - PDF generation and exports

## Quick Start

### Accessing Invoices

1. Navigate to **Invoices** from the main navigation menu
2. The Invoices page is available on the **Scale Plan** and above
3. If you don't have access, you'll see an upgrade prompt

### Creating Your First Invoice

1. Click the **"Create Invoice"** button in the top-right corner
2. Fill in the invoice form (single-page form)
3. Add invoice items with descriptions and prices
4. Save the invoice
5. Send to client or download as PDF

## Key Features

- **Invoice Templates Section**: Quick access to invoice templates management
- **Search and Filtering**: Real-time search by invoice number or client name, filter by status
- **Invoice List**: View all invoices in card (mobile) or table (desktop) format
- **Invoice Actions**: View, edit, send, mark as paid, and delete invoices
- **Payment Tracking**: Track payment status and history
- **PDF Generation**: Download invoices as PDF
- **Integration**: Link invoices to clients, orders, calendar, and statistics

## Subscription Requirements

The Invoices feature is available on:
- **Scale Plan** and above
- Enterprise plans
- Admin accounts

If you don't have access, you'll see an upgrade prompt with details about the Scale plan.

---

*Last updated: December 2024`,

  'invoices/overview': `# Invoices Overview

The Invoices page is your complete billing management system. Create professional invoices, track payment status, send invoices to clients, and maintain detailed payment records.

## Page Layout

The Invoices page includes:

- **Invoice Templates Section**: Quick access to template management
- **Search and Filters Bar**: Real-time search and status filtering  
- **Invoice List**: Card view (mobile) or table view (desktop)
- **Invoice Actions**: View, edit, send, mark as paid, delete

## Invoice Information Structure

Each invoice contains:

- **Basic Information**: Invoice number, client, currency, dates, status
- **Invoice Items**: Line items with descriptions, quantities, prices
- **Financial Calculations**: Subtotal, tax, discount, total (auto-calculated)
- **Additional Information**: Notes, terms, tags, template

## Invoice Statuses

- **Draft**: Gray - Invoice being prepared
- **Sent**: Blue - Invoice sent to client  
- **Paid**: Green - Payment received
- **Overdue**: Red - Payment past due date
- **Canceled**: Gray - Invoice cancelled

## Invoice View Modal

Click on any invoice to view:
- Complete invoice information
- Client details
- Line items with totals
- Payment status and history
- Download PDF option

## Integration

- **Clients**: Link invoices to clients, view billing history
- **Orders**: Import items from orders, reference orders
- **Calendar**: Due dates appear on calendar, overdue tracking
- **Statistics**: Contribute to revenue statistics, payment analytics

---

*Last updated: December 2024`,

  'invoices/creating-invoices': `# Creating Invoices

Learn how to create comprehensive invoices in FiverFlow using a single-page form.

## Invoice Creation Form

The form includes all fields on one page:

### Required Fields

- **Client**: Select client from dropdown (required)
- **Items**: At least one line item with description, quantity, and unit price

### Optional Fields

- **Currency**: Currency for invoice (default: USD)
- **Issue Date**: Invoice creation date (default: today)
- **Due Date**: Payment deadline (default: 7 days from issue date)
- **Tax Rate**: Tax percentage (default: 0)
- **Discount**: Discount amount (default: 0)
- **Notes**: Additional notes for client
- **Template**: Invoice template (optional)

### Invoice Items

Each line item requires:
- **Description**: Item/service description (required)
- **Quantity**: Number of units (required, default: 1)
- **Unit Price**: Price per unit (required, default: 0)
- **Line Total**: Quantity × Unit Price (auto-calculated)

### Import from Orders

- **Import Button**: Import items from completed orders
- **Order Selection**: Select from list of completed orders
- **Automatic Import**: Imports order title as description and budget as unit price
- **Add to Items**: Adds imported items to existing invoice items

### Financial Calculations

Automatic calculations:
1. **Line Totals**: Quantity × Unit Price for each item
2. **Subtotal**: Sum of all line item totals
3. **Tax Amount**: Subtotal × Tax Rate
4. **Total**: Subtotal + Tax - Discount

## Creating an Invoice

1. Click **"Create Invoice"** button in top-right corner
2. Select client from dropdown (required)
3. Set invoice details (dates, currency, tax, discount)
4. Add invoice items:
   - Click "Add Item" to add line items
   - Enter description, quantity, and unit price
   - Or click "Import from Orders" to import from orders
5. Review totals (auto-calculated)
6. Click **"Create Invoice"** to save

## Editing Invoices

1. Click **Edit** button on invoice card/row
2. Form opens with existing data pre-filled
3. Update any fields as needed
4. Modify items (add, remove, or edit)
5. Click **"Update"** to save changes

## Best Practices

- Select correct client before creating invoice
- Use clear, descriptive item descriptions
- Set realistic due dates (default: 7 days)
- Include payment terms in notes field
- Verify totals before saving
- Use templates for professional appearance
- Import from orders to save time

---

*Last updated: December 2024`,

  'invoices/payment-tracking': `# Payment Tracking

Learn how to track payments for your invoices and manage invoice statuses in FiverFlow.

## Payment Status Overview

Invoices can have different statuses that reflect their payment state:

- **Draft**: Invoice created but not yet sent
- **Sent**: Invoice sent to client
- **Paid**: Invoice fully paid
- **Overdue**: Payment past due date
- **Canceled**: Invoice cancelled

## Viewing Payment Status

### Invoice View Modal

When you view an invoice, the modal displays:

- **Total Amount**: Total invoice amount
- **Total Paid**: Sum of all payments received
- **Amount Due**: Remaining amount to be paid
- **Payment Status**: Fully paid, partially paid, or unpaid
- **Payment History**: List of all payments with dates and amounts

### Payment Status Indicators

- **Fully Paid**: Status shows "Paid" with green badge, Total Paid = Total Amount
- **Partially Paid**: Status shows "Sent" or "Overdue", Total Paid < Total Amount
- **Unpaid**: Status shows "Sent" or "Overdue", Total Paid = 0

## Marking Invoices as Paid

### Quick Mark as Paid

1. Click **"Mark as Paid"** button on sent invoice
2. Status changes to "Paid" immediately
3. Payment is recorded automatically
4. Success message is displayed

### Mark as Paid Features

- **Instant Update**: Status updates immediately
- **Automatic Recording**: Payment is recorded automatically
- **Full Payment**: Marks invoice as fully paid
- **Status Change**: Status changes from "Sent" to "Paid"

## Invoice Status Workflow

### Typical Status Flow

1. **Draft** → Create invoice
2. **Sent** → Send to client (click Send button)
3. **Paid** → Mark as paid when payment received
4. **Overdue** → Automatically set when past due date
5. **Canceled** → Cancel invoice if needed

### Status Transitions

- **Draft → Sent**: Click Send button on draft invoice
- **Sent → Paid**: Click Mark as Paid button on sent invoice
- **Sent → Overdue**: Automatic when past due date
- **Any → Canceled**: Cancel invoice through edit form

## Automatic Status Updates

### Overdue Detection

- **Automatic Detection**: Invoices automatically marked overdue when past due date
- **Status Update**: Status changes from "Sent" to "Overdue"
- **Visual Indicator**: Red badge for overdue invoices
- **Calendar Integration**: Overdue invoices appear on calendar

### Payment Status Updates

- **Status Updates**: Status updates when payments are added
- **Automatic Calculation**: Payment status calculated automatically
- **Real-time Updates**: Status updates in real-time

## Payment Tracking Best Practices

### Payment Management

1. **Update Status Promptly**: Mark as paid when payment received
2. **Monitor Overdue**: Check for overdue invoices regularly
3. **Follow Up**: Follow up on overdue invoices
4. **Track Payment History**: Monitor payment history for each invoice

### Status Management

1. **Keep Status Current**: Update status as payments are received
2. **Use Status Filters**: Filter invoices by status for organization
3. **Monitor Overdue**: Track overdue invoices regularly
4. **Use Calendar**: View due dates and overdue invoices on calendar

---

*Last updated: December 2024`,

  'invoices/exporting-invoices': `# Exporting Invoices

Learn how to export and download invoices as PDF files in FiverFlow.

## PDF Generation Overview

FiverFlow allows you to generate professional PDF invoices that can be downloaded, printed, or emailed to clients. PDFs are generated with your invoice template design and include all invoice information.

## Downloading Invoice PDFs

### From Invoice View Modal

1. **Open Invoice**: Click on an invoice to open view modal
2. **Click Download Button**: Click the download icon in modal header
3. **PDF Generation**: PDF is generated with invoice data
4. **Download**: PDF file is downloaded to your device
5. **File Ready**: File is ready for printing or emailing

### PDF Download Features

- **Instant Generation**: PDF generated immediately
- **Template Support**: Uses invoice template if available
- **Professional Layout**: Clean, professional design
- **Complete Information**: Includes all invoice details
- **Print Ready**: Optimized for printing

## PDF Content

The generated PDF includes:

### Header Information
- **Company Information**: Your business details
- **Company Logo**: Your company logo (if available)
- **Company Address**: Your business address

### Invoice Details
- **Invoice Number**: Unique invoice identifier
- **Issue Date**: When invoice was created
- **Due Date**: Payment deadline
- **Invoice Status**: Current status

### Client Information
- **Client Name**: Associated client name
- **Client Email**: Client email address
- **Client Address**: Client address (if available)

### Line Items
- **Item Descriptions**: All invoice item descriptions
- **Quantities**: Quantities for each item
- **Unit Prices**: Unit prices for each item
- **Line Totals**: Line totals for each item

### Financial Summary
- **Subtotal**: Sum of all line items
- **Tax Amount**: Tax amount (if applicable)
- **Discount Amount**: Discount amount (if applicable)
- **Total Amount**: Final invoice total

### Additional Information
- **Notes**: Additional notes for client
- **Terms**: Payment terms and conditions
- **Payment Instructions**: Payment instructions (if any)

## Invoice Templates

### Template-Based PDFs

If an invoice uses a template:

- **Template Design**: PDF uses template design
- **Branding**: Custom branding from template
- **Layout**: Template layout applied to PDF
- **Consistency**: Consistent appearance across invoices

### Default PDFs

If no template is used:

- **Default Design**: PDF uses default design
- **Professional Layout**: Clean, professional layout
- **Complete Information**: All invoice information included
- **Print Ready**: Optimized for printing

## Using PDFs

### Printing Invoices

1. **Download PDF**: Download invoice as PDF
2. **Open PDF**: Open PDF in PDF viewer
3. **Print**: Print PDF using printer
4. **Send to Client**: Mail printed invoice to client

### Emailing Invoices

1. **Download PDF**: Download invoice as PDF
2. **Attach PDF**: Attach PDF to email
3. **Send Email**: Send email to client
4. **Invoice Delivered**: Client receives invoice

### Archiving Invoices

1. **Download PDF**: Download invoice as PDF
2. **Save PDF**: Save PDF to archive
3. **Organize**: Organize PDFs by date or client
4. **Backup**: Keep PDF backups for records

## Best Practices

### PDF Generation

1. **Use Templates**: Use templates for professional appearance
2. **Verify Information**: Check invoice information before generating PDF
3. **Test Printing**: Test PDF printing before sending to clients
4. **Archive PDFs**: Keep PDF backups for records

### Organization

1. **Save PDFs**: Save PDFs for records
2. **Organize by Date**: Organize PDFs by date
3. **Organize by Client**: Organize PDFs by client
4. **Backup PDFs**: Keep PDF backups
5. **Archive Completed**: Archive paid invoice PDFs

## Troubleshooting

### PDF Not Generating

If PDF won't generate:
1. **Check browser**: Ensure browser supports PDF generation
2. **Try again**: Click download button again
3. **Check invoice data**: Ensure invoice has complete information
4. **Check popup blocker**: Allow popups for PDF download
5. **Check connection**: Ensure internet connection is stable

### PDF Not Downloading

If PDF won't download:
1. **Check browser settings**: Ensure downloads are allowed
2. **Check popup blocker**: Allow popups for PDF download
3. **Try different browser**: Try different browser
4. **Check file permissions**: Ensure file permissions allow downloads

### PDF Content Missing

If PDF content is missing:
1. **Check invoice data**: Ensure invoice has complete information
2. **Refresh invoice**: Reload invoice data
3. **Try again**: Try generating PDF again
4. **Check template**: Ensure template is correct

## Future Features

### Planned Export Options

The following export features are planned for future releases:

- **Multiple Formats**: Export to CSV, Excel, and other formats
- **Bulk Export**: Export multiple invoices at once
- **Scheduled Exports**: Schedule automatic exports
- **Custom Export Fields**: Select specific fields for export
- **Email Integration**: Send PDFs directly via email

---

*Last updated: December 2024`,
  
  workboard: `# Workboard

Organize and manage all your tasks in a modern kanban-style workboard. Track task status, priorities, deadlines, and progress. Group tasks by client or view them in a unified list.

## What You'll Find

The Workboard page is organized into four main sections:
- **Overview** - Workboard interface, view modes, and features
- **Creating Boards** - Creating tasks and organizing your workboard
- **Task Management** - Managing tasks, statuses, and priorities
- **Collaboration** - Team collaboration features (coming soon)

## Documentation Sections

For detailed information about each section, see:
- [Workboard Overview](/docs/workboard/overview) - Interface and view modes
- [Creating Boards](/docs/workboard/creating-boards) - Creating and organizing tasks
- [Task Management](/docs/workboard/task-management) - Managing tasks and statuses
- [Collaboration](/docs/workboard/collaboration) - Team collaboration features

## Quick Start

### Accessing the Workboard

1. Navigate to **Tasks** or **Workboard** from the main navigation menu
2. The Workboard is available on the **Boost Plan** and above
3. If you don't have access, you'll see an upgrade prompt

### Creating Your First Task

1. Click the **"Add Task"** button in the top-right corner
2. Fill in the task information
3. Save the task
4. The task will appear in your workboard

## Key Features

- **View Modes**: List View (grid of task cards) and Client View (grouped by client)
- **Task Statistics**: Total Tasks, Completed, In Progress, To Do, Overdue, Today
- **Filtering**: Tab filters (All, Today, Upcoming, Completed) and client/status filters
- **Task Management**: Create, edit, delete tasks with priorities and due dates
- **Task Statuses**: To Do, In Progress, Completed
- **Task Priorities**: Low (green), Medium (orange), High (red)
- **Integration**: Link tasks to orders and clients, calendar integration
- **Task Drawer**: View and edit task details in a side drawer

## Subscription Requirements

The Workboard feature is available on:
- **Boost Plan** and above
- Enterprise plans
- Admin accounts

If you don't have access, you'll see an upgrade prompt with details about the Boost plan.

---

*Last updated: December 2024`,

  'workboard/overview': `# Workboard Overview

The Workboard (also called Tasks) is your central task management hub. It provides a kanban-style interface where you can organize tasks by status, track deadlines, manage priorities, and link tasks to clients and orders. Perfect for managing your daily workflow and project tasks.

## Page Layout

The Workboard page is organized into multiple sections:

### Controls Bar

- **View Mode Toggle**: Switch between List View and Client View
  - **List View**: Grid of task cards organized by status
  - **Client View**: Tasks grouped by client name
- **Client Filter**: Filter tasks by client (in List View)
- **Add Task Button**: Create new tasks

### Task Statistics Section

Displays key metrics that are clickable to filter tasks:

- **Total Tasks**: Total number of tasks (click to reset all filters)
- **Completed**: Number of completed tasks (click to filter)
- **In Progress**: Number of tasks in progress (click to filter)
- **To Do**: Number of tasks to do (click to filter)
- **Overdue**: Number of overdue tasks (click to filter)
- **Today**: Number of tasks due today (click to filter)

### Tab Switcher

Quick filter tabs at the top:

- **All**: Show all tasks
- **Today**: Tasks due today
- **Upcoming**: Tasks due in the future
- **Completed**: Only completed tasks

### Tasks Content Area

- **List View**: Grid of task cards (1-3 columns depending on screen size)
- **Client View**: Groups of tasks organized by client name
- **Empty State**: Message when no tasks match filters

## View Modes

### List View (Default)

**Grid Layout**:
- Tasks displayed in a responsive grid
- 1 column on mobile, 2 columns on tablet, 3 columns on desktop
- Tasks organized by status (To Do, In Progress, Completed)
- Visual task cards with key information

**Task Cards Display**:
- Task title
- Priority indicator (color-coded)
- Due date
- Status badge
- Client information (if linked to order)
- Order information (if linked)

**Features**:
- Click task card to open task drawer
- View all tasks in one unified view
- Client filter dropdown available
- Status filter available

### Client View

**Client Groups**:
- Tasks grouped by client name
- Expandable/collapsible client groups
- "No Client" group for tasks without client association
- Client search bar at top

**Client Group Features**:
- Click client name to expand/collapse
- See all tasks for a client together
- Client context for each task
- Search clients by name

**Client Search**:
- Real-time search across client names
- Filter client groups as you type
- Clear button to reset search

## Task Status Columns

Tasks are organized into three main status columns (conceptually):

### To Do

- **New tasks**: Tasks not yet started
- **Pending work**: Tasks waiting to begin
- **Planned tasks**: Tasks scheduled for future
- **Default status**: New tasks start in To Do

### In Progress

- **Active work**: Tasks currently being worked on
- **Ongoing tasks**: Tasks in progress
- **Work in progress**: Tasks being actively completed
- **Status indicator**: Yellow/orange color

### Completed

- **Finished tasks**: Tasks that are done
- **Archived work**: Completed and closed tasks
- **Success indicators**: Visual confirmation of completion
- **Status indicator**: Green color

## Task Information Structure

Each task contains comprehensive information:

### Basic Information

- **Title**: Task title (required)
- **Description**: Detailed task description (optional)
- **Status**: Current task status (To Do, In Progress, Completed)
- **Priority**: Task priority (Low, Medium, High)

### Dates and Time

- **Due Date**: Task deadline (optional)
- **Estimated Hours**: Time estimate for the task (optional)
- **Created Date**: When task was created (automatic)
- **Updated Date**: When task was last updated (automatic)

### Relationships

- **Order**: Link to an order (optional)
- **Client**: Associated client (through order, automatic)
- **Client ID**: Direct client association (optional)

## Task Priorities

Tasks have three priority levels with visual indicators:

### Low Priority

- **Color**: Green
- **Indicator**: Green badge/indicator
- **Use Case**: Not urgent, can be done later

### Medium Priority

- **Color**: Orange/Yellow
- **Indicator**: Orange/yellow badge/indicator
- **Use Case**: Normal priority, standard tasks

### High Priority

- **Color**: Red
- **Indicator**: Red badge/indicator
- **Use Case**: Urgent, important tasks

### Priority Features

- **Visual Indicators**: Color coding on task cards
- **Sorting**: High priority tasks appear first
- **Calendar Display**: Priority shown on calendar
- **Filtering**: Filter tasks by priority

## Task Statistics

The workboard displays key metrics:

### Statistics Cards

- **Total Tasks**: Total number of tasks
- **Completed**: Number of completed tasks
- **In Progress**: Number of tasks in progress
- **To Do**: Number of tasks to do
- **Overdue**: Number of overdue tasks (past due date, not completed)
- **Today**: Number of tasks due today

### Interactive Statistics

- **Click to Filter**: Click on any statistic to filter tasks
- **Visual Feedback**: Active filter highlighted
- **Reset Filter**: Click "Total Tasks" to reset all filters
- **Real-time Updates**: Statistics update as tasks change

## Task Filtering

### Tab Filters

Quick filters at the top:

- **All**: Show all tasks (reset filters)
- **Today**: Tasks due today
- **Upcoming**: Tasks due in the future (not today)
- **Completed**: Only completed tasks

### Additional Filters

- **Status Filter**: Filter by specific status (To Do, In Progress, Completed)
- **Client Filter**: Filter tasks by client (in List View)
- **Overdue Filter**: Show only overdue tasks (via statistics)
- **Date Filter**: Filter by due date (from calendar URL parameter)

### Filter Combinations

- **Multiple Filters**: Combine tab filters with client/status filters
- **Calendar Date**: Filter by date from calendar navigation
- **Reset Filters**: Click "All" tab or "Total Tasks" statistic to reset

## Calendar Integration

Tasks with due dates integrate with the calendar:

### Calendar Navigation

- **Date Parameter**: URL parameter ?date=YYYY-MM-DD filters tasks
- **Calendar Click**: Click date on calendar to filter tasks
- **Date Display**: Shows which date is being filtered
- **Clear Filter**: Remove date parameter to show all tasks

### Calendar Creation

- **Create from Calendar**: Create tasks from calendar dates
- **Pre-filled Date**: Due date pre-filled with selected calendar date
- **Action Parameter**: URL parameter ?action=create opens task creation modal

## Empty States

### No Tasks

When no tasks exist:
- **Icon**: Calendar icon
- **Message**: "No tasks found"
- **Description**: "Get started by creating your first task"
- **Action Button**: "Create Task" button

### No Matching Tasks

When filters don't match any tasks:
- **Icon**: Calendar icon
- **Message**: "No tasks found"
- **Description**: "No tasks match your current filter"
- **Action**: Adjust filters to see tasks

### No Clients (Client View)

When no clients exist in Client View:
- **Icon**: Users icon
- **Message**: "No clients found"
- **Description**: "No clients available. Create a client first."
- **Action**: Create a client to see client groups

## Integration with Other Features

### Orders Integration

- **Task Linking**: Link tasks to orders
- **Order Selection**: Choose from existing orders when creating task
- **Order Context**: See order details from task drawer
- **Project Organization**: Group tasks by project/order
- **Revenue Tracking**: Track tasks related to billable work

### Clients Integration

- **Client Association**: Tasks linked to clients through orders
- **Automatic Linking**: Tasks inherit client from order
- **Client Filtering**: Filter tasks by client
- **Client View**: See all tasks for a client together
- **Client Context**: Understand task context

### Calendar Integration

- **Due Date Display**: Tasks with due dates appear on calendar
- **Calendar Creation**: Create tasks from calendar dates
- **Deadline Tracking**: Never miss a task deadline
- **Calendar Navigation**: Click calendar date to filter tasks
- **Date Filtering**: Filter tasks by calendar date

### Statistics Integration

- **Task Metrics**: Task data contributes to statistics
- **Completion Rates**: Track task completion rates
- **Time Tracking**: Monitor estimated vs. actual hours
- **Productivity Metrics**: Analyze task completion patterns

## Best Practices

### Task Organization

1. **Use Clear Titles**: Make task titles descriptive and specific
2. **Set Priorities**: Mark important tasks as high priority
3. **Set Due Dates**: Always set deadlines for time-sensitive tasks
4. **Add Descriptions**: Include important details in descriptions
5. **Link to Orders**: Connect tasks to projects/orders

### View Mode Usage

1. **List View**: Use for viewing all tasks in one place
2. **Client View**: Use for organizing tasks by client
3. **Switch Views**: Switch between views based on your needs
4. **Use Filters**: Combine view modes with filters for better organization

### Statistics Usage

1. **Monitor Metrics**: Check statistics regularly
2. **Click to Filter**: Use statistics to quickly filter tasks
3. **Track Progress**: Monitor completion rates
4. **Identify Issues**: Check overdue tasks regularly

---

*Last updated: December 2024`,

  'workboard/creating-boards': `# Creating Boards

Learn how to create tasks and organize your workboard in FiverFlow.

## Creating Tasks

### Accessing Task Creation

#### From Workboard Page

1. Navigate to **Tasks** or **Workboard** from main navigation
2. Click **"Add Task"** button in top-right corner
3. Task creation modal opens

#### From Calendar

1. Navigate to **Calendar** from main navigation
2. Click on a date in the calendar
3. Select **"Create Task"** from action modal
4. Task creation modal opens with due date pre-filled

#### From Dashboard

1. Navigate to **Dashboard** from main navigation
2. Click **"Quick Actions"** button
3. Select **"Create Task"**
4. Redirects to workboard with task creation modal open

### Task Creation Form

The task creation form includes:

#### Required Fields

- **Title**: Task title (required)
  - Enter a clear, descriptive task name
  - Maximum length: Determined by database
  - Cannot be empty

#### Optional Fields

- **Description**: Detailed task description (optional)
  - Add additional details about the task
  - Supports multi-line text
  - Can be left empty

- **Priority**: Task priority (default: Medium)
  - **Low**: Green indicator - Not urgent
  - **Medium**: Orange indicator - Normal priority (default)
  - **High**: Red indicator - Urgent

- **Due Date**: Task deadline (optional)
  - Select date from date picker
  - Pre-filled if created from calendar
  - Can be left empty

- **Client**: Associated client (optional)
  - Select client from dropdown
  - Filters orders by selected client
  - Can be left empty

- **Order**: Link to order (optional)
  - Select order from dropdown
  - Filtered by selected client (if client selected)
  - Shows order title
  - Can be left empty

### Creating a Task

#### Step-by-Step Process

1. **Click "Add Task"**: Opens task creation modal

2. **Enter Task Title**:
   - Type task title in title field (required)
   - Make it clear and descriptive

3. **Add Description** (optional):
   - Enter task description
   - Include important details

4. **Set Priority** (optional):
   - Select priority from dropdown
   - Default: Medium
   - Options: Low, Medium, High

5. **Set Due Date** (optional):
   - Click date field
   - Select date from date picker
   - Or leave empty

6. **Select Client** (optional):
   - Choose client from dropdown
   - Filters orders by client
   - Or leave empty

7. **Link to Order** (optional):
   - Select order from dropdown
   - Only shows orders for selected client (if client selected)
   - Or leave empty

8. **Create Task**:
   - Click **"Create Task"** button
   - Task is created and saved
   - Modal closes
   - Task appears in workboard

### Form Validation

**Required Fields**:
- **Title**: Must not be empty
- Validation error shown if title is empty

**Validation Errors**:
- Error toast notification shown for missing title
- Form cannot be submitted until title is filled
- Clear error messages displayed

### Form Submission

When you click "Create Task":
- Form validates required fields
- Shows loading state
- Creates task in database
- Shows success toast notification
- Closes modal
- Refreshes task list
- New task appears in "To Do" status

## Quick Creation from Calendar

### Creating Tasks from Calendar

1. **Navigate to Calendar**: Go to Calendar page
2. **Click on Date**: Click on any date in calendar
3. **Select "Create Task"**: Choose "Create Task" from action modal
4. **Task Modal Opens**: Task creation modal opens automatically
5. **Due Date Pre-filled**: Due date is set to selected calendar date
6. **Complete Form**: Fill in remaining task details
7. **Create Task**: Click "Create Task" to save

### Calendar Integration Features

- **Pre-filled Date**: Due date automatically set to selected date
- **URL Parameter**: ?date=YYYY-MM-DD parameter in URL
- **Action Parameter**: ?action=create parameter opens modal
- **Automatic Cleanup**: URL parameters cleaned after modal opens

## Organizing Your Workboard

### Task Organization Strategies

#### By Status

- **To Do**: New tasks, not yet started
- **In Progress**: Active tasks, currently working on
- **Completed**: Finished tasks, done

#### By Priority

- **High Priority**: Urgent tasks, do first
- **Medium Priority**: Normal tasks, standard workflow
- **Low Priority**: Not urgent, can be done later

#### By Client

- **Client View**: Group tasks by client name
- **Client Filter**: Filter tasks by specific client
- **Client Context**: See all tasks for a client together

#### By Date

- **Due Date**: Organize tasks by deadline
- **Today**: Tasks due today
- **Upcoming**: Tasks due in the future
- **Overdue**: Tasks past due date

### Using View Modes

#### List View

- **Unified View**: See all tasks in one place
- **Status Organization**: Tasks organized by status
- **Client Filter**: Filter by client if needed
- **Best For**: Viewing all tasks, general overview

#### Client View

- **Client Groups**: Tasks grouped by client
- **Client Context**: See tasks for each client
- **Client Search**: Search for specific clients
- **Best For**: Organizing by client, client-focused work

### Using Filters

#### Tab Filters

- **All**: Show all tasks (reset filters)
- **Today**: Tasks due today
- **Upcoming**: Tasks due in the future
- **Completed**: Only completed tasks

#### Additional Filters

- **Status Filter**: Filter by status (To Do, In Progress, Completed)
- **Client Filter**: Filter by client (List View)
- **Overdue Filter**: Show only overdue tasks
- **Date Filter**: Filter by calendar date

### Using Statistics

- **Click Statistics**: Click on statistics to filter tasks
- **Total Tasks**: Reset all filters
- **Completed**: Filter by completed tasks
- **In Progress**: Filter by in progress tasks
- **To Do**: Filter by to do tasks
- **Overdue**: Filter by overdue tasks
- **Today**: Filter by today's tasks

## Best Practices

### Task Creation

1. **Clear Titles**: Use descriptive, specific task titles
2. **Add Descriptions**: Include important details
3. **Set Priorities**: Mark urgent tasks as high priority
4. **Set Due Dates**: Always set deadlines for time-sensitive tasks
5. **Link to Orders**: Connect tasks to projects/orders
6. **Select Clients**: Associate tasks with clients when possible

### Organization

1. **Use View Modes**: Switch between List and Client views
2. **Use Filters**: Combine filters for better organization
3. **Use Statistics**: Click statistics to quickly filter tasks
4. **Group by Client**: Use Client View for client-focused work
5. **Monitor Overdue**: Check overdue tasks regularly

### Workflow

1. **Create Tasks Promptly**: Create tasks as soon as they're identified
2. **Set Realistic Dates**: Use achievable deadlines
3. **Update Status**: Change status as work progresses
4. **Complete Tasks**: Mark finished tasks as completed
5. **Review Regularly**: Review and update tasks regularly

---

*Last updated: December 2024`,

  'workboard/task-management': `# Task Management

Learn how to manage tasks, update statuses, and handle priorities in FiverFlow Workboard.

## Viewing Task Details

### Task Drawer

Click on any task card to open the task drawer:

#### Drawer Header

- **Task Title**: Large display of task title
- **Close Button**: X button to close drawer
- **Task Status**: Current status badge

#### Drawer Content

- **Task Information**: All task details
  - Title and description
  - Status and priority
  - Due date
  - Order information (if linked)
  - Client information (if linked)

- **Edit Mode**: Click "Edit" to enter edit mode
- **Delete Button**: Remove task (with confirmation)

### Task Information Display

#### Basic Information

- **Title**: Task title
- **Description**: Task description (if provided)
- **Status**: Current status (To Do, In Progress, Completed)
- **Priority**: Task priority (Low, Medium, High)

#### Dates

- **Due Date**: Task deadline (if set)
- **Created Date**: When task was created
- **Updated Date**: When task was last updated

#### Relationships

- **Order**: Linked order (if any)
- **Client**: Associated client (if any)
- **Order Details**: Order title and information

## Editing Tasks

### Opening Edit Mode

1. **Click Task**: Click on task card to open drawer
2. **Click Edit**: Click "Edit" button in drawer
3. **Edit Mode**: Drawer switches to edit mode
4. **Edit Fields**: All fields become editable

### Editable Fields

- **Title**: Task title (required)
- **Description**: Task description (optional)
- **Status**: Task status (To Do, In Progress, Completed)
- **Priority**: Task priority (Low, Medium, High)
- **Due Date**: Task deadline (optional)
- **Order**: Link to order (optional)

### Saving Changes

1. **Edit Fields**: Update any fields you want to change
2. **Click Save**: Click "Save" button
3. **Updates Applied**: Changes are saved immediately
4. **Success Message**: Success toast notification shown
5. **Drawer Updates**: Drawer displays updated information

## Updating Task Status

### Status Options

Tasks can have three statuses:

- **To Do**: Task not yet started
- **In Progress**: Task currently being worked on
- **Completed**: Task finished

### Status Update Methods

#### Method 1: Task Drawer

1. **Open Drawer**: Click on task to open drawer
2. **Click Edit**: Click "Edit" button
3. **Select Status**: Choose new status from dropdown
4. **Save**: Click "Save" button
5. **Status Updated**: Status changes immediately

#### Method 2: Status Dropdown (if available)

1. **Open Drawer**: Click on task to open drawer
2. **Status Selector**: Use status selector (if available)
3. **Select Status**: Choose new status
4. **Auto-save**: Status updates automatically (if enabled)

### Status Transitions

- **To Do → In Progress**: Start working on task
- **In Progress → Completed**: Finish the task
- **Completed → In Progress**: Reopen completed task
- **Any → To Do**: Reset task to initial status

## Managing Task Priorities

### Priority Levels

Tasks have three priority levels:

- **Low Priority**: Green indicator - Not urgent
- **Medium Priority**: Orange indicator - Normal priority
- **High Priority**: Red indicator - Urgent

### Updating Priority

1. **Open Drawer**: Click on task to open drawer
2. **Click Edit**: Click "Edit" button
3. **Select Priority**: Choose priority from dropdown
4. **Save**: Click "Save" button
5. **Priority Updated**: Priority changes immediately

### Priority Features

- **Visual Indicators**: Color coding on task cards
- **Sorting**: High priority tasks appear first
- **Filtering**: Filter tasks by priority (via status filter)
- **Calendar Display**: Priority shown on calendar

## Managing Due Dates

### Setting Due Dates

1. **Open Drawer**: Click on task to open drawer
2. **Click Edit**: Click "Edit" button
3. **Select Due Date**: Choose date from date picker
4. **Save**: Click "Save" button
5. **Due Date Set**: Due date is saved

### Updating Due Dates

1. **Open Drawer**: Click on task to open drawer
2. **Click Edit**: Click "Edit" button
3. **Change Due Date**: Select new date from date picker
4. **Save**: Click "Save" button
5. **Due Date Updated**: Due date is updated

### Removing Due Dates

1. **Open Drawer**: Click on task to open drawer
2. **Click Edit**: Click "Edit" button
3. **Clear Date**: Clear date field
4. **Save**: Click "Save" button
5. **Due Date Removed**: Due date is removed

### Due Date Features

- **Calendar Integration**: Tasks with due dates appear on calendar
- **Overdue Detection**: Tasks past due date marked as overdue
- **Today Filter**: Filter tasks due today
- **Upcoming Filter**: Filter tasks due in the future

## Linking Tasks to Orders

### Linking to Orders

1. **Open Drawer**: Click on task to open drawer
2. **Click Edit**: Click "Edit" button
3. **Select Order**: Choose order from dropdown
4. **Save**: Click "Save" button
5. **Task Linked**: Task is linked to order

### Order Association Features

- **Order Context**: See order details from task
- **Client Association**: Task inherits client from order
- **Project Organization**: Group tasks by project/order
- **Revenue Tracking**: Track tasks related to billable work

### Unlinking from Orders

1. **Open Drawer**: Click on task to open drawer
2. **Click Edit**: Click "Edit" button
3. **Clear Order**: Clear order selection
4. **Save**: Click "Save" button
5. **Task Unlinked**: Task is unlinked from order

## Deleting Tasks

### Deleting a Task

1. **Open Drawer**: Click on task to open drawer
2. **Click Delete**: Click "Delete" button
3. **Confirm Deletion**: Confirm deletion in confirmation dialog
4. **Task Deleted**: Task is removed from workboard
5. **Drawer Closes**: Drawer closes automatically

### Deletion Confirmation

- **Confirmation Dialog**: "Are you sure you want to delete this task?"
- **Cancel Option**: Cancel deletion
- **Confirm Option**: Confirm deletion
- **Permanent Action**: Deletion cannot be undone

### Deletion Features

- **Permanent Deletion**: Task is permanently deleted
- **No Recovery**: Deleted tasks cannot be recovered
- **Drawer Closes**: Drawer closes after deletion
- **List Refreshes**: Task list refreshes after deletion

## Task Organization

### Organizing by Status

- **To Do**: New tasks, not yet started
- **In Progress**: Active tasks, currently working on
- **Completed**: Finished tasks, done

### Organizing by Priority

- **High Priority**: Urgent tasks, do first
- **Medium Priority**: Normal tasks, standard workflow
- **Low Priority**: Not urgent, can be done later

### Organizing by Client

- **Client View**: Group tasks by client name
- **Client Filter**: Filter tasks by specific client
- **Client Context**: See all tasks for a client together

### Organizing by Date

- **Due Date**: Organize tasks by deadline
- **Today**: Tasks due today
- **Upcoming**: Tasks due in the future
- **Overdue**: Tasks past due date

## Best Practices

### Task Management

1. **Update Status Regularly**: Change status as work progresses
2. **Set Realistic Dates**: Use achievable deadlines
3. **Update Priorities**: Adjust priorities as needed
4. **Complete Tasks**: Mark finished tasks as completed
5. **Review Regularly**: Review and update tasks regularly

### Status Management

1. **Start Work**: Change status to "In Progress" when starting
2. **Complete Work**: Change status to "Completed" when finished
3. **Reopen if Needed**: Change status back to "In Progress" if needed
4. **Use Status Filters**: Filter tasks by status for organization

### Priority Management

1. **Mark Urgent Tasks**: Use high priority for urgent items
2. **Review Regularly**: Update priorities as needed
3. **Focus on High Priority**: Tackle high priority tasks first
4. **Balance Workload**: Distribute priorities evenly

### Deadline Management

1. **Set Realistic Dates**: Use achievable deadlines
2. **Update Dates**: Adjust dates as needed
3. **Monitor Overdue**: Check overdue tasks regularly
4. **Use Calendar**: View tasks on calendar for planning

---

*Last updated: December 2024`,

  'workboard/collaboration': `# Collaboration

Team collaboration features for the Workboard are planned for future releases.

## Coming Soon

The following collaboration features are planned:

### Team Collaboration

- **Team Members**: Add team members to workboard
- **Task Assignment**: Assign tasks to team members
- **Team View**: View tasks assigned to team members
- **Team Statistics**: Track team productivity and completion rates

### Real-time Collaboration

- **Real-time Updates**: See task updates in real-time
- **Live Editing**: Multiple users can edit tasks simultaneously
- **Activity Feed**: Track task activity and changes
- **Notifications**: Get notified of task updates

### Task Comments

- **Comments**: Add comments to tasks
- **Threaded Discussions**: Have discussions about tasks
- **Mentions**: Mention team members in comments
- **Comment Notifications**: Get notified of new comments

### Task Sharing

- **Share Tasks**: Share tasks with team members
- **Task Permissions**: Control who can view/edit tasks
- **Public Tasks**: Make tasks public to team
- **Private Tasks**: Keep tasks private

### Team Workboards

- **Team Boards**: Create workboards for teams
- **Board Permissions**: Control board access
- **Team Templates**: Use team templates for tasks
- **Team Analytics**: Track team performance

## Planned Features

### Task Assignment

- Assign tasks to specific team members
- Track assigned tasks
- View tasks assigned to you
- Filter tasks by assignee

### Task Comments

- Add comments to tasks
- Reply to comments
- Mention team members
- Get comment notifications

### Task Sharing

- Share tasks with team members
- Control task permissions
- Public and private tasks
- Team task visibility

### Team Analytics

- Track team productivity
- Monitor completion rates
- Analyze team performance
- Generate team reports

## Use Cases

### Team Projects

- Assign tasks to team members
- Track project progress
- Collaborate on tasks
- Monitor team productivity

### Client Work

- Assign tasks to team members
- Track client project progress
- Collaborate on client work
- Monitor client project completion

### Task Coordination

- Coordinate tasks between team members
- Track task dependencies
- Monitor task progress
- Ensure task completion

## Future Updates

Stay tuned for updates on collaboration features. These features will enhance team productivity and make task management more collaborative.

---

*Last updated: December 2024`,
  
  profile: `# Profile

Manage your personal account settings, preferences, and security options in FiverFlow. Customize your profile, update your information, and configure your account settings.

## What You'll Find

The Profile/Settings page is organized into three main sections:
- **Overview** - Profile information and account management
- **Settings & Preferences** - Customize your account settings and preferences
- **Security & Sessions** - Manage security settings and active sessions

## Documentation Sections

For detailed information about each section, see:
- [Profile Overview](/docs/profile/overview) - Profile information and account management
- [Settings & Preferences](/docs/profile/settings-preferences) - Account settings and preferences
- [Security & Sessions](/docs/profile/security-sessions) - Security settings and sessions

## Quick Start

### Accessing Settings

1. Navigate to **Settings** from the main navigation menu or profile menu
2. Settings page opens with multiple categories
3. Select a category from the sidebar or mobile menu
4. Configure your settings as needed

## Key Features

- **Profile Information**: Update personal and professional information
- **Privacy Settings**: Control information visibility and privacy
- **Appearance**: Customize theme and visual preferences
- **Social Networks**: Manage social media links
- **Skills & Awards**: Showcase your skills and awards
- **Projects**: Manage your public projects
- **Subscription**: View and manage subscription

## Categories

The Settings page includes the following categories:

- **Profile**: Personal and professional information
- **Privacy**: Privacy and visibility settings
- **Notifications**: Notification preferences (coming soon)
- **Appearance**: Theme and visual preferences
- **Social Networks**: Links to social media
- **Skills & Awards**: Manage skills and awards
- **Projects**: Manage public projects
- **Subscription & Payment**: Subscription management

---

*Last updated: December 2024`,

  'profile/overview': `# Profile Overview

Create and manage your professional profile on FiverFlow. Showcase your skills, awards, projects, and social links. Build your professional presence and connect with the community.

## Profile Page Structure

Your profile page displays:

### Profile Header

- **Banner Image**: Custom banner image or default gradient
- **Profile Avatar**: Profile picture or initials fallback
- **Edit Button**: Quick access to edit profile (on your own profile)

### Profile Information

- **Full Name**: Your display name
- **Username**: Unique username (e.g., @username)
- **Professional Title**: Your job title or profession
- **Bio**: Personal biography and description
- **Location**: Your location (city, country)
- **Contact Information**: Email and phone (if public)

### Social Links

- **GitHub**: Link to your GitHub profile
- **Twitter/X**: Link to your Twitter account
- **LinkedIn**: Link to your LinkedIn profile
- **Instagram**: Link to your Instagram account
- **Discord**: Discord username
- **YouTube**: Link to your YouTube channel
- **Website**: Link to your personal website
- **TikTok**: Link to your TikTok account

### Skills Section

- **Skill Name**: Name of the skill
- **Skill Level**: Beginner, Intermediate, Advanced, Expert
- **Color Coding**: Different colors for each level
- **Skill Cards**: Visual representation of skills

### Awards Section

- **Award Title**: Name of the award
- **Issuer**: Who issued the award
- **Category**: Award category
- **Date Received**: When award was received
- **Description**: Award description

### Projects Section

- **Project Cards**: Visual project cards
- **Project Information**: Project details
- **Public/Private**: Control project visibility
- **Project Actions**: View, edit, delete projects

## Accessing Your Profile

### Your Own Profile

1. Navigate to **Profile** from the navigation menu
2. Or visit **/profile/[username]** with your username
3. Click **Edit** button to edit your profile
4. Or navigate to **Settings** page

### Other Profiles

1. Visit **/profile/[username]** with the username
2. View public profile information
3. See skills, awards, and projects
4. View social links

## Profile Settings

Access profile settings from the Settings page:

1. Navigate to **Settings** from navigation
2. Select **Profile** category
3. Update your information
4. Click **Save** to save changes

## Profile Information

### Personal Information

- **Full Name**: Your display name
- **Professional Title**: Your job title or profession
- **Location**: Your location
- **Bio**: Personal biography

### Contact Information

- **Contact Email**: Email for contact
- **Contact Phone**: Phone number for contact
- **Email Visibility**: Control email visibility
- **Phone Visibility**: Control phone visibility

## Privacy Settings

### Information Visibility

- **Public Profile**: Make profile public or private
- **Show Email**: Control email visibility
- **Show Phone**: Control phone visibility
- **Profile Visibility**: Control overall profile visibility

## Social Networks

### Social Links

Manage your social media links:

- **GitHub**: Connected via OAuth (read-only)
- **LinkedIn**: Enter LinkedIn username
- **Twitter/X**: Enter Twitter username
- **Instagram**: Enter Instagram username
- **TikTok**: Enter TikTok username
- **YouTube**: Enter YouTube channel name
- **Website**: Enter website URL
- **Discord**: Connected via OAuth (read-only)

### OAuth Connections

- **GitHub**: Connect via OAuth, username displayed automatically
- **Discord**: Connect via OAuth, username displayed automatically
- **Non-Editable**: OAuth connections cannot be edited manually

## Skills & Awards

### Skills

- **Add Skills**: Add skills with name, category, and level
- **Edit Skills**: Update skill information
- **Delete Skills**: Remove skills
- **Skill Levels**: Beginner, Intermediate, Advanced, Expert

### Awards

- **Add Awards**: Add awards with title, issuer, category, date
- **Edit Awards**: Update award information
- **Delete Awards**: Remove awards
- **Award Display**: Awards displayed on profile

## Projects

### Managing Projects

- **View Projects**: See all your projects
- **Make Public/Private**: Control project visibility
- **Delete Projects**: Remove projects
- **Project Details**: View project information

### Project Visibility

- **Public Projects**: Visible to everyone
- **Private Projects**: Only visible to you
- **Toggle Visibility**: Switch between public and private

## Best Practices

### Profile Management

1. **Complete Profile**: Fill in all relevant information
2. **Professional Bio**: Write a professional biography
3. **Add Skills**: Showcase your skills
4. **Add Awards**: Display your awards and certifications
5. **Social Links**: Connect your social media profiles

### Privacy

1. **Control Visibility**: Set appropriate privacy settings
2. **Email Privacy**: Control email visibility
3. **Phone Privacy**: Control phone visibility
4. **Profile Privacy**: Make profile public or private

---

*Last updated: December 2024`,

  'profile/settings-preferences': `# Settings & Preferences

Customize your account settings and preferences in FiverFlow. Configure appearance, notifications, and other account preferences.

## Settings Page Structure

The Settings page is organized into categories:

### Categories

- **Profile**: Personal and professional information
- **Privacy**: Privacy and visibility settings
- **Notifications**: Notification preferences (coming soon)
- **Appearance**: Theme and visual preferences
- **Social Networks**: Links to social media
- **Skills & Awards**: Manage skills and awards
- **Projects**: Manage public projects
- **Subscription & Payment**: Subscription management

### Category Navigation

- **Desktop**: Sidebar with category list
- **Mobile**: Horizontal scrolling menu with categories
- **Active Category**: Highlighted current category
- **Category Icons**: Visual icons for each category

## Profile Settings

### Personal Information

- **Full Name**: Your display name
- **Professional Title**: Your job title or profession
- **Location**: Your location
- **Website**: Your website URL
- **Bio**: Personal biography

### Contact Information

- **Contact Email**: Email for contact
- **Contact Phone**: Phone number for contact

### Saving Profile

- **Save Button**: Save all profile changes
- **Loading State**: Shows saving progress
- **Success Notification**: Confirmation when saved
- **Error Handling**: Error messages if save fails

## Privacy Settings

### Information Visibility

- **Public Profile**: Toggle to make profile public or private
- **Show Email**: Toggle to show/hide email
- **Show Phone**: Toggle to show/hide phone

### Privacy Toggles

- **Toggle Switches**: Easy-to-use toggle switches
- **Instant Feedback**: Visual feedback on toggle state
- **Save Required**: Click Save to apply changes

## Appearance Settings

### Theme Selection

- **Dark Theme**: Classic dark interface
- **Light Theme**: Clean and modern interface
- **Theme Cards**: Visual theme preview cards
- **Activate Button**: Button to activate selected theme

### Theme Features

- **Instant Preview**: See theme preview
- **Auto-save**: Theme saved automatically
- **Persistent**: Theme persists across sessions
- **Sync**: Theme synchronized across devices

## Social Networks Settings

### Social Links

- **LinkedIn**: Enter LinkedIn username
- **Twitter/X**: Enter Twitter username
- **Instagram**: Enter Instagram username
- **TikTok**: Enter TikTok username
- **YouTube**: Enter YouTube channel name
- **Website**: Enter website URL

### OAuth Connections

- **GitHub**: Connected via OAuth (read-only)
  - Shows GitHub username if connected
  - Link to GitHub profile
  - Cannot be edited manually
- **Discord**: Connected via OAuth (read-only)
  - Shows Discord username if connected
  - Cannot be edited manually
  - Connect button if not connected

### Social Link Format

- **Username Only**: Enter username without @ or https://
- **Auto-formatting**: Links generated automatically
- **Preview**: See generated link format
- **Validation**: Validates link format

## Skills & Awards Settings

### Skills Management

- **Add Skill**: Button to add new skill
- **Skill Modal**: Modal to create/edit skills
- **Skill Fields**: Name, category, level
- **Skill Levels**: Beginner, Intermediate, Advanced, Expert
- **Edit Skill**: Click edit icon to edit skill
- **Delete Skill**: Click delete icon to remove skill

### Awards Management

- **Add Award**: Button to add new award
- **Award Modal**: Modal to create/edit awards
- **Award Fields**: Title, issuer, category, date, description
- **Edit Award**: Click edit icon to edit award
- **Delete Award**: Click delete icon to remove award

## Projects Settings

### Projects Management

- **View Projects**: See all your projects
- **Make Public/Private**: Toggle project visibility
- **Delete Projects**: Remove projects
- **Project Cards**: Visual project cards

### Project Actions

- **Public/Private Toggle**: Switch project visibility
- **Delete Button**: Remove project
- **View Project**: Navigate to project page

## Subscription & Payment Settings

### Subscription Management

- **View Plans**: Link to upgrade page
- **Current Plan**: Display current subscription plan
- **Upgrade Button**: Navigate to upgrade page
- **Subscription Info**: View subscription details

## Notifications Settings

### Notification Preferences

**Coming Soon**: Notification settings will be available in future releases.

Planned features:
- Email notifications
- In-app notifications
- Notification preferences
- Notification categories

## Saving Settings

### Save Process

1. **Make Changes**: Update settings in any category
2. **Click Save**: Click Save button (for Profile, Privacy, Social categories)
3. **Loading State**: Shows saving progress
4. **Success Notification**: Confirmation when saved
5. **Error Handling**: Error messages if save fails

### Auto-save

- **Theme**: Theme saved automatically when changed
- **Other Settings**: Require manual save (Profile, Privacy, Social)

## Best Practices

### Settings Management

1. **Update Regularly**: Keep your information up to date
2. **Privacy Settings**: Review privacy settings regularly
3. **Theme Preference**: Choose theme that works for you
4. **Social Links**: Keep social links current
5. **Skills & Awards**: Regularly update skills and awards

### Profile Completeness

1. **Complete Information**: Fill in all relevant fields
2. **Professional Bio**: Write a professional biography
3. **Add Skills**: Showcase your skills
4. **Add Awards**: Display your awards
5. **Social Links**: Connect your social profiles

---

*Last updated: December 2024`,

  'profile/security-sessions': `# Security & Sessions

Manage your account security and active sessions in FiverFlow. Configure security settings, manage passwords, and monitor active sessions.

## Security Settings Overview

The Security & Sessions section allows you to:

- **Password Management**: Change your password
- **Session Management**: View and manage active sessions
- **Security Preferences**: Configure security settings
- **Account Security**: Monitor account security

## Password Management

### Changing Password

**Note**: Password management functionality may vary. Currently, password changes are handled through Supabase authentication.

### Password Requirements

- **Minimum Length**: Password must meet minimum length requirements
- **Complexity**: Password should include letters, numbers, and special characters
- **Security**: Use a strong, unique password

### Password Best Practices

1. **Use Strong Passwords**: Use complex, unique passwords
2. **Don't Reuse Passwords**: Use different passwords for different accounts
3. **Update Regularly**: Change password periodically
4. **Secure Storage**: Don't share passwords

## Session Management

### Active Sessions

**Note**: Session management functionality may vary. Currently, sessions are managed through Supabase authentication.

### Viewing Sessions

- **Active Sessions**: View all active sessions
- **Session Information**: See session details (device, location, etc.)
- **Session Management**: Manage active sessions

### Session Features

- **Multiple Devices**: Sessions on multiple devices
- **Session Details**: Device and location information
- **Session Termination**: End specific sessions
- **Security Monitoring**: Monitor session activity

## Security Preferences

### Security Settings

**Note**: Additional security settings may be available in future releases.

Planned features:
- Two-factor authentication (2FA)
- Login notifications
- Security alerts
- Account recovery options

## Account Security

### Security Monitoring

- **Account Activity**: Monitor account activity
- **Login History**: View login history
- **Security Alerts**: Get security alerts
- **Suspicious Activity**: Detect suspicious activity

### Security Best Practices

1. **Strong Passwords**: Use strong, unique passwords
2. **Secure Sessions**: Log out from shared devices
3. **Monitor Activity**: Regularly check account activity
4. **Enable 2FA**: Enable two-factor authentication when available
5. **Security Alerts**: Enable security alerts

## OAuth Connections

### OAuth Providers

- **GitHub**: Connect GitHub account via OAuth
- **Discord**: Connect Discord account via OAuth
- **Other Providers**: Additional OAuth providers may be available

### Managing OAuth Connections

- **View Connections**: See connected OAuth providers
- **Disconnect**: Disconnect OAuth providers
- **Reconnect**: Reconnect OAuth providers
- **Connection Status**: View connection status

## Security Features

### Authentication

- **Email/Password**: Standard email and password authentication
- **OAuth**: OAuth authentication (GitHub, Discord)
- **Session Management**: Secure session management
- **Token Refresh**: Automatic token refresh

### Security Measures

- **Encrypted Storage**: Secure data storage
- **HTTPS**: Secure connections
- **Session Security**: Secure session management
- **Data Protection**: Protect user data

## Troubleshooting

### Password Issues

If you have password issues:
1. **Reset Password**: Use password reset functionality
2. **Check Email**: Check email for reset link
3. **Contact Support**: Contact support if needed
4. **Verify Account**: Verify your account

### Session Issues

If you have session issues:
1. **Clear Sessions**: Clear all sessions
2. **Re-login**: Log in again
3. **Check Browser**: Clear browser cache
4. **Contact Support**: Contact support if needed

## Future Features

### Planned Security Features

The following security features are planned for future releases:

- **Two-Factor Authentication (2FA)**: Add an extra layer of security
- **Login Notifications**: Get notified of new logins
- **Security Alerts**: Receive security alerts
- **Account Recovery**: Enhanced account recovery options
- **Session Management**: Advanced session management
- **Security Dashboard**: Comprehensive security dashboard

### Security Enhancements

- **Biometric Authentication**: Support for biometric authentication
- **Security Keys**: Support for security keys
- **Advanced Monitoring**: Enhanced security monitoring
- **Threat Detection**: Automated threat detection

---

*Last updated: December 2024`,
};

const DocPage: React.FC = () => {
  const { page } = useParams<{ page: string }>();
  const location = useLocation();
  
  const getContent = () => {
    // Normalize path so "/docs" and "/docs/" both resolve to index
    const fullPath = location.pathname.replace(/^\/docs\/?/, '');
    const docPage = fullPath === '' ? 'index' : fullPath;
    
    // Check if we have specific content for this page
    if (contentMap[docPage]) {
      return contentMap[docPage];
    }
    
    // Generate content based on the page path
    const pathParts = docPage.split('/');
    const pageName = pathParts[pathParts.length - 1];
    const category = pathParts[0] || docPage;
    
    // Format the title nicely
    const title = pageName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    const categoryTitle = category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return `# ${title}

This page provides detailed information about ${title} in the ${categoryTitle} section.

## Overview

Content coming soon for this page. We're working on comprehensive documentation.

## Features

Stay tuned for detailed instructions and examples.

---

*Last updated: ${new Date().toLocaleDateString()}*`;
  };

  const content = getContent();
  const getBreadcrumbPath = () => {
    const currentPath = location.pathname;
    const parts = currentPath.split('/').filter(Boolean);
    const breadcrumbs = parts.map((part, index) => ({
      label: part.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      path: '/' + parts.slice(0, index + 1).join('/')
    }));
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbPath();

  return (
    <div className="prose prose-invert max-w-none text-[#A6A6A6]">
      {/* Breadcrumb */}
      {breadcrumbs.length > 1 && (
        <div className="flex items-center gap-2 text-sm text-[#A6A6A6] mb-6">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              {index > 0 && <ChevronRight size={14} />}
              <span className={index === breadcrumbs.length - 1 ? 'text-[#FAFAFA]' : 'text-[#A6A6A6]'}>
                {crumb.label}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="markdown-content">
        <ReactMarkdown
        components={{
          h1: ({ children }) => {
            const id = children?.toString().toLowerCase().replace(/\s+/g, '-');
            return (
              <h1 id={id} className="text-4xl md:text-5xl font-bold text-[#FAFAFA] mt-0 mb-6 scroll-mt-20">
                {children}
              </h1>
            );
          },
          h2: ({ children }) => {
            const id = children?.toString().toLowerCase().replace(/\s+/g, '-');
            return (
              <h2 id={id} className="text-2xl font-bold text-[#FAFAFA] mt-12 mb-4 scroll-mt-20">
                {children}
              </h2>
            );
          },
          h3: ({ children }) => {
            const id = children?.toString().toLowerCase().replace(/\s+/g, '-');
            return (
              <h3 id={id} className="text-xl font-semibold text-[#FAFAFA] mt-8 mb-3 scroll-mt-20">
                {children}
              </h3>
            );
          },
          p: ({ children }) => (
            <p className="text-[#A6A6A6] mb-4 leading-relaxed">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="text-[#A6A6A6] mb-4 space-y-2 ml-6">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="text-[#A6A6A6] mb-4 space-y-2 ml-6">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="leading-relaxed">{children}</li>
          ),
          code: ({ children, className }) => {
            const isInline = !className;
            return isInline ? (
              <code className="bg-[#1A1A1A] text-[#8B5CF6] px-1.5 py-0.5 rounded text-sm font-mono">
                {children}
              </code>
            ) : (
              <code className="block bg-[#1A1A1A] text-[#A6A6A6] p-4 rounded-lg text-sm font-mono overflow-x-auto mb-4 border border-[rgba(255,255,255,0.04)]">
                {children}
              </code>
            );
          },
          strong: ({ children }) => (
            <strong className="font-semibold text-[#FAFAFA]">{children}</strong>
          ),
          a: ({ href, children }) => (
            <a 
              href={href} 
              className="text-[#8B5CF6] hover:text-[#A78BFA] underline transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-[#8B5CF6] pl-4 italic text-[#A6A6A6] my-4">
              {children}
            </blockquote>
          ),
          hr: () => (
            <hr className="border-t border-[rgba(255,255,255,0.04)] my-8" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
      </div>
    </div>
  );
};

export default DocPage;